import { useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo, forwardRef } from 'react';
import { AnimatePresence } from 'motion/react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plus, Minus, Trash2, Upload, Sparkles, MessageCircle, Cloud, Zap, Square, Type as TypeIcon,
  Loader2, X, LayoutGrid, ImageOff, Shuffle, Move, ZoomIn, ZoomOut,
  FlipHorizontal2, FlipVertical2, BookOpen, ChevronLeft, ChevronRight, Copy, NotebookText,
  AlignLeft, AlignCenter, AlignRight, Download, Palette, MessageSquareDashed,
  Info, ChevronUp, ChevronDown, Check, Megaphone, PenLine, Wand2,
} from 'lucide-react';
import { api, AiLimitError, type MangaPage, type MangaLayout, type MangaBubble, type MangaPanel, type BubbleType, type BibleEntry } from '../lib/api';
import { notify } from '../lib/toast';
import { cn } from '../lib/utils';
import { StoryBibleView } from './story-bible-view';
import { OutlineBoardView } from './outline-board-view';
import { useAppStore } from '../stores/app-store';
import { generateMangaLayout, centroid, toPolygonCss } from '../lib/manga-templates';
import { mirrorPageHorizontally } from '../lib/manga-mirror';
import { parsePageScript, serializePanelBlock, serializePageScript, type ParsedDialogue, type ParsedPanel } from '../lib/manga-script';
import { useIsMobile } from '../hooks/use-mobile';
import { Drawer, DrawerContent, DrawerTitle } from './ui/drawer';
import { AIChat, type MangaChatMode } from './ai';
import { useAIStore } from '../stores/ai-store';
import { smoothClosedPath, centroidOf, rayPolygonExit, resamplePolygonLinear } from '../lib/bubble-shape';
import {
  generateBubblePath, thoughtTailCircles, toPathBubble,
  BUBBLE_FONT_OPTIONS, resolveFontFamily, resolveBubbleStyle, bubbleFontWeight, bubbleFontStyleCss, defaultBubbleFontPx, defaultBubblePaddingPx,
  fitBubbleFontPx, BUBBLE_LINE_HEIGHT, computeSfxLayout, groupJoinedBubbles, connectorPathsForGroup, computeTailOmissions,
} from '../lib/bubble-path';
import { exportPagePng, exportAllPngs, exportPdf, exportHtml } from '../lib/manga-export';
import { runPageFlip } from '../lib/page-flip';
import { useEditorShortcuts, groupShortcuts, type EditorShortcut } from '../hooks/use-editor-shortcuts';
import { useShortcutsStore } from '../stores/shortcuts-store';
import { ShortcutsHelpButton } from './shortcuts-help';

const uid = () => Math.random().toString(36).slice(2, 10);

// Reader page-turn animation duration (ms) — passed explicitly to every
// `runPageFlip` call (both here and its 1:1 twin, `readerRuntime`, in
// manga-export.ts) so the two never drift.
const READER_FLIP_MS = 700;

// ── Shared toolbar button skins — mirror the BOOK editor's header exactly so the
// two editors read as siblings: ghost icon buttons live inside a pill, active
// state tints them, and the primary action uses the accent (like "Publicar"). ──
const GHOST_BTN =
  'inline-flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted/80 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 disabled:cursor-not-allowed';
const GHOST_ACTIVE = 'bg-muted text-foreground';
const GHOST_PRIMARY = 'bg-primary/15 text-primary hover:bg-primary/20 hover:text-primary';
// Toolbar section divider — identical to the book header's vertical separator.
function ToolSep() {
  return <div role="separator" aria-orientation="vertical" className="mx-1 h-5 w-px bg-border/70" />;
}

/** Tiny reading-order diagram for the direction picker — a 2×2 mini-grid whose
 * panels are numbered in the given reading direction (RTL manga vs LTR comic). */
function DirectionGlyph({ rtl, active }: { rtl: boolean; active: boolean }) {
  // Cells are laid out TL, TR, BL, BR; the number each shows is its reading
  // rank, which within a row starts on the RIGHT for RTL and the LEFT for LTR.
  const order = rtl ? [2, 1, 4, 3] : [1, 2, 3, 4];
  return (
    <span
      aria-hidden
      className={cn(
        'grid h-9 w-9 shrink-0 grid-cols-2 gap-0.5 rounded-md border p-0.5',
        active ? 'border-primary/60 bg-primary/5' : 'border-border bg-muted/40',
      )}>
      {order.map((n, i) => (
        <span
          key={i}
          className={cn(
            'flex items-center justify-center rounded-[2px] text-[7px] font-bold leading-none',
            active ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
          )}>
          {n}
        </span>
      ))}
    </span>
  );
}

const BUBBLE_TYPES: { type: BubbleType; labelKey: string; label: string; icon: React.ReactNode }[] = [
  { type: 'speech', labelKey: 'manga.bubbleSpeech', label: 'Fala', icon: <MessageCircle size={14} /> },
  { type: 'thought', labelKey: 'manga.bubbleThought', label: 'Pensamento', icon: <Cloud size={14} /> },
  { type: 'shout', labelKey: 'manga.bubbleShout', label: 'Grito', icon: <Zap size={14} /> },
  { type: 'whisper', labelKey: 'manga.bubbleWhisper', label: 'Sussurro', icon: <MessageSquareDashed size={14} /> },
  { type: 'narration', labelKey: 'manga.bubbleNarration', label: 'Narração', icon: <Square size={14} /> },
  { type: 'caption', labelKey: 'manga.bubbleCaption', label: 'Legenda', icon: <TypeIcon size={14} /> },
  { type: 'sfx', labelKey: 'manga.bubbleSfx', label: 'SFX', icon: <Megaphone size={14} /> },
];

/** Tailwind classes for horizontal text alignment inside a flex bubble box —
 * defaults to centered when `align` is unset (matches the pre-existing look). */
function bubbleAlignClasses(align?: 'left' | 'center' | 'right'): string {
  if (align === 'left') return 'justify-start text-left';
  if (align === 'right') return 'justify-end text-right';
  return 'justify-center text-center';
}

// ── Liquid auto-size: measure the text against a hidden singleton element ──────
// (reused across bubbles — cheap, avoids layout thrash from live DOM clones).
let measureEl: HTMLDivElement | null = null;
function getMeasureEl(): HTMLDivElement {
  if (measureEl) return measureEl;
  const el = document.createElement('div');
  el.style.position = 'fixed';
  el.style.visibility = 'hidden';
  el.style.pointerEvents = 'none';
  el.style.left = '-9999px';
  el.style.top = '0';
  el.style.whiteSpace = 'pre-wrap';
  el.style.overflowWrap = 'break-word';
  el.style.lineHeight = String(BUBBLE_LINE_HEIGHT); // unified with fitBubbleFontPx/every renderer — was 1.05, drifted from the 1.25 actually painted
  document.body.appendChild(el);
  measureEl = el;
  return el;
}

/** Height (px) needed to render `text` at a given content width/font, using the
 * same metrics as the live bubble text layer (see the Bubble component below). */
function measureBubbleTextPx(text: string, widthPx: number, fontPx: number, bold: boolean, fontFamily: string): number {
  const el = getMeasureEl();
  el.style.width = `${Math.max(1, widthPx)}px`;
  el.style.fontSize = `${fontPx}px`;
  el.style.fontWeight = bold ? '700' : '400';
  el.style.fontFamily = fontFamily;
  el.textContent = text || ' ';
  return el.scrollHeight;
}

const AUTO_SIZE = { minW: 0.06, maxW: 0.7, minH: 0.05, maxH: 0.6, growEps: 0.004 } as const;

/** Grows (never shrinks) a bubble's w/h so its text fits, preferring height and
 * only widening when height alone hits the cap and the text still overflows.
 * `boxRect` is the bubble's own on-screen box — used to derive canvas px/unit
 * without threading extra refs (box.width = canvasWidthPx * b.w, by layout). */
function computeAutoSize(text: string, type: BubbleType, w: number, h: number, boxRect: DOMRect, pageW: number, explicitFontPx?: number, explicitPadPx?: number, fontFamilyKey?: string): { w: number; h: number } | null {
  if (w <= 0 || h <= 0 || boxRect.width <= 0 || boxRect.height <= 0) return null;
  const canvasW = boxRect.width / w;
  const canvasH = boxRect.height / h;
  if (!Number.isFinite(canvasW) || !Number.isFinite(canvasH) || canvasW <= 0 || canvasH <= 0) return null;
  // Fixed padding in PAGE px (same default as the renderers), converted to display px.
  const bold = type !== 'shout';
  const fontFamily = resolveFontFamily(type, fontFamilyKey);
  let nw = w, nh = h;
  for (let iter = 0; iter < 8; iter++) {
    const pageToDisplay = (boxRect.width / w) / pageW;
    const fontPx = (explicitFontPx ?? defaultBubbleFontPx({ type }, pageW)) * pageToDisplay;
    const boxWidthPx = canvasW * nw;
    const boxHeightPx = canvasH * nh;
    const padPx = (explicitPadPx ?? defaultBubblePaddingPx({ type }, pageW)) * pageToDisplay;
    const contentWidthPx = Math.max(1, boxWidthPx - 2 * padPx);
    const neededContentPx = measureBubbleTextPx(text, contentWidthPx, fontPx, bold, fontFamily);
    const neededBoxHeightPx = neededContentPx + 2 * padPx;
    const neededH = neededBoxHeightPx / canvasH;
    if (neededH <= nh + 1e-4 || boxHeightPx >= neededBoxHeightPx) break; // fits
    if (neededH <= AUTO_SIZE.maxH) { nh = neededH; break; } // grows within cap, done
    nh = AUTO_SIZE.maxH;
    if (nw < AUTO_SIZE.maxW) { nw = Math.min(AUTO_SIZE.maxW, nw + 0.05); continue; } // widen, remeasure
    break; // both maxed — let it clip rather than grow forever
  }
  nw = Math.max(AUTO_SIZE.minW, Math.min(AUTO_SIZE.maxW, nw));
  nh = Math.max(AUTO_SIZE.minH, Math.min(AUTO_SIZE.maxH, nh));
  if (nw - w <= AUTO_SIZE.growEps && nh - h <= AUTO_SIZE.growEps) return null; // no meaningful growth
  return { w: nw, h: nh };
}

// ── Magnetic snapping — bubble edges/centers "grab" other bubbles, panel
// bounding boxes and the page edges/center, with a small flush/gap pair. ─────
const SNAP_THRESHOLD = 0.012;
const SNAP_GAP = 0.008;

interface SnapAxisTargets { xs: number[]; ys: number[]; }

/** Candidate values (normalized 0..1) each bubble edge/center may snap to. */
function collectSnapTargets(bubbles: MangaBubble[], panels: MangaPanel[], excludeId: string): SnapAxisTargets {
  const xs: number[] = [0, 0.5, 1];
  const ys: number[] = [0, 0.5, 1];
  for (const b of bubbles) {
    if (b.id === excludeId) continue;
    const left = b.x, right = b.x + b.w, cx = b.x + b.w / 2;
    const top = b.y, bottom = b.y + b.h, cy = b.y + b.h / 2;
    xs.push(left, right, cx, right + SNAP_GAP, left - SNAP_GAP);
    ys.push(top, bottom, cy, bottom + SNAP_GAP, top - SNAP_GAP);
  }
  for (const p of panels) {
    const pxs = p.points.map((pt) => pt.x), pys = p.points.map((pt) => pt.y);
    const minX = Math.min(...pxs), maxX = Math.max(...pxs);
    const minY = Math.min(...pys), maxY = Math.max(...pys);
    xs.push(minX, maxX, (minX + maxX) / 2);
    ys.push(minY, maxY, (minY + maxY) / 2);
  }
  return { xs, ys };
}

/** Snaps a moving box's left/right/center edge to the closest target within
 * threshold; returns the adjusted position and the target it grabbed (guide). */
function snapMoveAxis(pos: number, size: number, targets: number[], threshold: number): { pos: number; guide?: number } {
  let best: { delta: number; newPos: number; guide: number } | null = null;
  const offsets: [number, number][] = [[0, pos], [size, pos + size], [size / 2, pos + size / 2]];
  for (const t of targets) {
    for (const [offset, cand] of offsets) {
      const delta = Math.abs(cand - t);
      if (delta <= threshold && (!best || delta < best.delta)) best = { delta, newPos: pos + (t - (pos + offset)), guide: t };
    }
  }
  return best ? { pos: best.newPos, guide: best.guide } : { pos };
}

/** Snaps a resizing box's MOVING edge to the closest target — `fixedPos` is
 * the OPPOSITE (anchored) corner's coordinate on this axis; `growPositive`
 * picks which side of it the moving edge sits on (true: fixedPos + size, i.e.
 * growing right/down; false: fixedPos - size, growing left/up — corner-anchored
 * resize can grow in either direction depending on which corner was grabbed). */
function snapResizeAxis(fixedPos: number, size: number, targets: number[], threshold: number, growPositive = true): { size: number; guide?: number } {
  let best: { delta: number; newSize: number; guide: number } | null = null;
  const edge = growPositive ? fixedPos + size : fixedPos - size;
  for (const t of targets) {
    const delta = Math.abs(edge - t);
    const newSize = growPositive ? t - fixedPos : fixedPos - t;
    if (delta <= threshold && newSize > 0 && (!best || delta < best.delta)) best = { delta, newSize, guide: t };
  }
  return best ? { size: best.newSize, guide: best.guide } : { size };
}

interface SnapGuideLine { axis: 'x' | 'y'; pos: number }

type Sel = { kind: 'panel' | 'bubble'; id: string } | null;
interface Drag {
  mode: 'corner' | 'panelMove' | 'bubMove' | 'bubResize' | 'bubTail' | 'artPan' | 'bubRotate';
  id: string; index?: number;
  startX: number; startY: number;
  orig: unknown; rect: DOMRect;
}

/** Rotates point `(px,py)` around `(cx,cy)` by `angleDeg` degrees, using the
 * EXACT same matrix SVG's own `rotate(a, cx, cy)` transform applies — so a
 * handle positioned with this stays glued to the shape's rotated silhouette
 * pixel-for-pixel instead of drifting out of sync with the CSS/SVG rotation. */
function rotatePoint(px: number, py: number, cx: number, cy: number, angleDeg: number): { x: number; y: number } {
  const rad = (angleDeg * Math.PI) / 180;
  const dx = px - cx, dy = py - cy;
  const cos = Math.cos(rad), sin = Math.sin(rad);
  return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
}

/** Wraps a rotation degree value into (-180, 180] — keeps it in sync with the
 * style panel's -180..180 slider range regardless of how many turns a drag
 * gesture accumulated. */
function normalizeDeg(deg: number): number {
  return ((((deg + 180) % 360) + 360) % 360) - 180;
}

// ── Dialogue-bubble placement recipe — SINGLE SOURCE OF TRUTH shared by the
// AI script assistant (bulk, one panel's worth of lines at once) and the
// manual "+ Fala" form (one line at a time). Places the bubble inside the
// panel's bounding box, alternating left/right sides and stacking downward
// as `order` grows, sized from the text length, with the standard tail. ────
/** Ray-casting point-in-polygon test (normalized page coords). */
function pointInPoly(poly: { x: number; y: number }[], x: number, y: number): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y, xj = poly[j].x, yj = poly[j].y;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** All 4 corners + edge midpoints of the rect (grown by `margin`) inside the
 * polygon — so slanted panel cuts can't clip an auto-placed bubble. */
function rectInsidePoly(poly: { x: number; y: number }[], x: number, y: number, w: number, h: number, margin = 0.006): boolean {
  const x0 = x - margin, y0 = y - margin, x1 = x + w + margin, y1 = y + h + margin;
  const probes: [number, number][] = [
    [x0, y0], [x1, y0], [x0, y1], [x1, y1],
    [(x0 + x1) / 2, y0], [(x0 + x1) / 2, y1], [x0, (y0 + y1) / 2], [x1, (y0 + y1) / 2],
  ];
  return probes.every(([px, py]) => pointInPoly(poly, px, py));
}

/** Slides the rect toward the polygon's centroid until it fits fully inside —
 * cropped/slanted frames get bubbles that never leak out. Falls back to the
 * centroid when the panel is just too small for the rect. */
function fitRectInPoly(poly: { x: number; y: number }[], x: number, y: number, w: number, h: number): { x: number; y: number } {
  const cx = poly.reduce((a, p) => a + p.x, 0) / poly.length;
  const cy = poly.reduce((a, p) => a + p.y, 0) / poly.length;
  let px = x, py = y;
  for (let i = 0; i < 28; i++) {
    if (rectInsidePoly(poly, px, py, w, h)) return { x: px, y: py };
    px += (cx - (px + w / 2)) * 0.16;
    py += (cy - (py + h / 2)) * 0.16;
  }
  return { x: cx - w / 2, y: cy - h / 2 };
}

function buildDialogueBubble(panel: MangaPanel, order: number, d: { speaker?: string; type: BubbleType; text: string }): MangaBubble {
  const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));
  const poly = panel.points;
  const xs = poly.map((p) => p.x), ys = poly.map((p) => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const bw = maxX - minX, bh = maxY - minY;

  const isCaption = d.type === 'caption';
  const isNarration = d.type === 'narration';
  const isSfx = d.type === 'sfx';
  // Captions are small corner tags (manga convention); balloons scale to the
  // text but never wider than ~72% of the panel so tight frames still fit them.
  // SFX drops in BIG (~30% of the panel) — a letreiro, not a balloon.
  let w = isSfx
    ? 0.3
    : isCaption
      ? clamp(0.10 + d.text.length * 0.0018, 0.10, 0.24)
      : clamp(0.14 + d.text.length * 0.0022, 0.12, 0.34);
  w = Math.min(w, Math.max(0.08, bw * 0.72));
  const estLines = Math.max(1, Math.ceil(d.text.length / 24));
  let h = isSfx
    ? clamp(0.09 + (estLines - 1) * 0.05, 0.09, 0.3)
    : isCaption
      ? clamp(0.045 + (estLines - 1) * 0.024, 0.045, 0.14)
      : clamp(0.07 + (estLines - 1) * 0.03, 0.06, 0.32);
  h = Math.min(h, Math.max(0.05, bh * 0.6));

  // Seed position by role: captions hug a corner (alternating), narration runs
  // along the top, SFX scatters near the top (slight left/right jitter, like
  // a letreiro leaking over the art), speech/thought/shout alternate sides top-down.
  let sx: number, sy: number;
  if (isCaption) {
    const corner = order % 4; // TL, TR, BL, BR
    sx = corner % 2 === 0 ? minX + 0.015 : maxX - w - 0.015;
    sy = corner < 2 ? minY + 0.015 : maxY - h - 0.015;
  } else if (isNarration) {
    sx = minX + (bw - w) / 2;
    sy = minY + 0.02 + order * 0.02;
  } else if (isSfx) {
    sx = minX + (bw - w) / 2 + (order % 2 === 0 ? -0.03 : 0.03);
    sy = minY + 0.06 + order * 0.05;
  } else {
    const leftSide = order % 2 === 0;
    sx = leftSide ? minX + 0.03 : maxX - w - 0.03;
    sy = minY + 0.04 + order * 0.09;
  }
  // Never leak outside the frame: slide into the actual polygon.
  const { x, y } = fitRectInPoly(poly, clamp(sx, 0, 1 - w), clamp(sy, 0, 1 - h), w, h);

  const hasTail = d.type === 'speech' || d.type === 'thought';
  return {
    id: uid(), type: d.type, x, y, w, h, text: d.text,
    speaker: d.speaker || undefined,
    tail: hasTail ? { x: 0, y: h * 1.1 } : undefined,
    align: 'center',
  };
}

// ── Apply a WHOLE parsed page script onto a page in ONE pure computation ──────
// Used by the conversational Ghostwriter's [ACTION:MANGA_PAGE_SCRIPT]: it must
// build the next layout without relying on React state re-reads (patch is
// async), so everything is derived from the given `page` snapshot. Reconciles
// existing script-managed bubbles by speaker+text when the panel COUNT is
// unchanged (preserving their position/size/style, exactly like the per-block
// apply); when the count differs it regenerates the layout (preserving art by
// reading order) and drops the old script bubbles, keeping manual ones.
function buildPageScriptLayout(
  page: MangaPage,
  parsed: ParsedPanel[],
  dir: 'ltr' | 'rtl',
  cast: BibleEntry[],
): { layout: MangaLayout } {
  const findCastIds = (text: string): string[] => {
    const lower = text.toLowerCase();
    return cast.filter((c) => c.name && lower.includes(c.name.toLowerCase())).map((c) => c.id);
  };
  const count = parsed.length;
  const sameCount = page.layout.panels.length === count;
  const basePanels: MangaPanel[] = sameCount
    ? page.layout.panels
    : generateMangaLayout(count, dir).map((points, i) => {
        const prev = page.layout.panels[i];
        return {
          id: prev?.id ?? uid(), points,
          imageKey: prev?.imageKey, imageUrl: prev?.imageUrl,
          scale: prev?.scale, offsetX: prev?.offsetX, offsetY: prev?.offsetY,
          flipX: prev?.flipX, flipY: prev?.flipY,
        };
      });
  const existingBubbles = page.layout.bubbles;
  const scripted: MangaBubble[] = [];
  const managedRemove = new Set<string>();
  const nextPanels = basePanels.map((panel, i) => {
    const pp = parsed[i];
    if (!pp) return panel;
    const xs = panel.points.map((p) => p.x), ys = panel.points.map((p) => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
    const managedInPanel = sameCount
      ? existingBubbles.filter((b) => {
          if (!b.speaker) return false;
          const cx = b.x + b.w / 2, cy = b.y + b.h / 2;
          return cx >= minX && cx <= maxX && cy >= minY && cy <= maxY;
        })
      : [];
    const usedIds = new Set<string>();
    pp.dialogues.forEach((d, j) => {
      const reused = d.speaker
        ? managedInPanel.find((b) => !usedIds.has(b.id) && b.speaker === d.speaker && b.text === d.text)
        : undefined;
      if (reused) {
        usedIds.add(reused.id);
        scripted.push(reused.type === d.type ? reused : { ...reused, type: d.type });
      } else {
        scripted.push(buildDialogueBubble(panel, j, d));
      }
    });
    managedInPanel.forEach((b) => managedRemove.add(b.id));
    const castText = [pp.description, ...pp.dialogues.map((d) => `${d.speaker} ${d.text}`)].join(' ');
    return { ...panel, script: { description: pp.description, castIds: findCastIds(castText) } };
  });
  // When the count changed, keep ONLY manual (speaker-less) bubbles — old
  // script bubbles belonged to a now-replaced panel set. When unchanged, keep
  // everything except the reconciled managed set.
  const outside = sameCount
    ? existingBubbles.filter((b) => !managedRemove.has(b.id))
    : existingBubbles.filter((b) => !b.speaker);
  return { layout: { panels: nextPanels, bubbles: [...outside, ...scripted] } };
}

// ── Pure helpers for art generation, usable for ANY page (not just the
// currently-open one) — the "transform all sketches" bulk action iterates
// every page, not only `current`, so these can't close over component state. ─
/** Pick the generation size that matches the panel's real aspect ratio, so the
 * art fills the panel with minimal cropping (portrait / landscape / square). */
function computeSizeForPanel(page: MangaPage | null, panelId: string): '1024x1024' | '1024x1536' | '1536x1024' {
  const panel = page?.layout.panels.find((p) => p.id === panelId);
  if (!panel) return '1024x1536';
  const xs = panel.points.map((p) => p.x), ys = panel.points.map((p) => p.y);
  const pageW = page?.width || 1000, pageH = page?.height || 1414;
  const w = (Math.max(...xs) - Math.min(...xs)) * pageW;
  const h = (Math.max(...ys) - Math.min(...ys)) * pageH;
  const ar = h > 0 ? w / h : 0.7;
  if (ar >= 1.25) return '1536x1024';
  if (ar <= 0.8) return '1024x1536';
  return '1024x1024';
}

/** Dialogue staging for art generation: the scene's lines tell the model WHO
 * is acting/reacting and how many balloons the composition must accommodate —
 * never rendered as text in the image. */
function computeStagingForPanel(page: MangaPage | null, panelId: string): string {
  const panel = page?.layout.panels.find((p) => p.id === panelId);
  if (!panel) return '';
  const xs = panel.points.map((p) => p.x), ys = panel.points.map((p) => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const inPanel = (page?.layout.bubbles ?? []).filter((b) => {
    const cx = b.x + b.w / 2, cy = b.y + b.h / 2;
    return b.speaker && cx >= minX && cx <= maxX && cy >= minY && cy <= maxY;
  });
  if (!inPanel.length) return '\n\nComposition: strong single impact shot; keep some plain negative space near the top edge.';
  const lines = inPanel.map((b) => {
    const mode = b.type === 'thought' ? ' (thinking)' : b.type === 'sfx' ? ' (sound effect)' : b.type === 'shout' ? (b.speaker === 'SFX' ? ' (sound effect)' : ' (shouting)') : b.type === 'whisper' ? ' (whispering)' : '';
    return `- ${b.speaker}${mode}: "${b.text.slice(0, 70)}"`;
  }).join('\n');
  return `\n\nSCENE DIALOGUE — use ONLY for acting, expressions, poses and who faces whom (absolutely NO text, letters or balloons in the image):\n${lines}\nComposition: every speaking character above must be VISIBLE in frame; leave clean plain negative space for ${inPanel.length} balloon(s) near the speakers.`;
}

export function MangaEditor() {
  const { projectId } = useParams<{ projectId: string }>();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const ws = searchParams.get('ws');
  const view: 'paginas' | 'universo' | 'estrutura' = ws === 'bible' ? 'universo' : ws === 'outline' ? 'estrutura' : 'paginas';

  const [pages, setPages] = useState<MangaPage[]>([]);
  const [dir, setDir] = useState<'ltr' | 'rtl'>('rtl');
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Sel>(null);
  // Which bubble (if any) is in inline contentEditable edit mode — lifted up
  // from the bubble node itself (rather than local component state) because
  // the bubble's SHAPE and its TEXT are now rendered as separate passes (see
  // `BubbleShape`/`BubbleTextHandles` + `groupJoinedBubbles`), so double-click
  // (on the shape) and the contentEditable div (in the text pass) need a
  // shared source of truth to stay in sync.
  const [editingBubbleId, setEditingBubbleId] = useState<string | null>(null);
  useEffect(() => {
    if (editingBubbleId && (selected?.kind !== 'bubble' || selected.id !== editingBubbleId)) setEditingBubbleId(null);
  }, [selected, editingBubbleId]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [panelCount, setPanelCount] = useState(4);
  // Stable layout proposals — re-rolled only on count change / explicit reshuffle.
  const [previewSeed, setPreviewSeed] = useState(0);
  const previews = useMemo(
    () => [0, 1, 2, 3].map(() => generateMangaLayout(panelCount, dir)),
    [panelCount, previewSeed, dir],
  );
  // Art-framing mode: when on, dragging the selected panel pans its image
  // (offsetX/offsetY) and zoom controls appear, instead of moving the panel.
  const [framing, setFraming] = useState(false);
  // Internal reader (proofing): full-screen, respects reading direction (RTL).
  const [readerIdx, setReaderIdx] = useState<number | null>(null);
  // Guided reading: darken the rest of the page and zoom the CURRENT panel,
  // advancing in reading order — panel by panel, then next page.
  const [readerGuided, setReaderGuided] = useState(true);
  // Guided-mode reading position WITHIN the current page. `null` = no
  // position yet (whole page, fresh/non-guided). While guided, `readerStep`
  // also uses the SENTINELS -1 (whole page, "entry" edge — about to read
  // panel 0 next) and `panelCount` (whole page, "exit" edge — about to turn
  // the page next) so the two whole-page pauses (before the first panel and
  // after the last one) are distinguishable — see `readerStep`.
  const [panelFocus, setPanelFocus] = useState<number | null>(null);
  // Active page-turn animation (see `readerStep`'s `crossPage`) — while set,
  // the reader shows the Moleskine-style 3D flip overlay (the CURRENT page's
  // own live DOM rotating over the static incoming page, see `runPageFlip`)
  // instead of the normal single page, and all navigation input is ignored
  // until it resolves (~READER_FLIP_MS, or instantly for prefers-reduced-
  // motion/anim-off, which never sets `pageTurn` at all — see `crossPage`).
  const [pageTurn, setPageTurn] = useState<{ dir: 1 | -1 } | null>(null);
  // Where the turn LANDS once the animation (or its fallback) finishes —
  // read by the effect that drives `runPageFlip` (can't be derived from
  // `pageTurn` alone, since `readerGuided`/panel count are needed too, and
  // re-deriving them at commit time could race a mid-flight guided toggle).
  const pendingLandingRef = useRef<{ np: number; landing: number | null } | null>(null);
  // `runPageFlip`'s own cancel handle (stops its transition/fallback timer
  // without firing `onDone`) — invoked by `cancelPageTurn` so closing/
  // Escaping the reader mid-flip never leaves a dangling animation running.
  const flipCancelRef = useRef<(() => void) | null>(null);
  // The three DOM nodes `runPageFlip` animates — see the `pageTurn` JSX
  // below: `flipSheetRef` is the rotating leaf (outgoing page's live DOM,
  // front/back faces), `flipIncomingRef` is the next/prev page's own box
  // (receives the "settle" scale pop), `flipShadeRef` is the optional
  // hinge-shading overlay inside the sheet's front face.
  const flipSheetRef = useRef<HTMLDivElement | null>(null);
  const flipIncomingRef = useRef<HTMLDivElement | null>(null);
  const flipShadeRef = useRef<HTMLDivElement | null>(null);
  const cancelPageTurn = useCallback(() => {
    if (flipCancelRef.current) { flipCancelRef.current(); flipCancelRef.current = null; }
    pendingLandingRef.current = null;
    setPageTurn(null);
  }, []);
  // Advance gesture side FOLLOWS the project's reading direction (single source
  // of truth = `dir`): manga (RTL) advances on the LEFT / ←, comics/HQ (LTR) on
  // the RIGHT / →. No standalone toggle anymore — change the direction from the
  // breadcrumb selector to flip this together with everything else.
  const navFlip = dir === 'rtl';
  // Bubble/text entrance animations in the reader (configurable, default on).
  const [readerAnim, setReaderAnim] = useState(() => {
    try { return localStorage.getItem('manga-reader-anim') !== '0'; } catch { return true; }
  });
  const toggleReaderAnim = () => setReaderAnim((v) => {
    const next = !v;
    try { localStorage.setItem('manga-reader-anim', next ? '1' : '0'); } catch { /* ignore */ }
    return next;
  });
  // Universe (Story Bible) entries — wired into panel generation for consistency.
  const [cast, setCast] = useState<BibleEntry[]>([]);
  // New characters the script just introduced get their reference image
  // generated automatically, server-side, fire-and-forget — it's usually not
  // done yet by the time the script response comes back, so refresh the cast
  // once immediately (name/entry shows up) and once again a bit later (the
  // freshly-generated image shows up too, no manual action needed).
  const refreshCastSoon = useCallback(() => {
    if (!projectId) return;
    api.getBibleEntries(projectId).then((r) => setCast(r.entries)).catch(() => {});
    setTimeout(() => { api.getBibleEntries(projectId).then((r) => setCast(r.entries)).catch(() => {}); }, 16000);
  }, [projectId]);
  const [genModal, setGenModal] = useState<{ panelId: string; prompt: string; refs: Set<string>; useSketch: boolean } | null>(null);
  // ── Roteiro (script) sidebar — story guide + AI page-script assistant ───────
  const [scriptOpen, setScriptOpen] = useState(false); // mobile drawer
  const [scriptDesktop, setScriptDesktop] = useState(true); // desktop column show/hide (like the book editor's sidebar)
  const [premise, setPremise] = useState('');
  const [projectTitle, setProjectTitle] = useState('');
  const [sceneText, setSceneText] = useState('');
  const [scenePanelCount, setScenePanelCount] = useState(4);
  const [scriptBusy, setScriptBusy] = useState(false);
  // Ghostwriter — completes the whole page's manual draft (see `runGhostwriter`).
  const [ghostwriterBusy, setGhostwriterBusy] = useState(false);
  // Conversational Ghostwriter (the SAME AIChat as the book editor) — opened by
  // the ✨ button and mod+shift+g; desktop = right side panel, mobile = drawer.
  const isMobile = useIsMobile();
  const [chatOpen, setChatOpen] = useState(false);
  // Which Universe characters/settings the author TICKED as present on this
  // page — the script generator makes them appear, by canonical name/looks.
  const [scriptCast, setScriptCast] = useState<Set<string>>(new Set());
  // Panel id → AI script (visual description + matched cast ids), used to
  // prefill the "Gerar arte" modal for panels generated from a script.
  const [panelScripts, setPanelScripts] = useState<Map<string, { description: string; castIds: string[] }>>(new Map());
  // ── Roteiro da página — ONE free-write block PER PANEL, in the same
  // screenplay grammar as manga-script.ts but without the "QUADRO N" marker
  // (see serializePanelBlock). `scriptDrafts` mirrors each panel's block text
  // while it's not dirty; typing marks that panel dirty so an unrelated
  // layout change never clobbers it. Applied automatically on blur — see
  // `applyPanelBlock` — which also backs the AI assistant's bulk apply.
  const [scriptDrafts, setScriptDrafts] = useState<Map<string, string>>(new Map());
  const [scriptDirty, setScriptDirty] = useState<Set<string>>(new Set());
  // Ephemeral "what just happened" feedback shown in a block's header for a
  // few seconds after it's applied (panel id → message).
  const [scriptFeedback, setScriptFeedback] = useState<Map<string, string>>(new Map());
  const scriptFeedbackTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Shows ONE explanatory toast the first time a block auto-applies this
  // session (inline feedback handles every apply after that).
  const scriptFirstApplyRef = useRef(false);
  const prevScriptPageIdRef = useRef<string | null>(null);
  const [scriptHelpOpen, setScriptHelpOpen] = useState(false); // "Como escrever?" example
  const [aiAssistOpen, setAiAssistOpen] = useState(false); // "Criar com IA" collapsible
  // "Gerar falas com IA" busy tracking — keyed by panel id for the per-block
  // action, or the literal 'all' for the bulk header/footer action.
  const [dialoguesBusy, setDialoguesBusy] = useState<Set<string>>(new Set());
  // Magnetic-snap guide lines shown while dragging/resizing a bubble.
  const [guides, setGuides] = useState<SnapGuideLine[]>([]);
  // Export popover (PNG / PDF) + in-progress status shown in its place.
  const [exportOpen, setExportOpen] = useState(false);
  const [exportProgress, setExportProgress] = useState<string | null>(null);
  // "Inverter direção de leitura" — mirrors EVERY page (layout, art, bubbles) in one shot.
  const [mirrorBusy, setMirrorBusy] = useState(false);
  // Bubble style popover (fill/stroke/text colours, font, tail, spikes…).
  const [styleOpen, setStyleOpen] = useState(false);
  // Insert-bubble popover — card picker (type of bubble to drop at the center).
  const [insertOpen, setInsertOpen] = useState(false);
  // Reading-direction selector popover (breadcrumb badge → real dropdown).
  const [dirMenuOpen, setDirMenuOpen] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<Drag | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const sketchFileRef = useRef<HTMLInputElement>(null);
  // Manuscrito — one textarea per panel block, keyed by panel id, so
  // Ctrl/Cmd+Enter can move focus straight to the NEXT panel's block
  // (sequential writing, no mouse) — see `handleBlockKeyDown`.
  const scriptTextareaRefs = useRef<Map<string, HTMLTextAreaElement>>(new Map());
  const lastGuidesKey = useRef('');
  const setGuidesIfChanged = useCallback((g: SnapGuideLine[]) => {
    const key = g.map((x) => `${x.axis}:${x.pos.toFixed(4)}`).join('|');
    if (key !== lastGuidesKey.current) { lastGuidesKey.current = key; setGuides(g); }
  }, []);

  const current = pages.find((p) => p.id === currentId) ?? null;

  // Leaving a panel (or switching selection) exits art-framing mode.
  useEffect(() => { setFraming(false); }, [selected?.id, selected?.kind]);
  // Leaving a bubble (or switching selection) closes its style popover.
  useEffect(() => { setStyleOpen(false); }, [selected?.id, selected?.kind]);

  // One step of reading — a small state machine per page:
  //   quadro0 → quadro1 → … → quadroN-1 → PÁGINA INTEIRA (zoom out) → [VIRADA] →
  //   próxima PÁGINA INTEIRA → quadro0 → … (guided, backward is the mirror).
  // Guided mode walks PANELS in reading order (zooming each); reaching either
  // end of the panel list first PAUSES on the whole page (no page change yet —
  // `next === cnt`/`next === -1` below), and only a SECOND step from that
  // whole-page pause actually turns the page (`crossPage`). Whole-page mode
  // (`!readerGuided`) turns the page on every step, no per-panel pauses.
  // Forward is always "the next thing to read".
  const crossPage = useCallback((dir: 1 | -1) => {
    if (readerIdx === null) return;
    const np = readerIdx + dir;
    if (np < 0 || np >= pages.length) return; // already at an edge — nothing to turn to
    const ncnt = pages[np]?.layout.panels.length ?? 0;
    // Land on the whole-page edge OPPOSITE the arrival direction: arriving
    // forward lands on the "entry" edge (next step reads panel 0); arriving
    // backward lands on the "exit" edge (next step reads the LAST panel).
    const landing = readerGuided && ncnt > 0 ? (dir === 1 ? -1 : ncnt) : null;
    const reduced = !readerAnim || (typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
    if (reduced) {
      // "Corte seco" — animations toggled off, or the OS asked for reduced
      // motion: no curl at all, the page just changes instantly.
      setReaderIdx(np);
      setPanelFocus(landing);
      return;
    }
    pendingLandingRef.current = { np, landing };
    setPageTurn({ dir }); // the effect below (keyed on `pageTurn`) drives the actual curl
  }, [readerIdx, readerGuided, readerAnim, pages]);

  const readerStep = useCallback((delta: 1 | -1) => {
    if (readerIdx === null || pageTurn) return; // ignore input mid-flip
    const cnt = pages[readerIdx]?.layout.panels.length ?? 0;
    if (readerGuided && cnt > 0) {
      const cur = panelFocus ?? (delta === 1 ? -1 : cnt); // fresh entry → normalize to the correct edge
      const next = cur + delta;
      if (next >= 0 && next < cnt) { setPanelFocus(next); return; } // move within panels
      if (next === cnt || next === -1) { setPanelFocus(next); return; } // reached a whole-page edge — pause here first
      crossPage(delta); // already AT the edge — advance for real, turn the page
    } else {
      crossPage(delta);
    }
  }, [readerIdx, readerGuided, panelFocus, pages, pageTurn, crossPage]);

  // Reader keyboard nav — arrows follow the reading direction; Esc closes.
  useEffect(() => {
    if (readerIdx === null) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { cancelPageTurn(); setReaderIdx(null); setPanelFocus(null); return; }
      const fwdKey = navFlip ? 'ArrowLeft' : 'ArrowRight';
      const backKey = navFlip ? 'ArrowRight' : 'ArrowLeft';
      if (e.key === fwdKey) readerStep(1);
      else if (e.key === backKey) readerStep(-1);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [readerIdx === null, navFlip, readerStep, cancelPageTurn]);

  // Drives the actual flip once `crossPage` sets `pageTurn`: by the time
  // this runs, the JSX below has ALREADY mounted the outgoing page's live
  // DOM into `flipSheetRef` (front face) and the incoming page's live DOM
  // into `flipIncomingRef`, in the SAME commit — a page is on screen every
  // single frame, there is no async gap to go blank in (see `page-flip.ts`'s
  // header for why the old rasterized curl couldn't make that guarantee).
  // `useLayoutEffect` (not `useEffect`) so the flip's frame-0 styles land
  // before the browser paints the freshly-mounted overlay.
  useLayoutEffect(() => {
    if (!pageTurn || readerIdx === null) return;
    let committed = false;
    const commit = (): void => {
      if (committed) return;
      committed = true;
      const pending = pendingLandingRef.current;
      pendingLandingRef.current = null;
      flipCancelRef.current = null;
      setPageTurn(null);
      if (pending) { setReaderIdx(pending.np); setPanelFocus(pending.landing); }
    };
    try {
      const sheet = flipSheetRef.current;
      const incoming = flipIncomingRef.current;
      const shade = flipShadeRef.current;
      if (!sheet || !incoming) throw new Error('flip refs not mounted');
      // Spine side follows the reading direction: LTR keeps the hinge as the
      // step direction; RTL mirrors it (spine on the RIGHT), so a forward turn
      // lifts from the correct edge for a manga.
      const hinge = (pageTurn.dir * (dir === 'rtl' ? -1 : 1)) as 1 | -1;
      flipCancelRef.current = runPageFlip(sheet, incoming, shade, { dir: hinge, durationMs: READER_FLIP_MS }, commit);
    } catch {
      // Never get stuck mid-navigation — corte seco imediato, commit right away.
      commit();
    }
    // Component unmount (or `pageTurn` changing again some other way) mid-
    // flip: tear down the flip's own listeners/timer so nothing calls back
    // into a stale closure.
    return () => {
      if (flipCancelRef.current) { flipCancelRef.current(); flipCancelRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTurn]);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const res = await api.getMangaPages(projectId);
        setPages(res.pages);
        setDir(res.readingDirection);
        setCurrentId(res.pages[0]?.id ?? null);
        // Load the cast/settings so panels can be generated with their references.
        api.getBibleEntries(projectId).then((r) => setCast(r.entries)).catch(() => {});
        // Load the book premise once — shown as the "story guide" header in the sidebar.
        api.getProject(projectId).then((r) => { setPremise(r.project.description ?? ''); setProjectTitle(r.project.title ?? ''); }).catch(() => {});
      } catch {
        notify.error(t('common.error', 'Erro'), t('manga.loadError', 'Não foi possível carregar o mangá.'));
      } finally { setLoading(false); }
    })();
  }, [projectId]);

  // Sync the app store so the AppShell knows which project is open — without
  // this, `isInEditor` stays false and the NORMAL app-nav sidebar shows on
  // desktop instead of the immersive editor shell (books do this in editor-view).
  const setCurrentProject = useAppStore((s) => s.setCurrentProject);
  const setAICurrentProject = useAIStore((s) => s.setCurrentProject);
  useEffect(() => {
    if (projectId) setCurrentProject(projectId);
  }, [projectId, setCurrentProject]);
  // Scope the conversational Ghostwriter's chat history to THIS project (the
  // AIChat store keeps messages per project, like the book editor does).
  useEffect(() => {
    if (projectId) setAICurrentProject(projectId);
  }, [projectId, setAICurrentProject]);

  // Default the script assistant's panel-count stepper to the current page's
  // existing panel count (or 4) whenever the user switches page.
  useEffect(() => {
    const pg = pages.find((p) => p.id === currentId);
    setScenePanelCount(pg?.layout.panels.length || 4);
  }, [currentId]);

  // Roteiro drafts sync — one draft per panel, re-serialized on a page switch
  // (an "outside" change) or when the panel SET changes on the same page
  // (e.g. a new layout adds/removes panels): existing drafts are preserved so
  // mid-typing edits are never clobbered, only missing/stale ids are
  // (re)filled. `applyPanelBlock`'s blur handler and the AI assistant force a
  // re-sync of the panel(s) they touch explicitly.
  const currentPanelIdsKey = current?.layout.panels.map((p) => p.id).join(',') ?? '';
  useEffect(() => {
    const pg = current;
    if (!pg) { setScriptDrafts(new Map()); setScriptDirty(new Set()); prevScriptPageIdRef.current = null; return; }
    const pageChanged = prevScriptPageIdRef.current !== pg.id;
    prevScriptPageIdRef.current = pg.id;
    setScriptDrafts((prev) => {
      const next = new Map<string, string>();
      pg.layout.panels.forEach((p, i) => {
        next.set(p.id, !pageChanged && prev.has(p.id) ? prev.get(p.id)! : serializePanelBlock(pg, i));
      });
      return next;
    });
    if (pageChanged) setScriptDirty(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId, currentPanelIdsKey]);

  // Clear any pending per-block feedback timers on unmount.
  useEffect(() => () => { scriptFeedbackTimers.current.forEach((tm) => clearTimeout(tm)); }, []);

  // Autosave indicator — same language as the book editor's status bar
  // (glowing dot + Salvo/Salvando/Não salvo), kept clean in the toolbar.
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'unsaved'>('saved');
  const scheduleSave = useCallback((pageId: string, layout: MangaLayout) => {
    setSaveStatus('unsaved');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      setSaveStatus('saving');
      api.updateMangaPage(pageId, { layout })
        .then(() => setSaveStatus('saved'))
        .catch(() => { setSaveStatus('unsaved'); notify.error('Erro', 'Falha ao salvar.'); });
    }, 700);
  }, []);

  const patch = useCallback((mut: (l: MangaLayout) => MangaLayout) => {
    setPages((prev) => prev.map((p) => {
      if (p.id !== currentId) return p;
      const layout = mut(p.layout);
      scheduleSave(p.id, layout);
      return { ...p, layout };
    }));
  }, [currentId, scheduleSave]);

  // ── History — undo/redo per page (Ctrl+Z / Ctrl+Y), deep stacks ─────────────
  const undoRef = useRef<Map<string, MangaLayout[]>>(new Map());
  const redoRef = useRef<Map<string, MangaLayout[]>>(new Map());
  /** Push the current layout onto the undo stack (call BEFORE a discrete change).
   * Continuous gestures (drags, typing auto-size) snapshot once at their start. */
  const snapshot = useCallback(() => {
    setPages((prev) => {
      const pg = prev.find((p) => p.id === currentId);
      if (pg) {
        const u = undoRef.current.get(pg.id) ?? [];
        u.push(pg.layout);
        if (u.length > 500) u.shift();
        undoRef.current.set(pg.id, u);
        redoRef.current.set(pg.id, []);
      }
      return prev; // same reference — React bails out, no re-render
    });
  }, [currentId]);
  const timeTravel = useCallback((dirn: 'undo' | 'redo') => {
    setPages((prev) => prev.map((p) => {
      if (p.id !== currentId) return p;
      const from = (dirn === 'undo' ? undoRef : redoRef).current.get(p.id) ?? [];
      const last = from.pop();
      if (!last) return p;
      const to = (dirn === 'undo' ? redoRef : undoRef).current.get(p.id) ?? [];
      to.push(p.layout);
      (dirn === 'undo' ? redoRef : undoRef).current.set(p.id, to);
      scheduleSave(p.id, last);
      return { ...p, layout: last };
    }));
    setSelected(null);
  }, [currentId, scheduleSave]);

  // Undo/redo (Ctrl+Z / Ctrl+Shift+Z / Ctrl+Y) is registered declaratively in
  // the shortcut engine below (with `blockInInput` so a focused bubble/textarea
  // keeps its native text-undo). See the `shortcuts` array.

  // ── Procedural layout — set the panel count, generate/shuffle organic layouts ─
  /** Applies a new set of panel shapes WITHOUT losing what's on the page: art
   * (imageKey/imageUrl) and framing carry over by reading order, and panel ids
   * are kept so bubbles/undo stay coherent. Extra art beyond the new count is
   * dropped only from the page, never from storage. */
  const applyLayout = (shapes: { x: number; y: number }[][]) => {
    snapshot();
    patch((l) => ({
      ...l,
      panels: shapes.map((points, i) => {
        const prev = l.panels[i];
        return {
          id: prev?.id ?? uid(), points,
          imageKey: prev?.imageKey, imageUrl: prev?.imageUrl,
          sketchKey: prev?.sketchKey, sketchUrl: prev?.sketchUrl,
          scale: prev?.scale, offsetX: prev?.offsetX, offsetY: prev?.offsetY,
          flipX: prev?.flipX, flipY: prev?.flipY,
        };
      }),
    }));
    setSelected(null);
  };
  const genLayout = (n: number) => applyLayout(generateMangaLayout(n, dir));
  const clearPanelArt = (id: string) => { snapshot(); patch((l) => ({
    ...l, panels: l.panels.map((p) => (p.id === id ? { ...p, imageKey: null, imageUrl: null } : p)),
  })); };

  const setPanelArt = (id: string, imageKey: string, imageUrl: string) => { snapshot(); patch((l) => ({
    ...l, panels: l.panels.map((p) => (p.id === id ? { ...p, imageKey, imageUrl } : p)),
  })); };

  // ── Art framing inside a panel (zoom + reset) ────────────────────────────────
  const clampScale = (n: number) => Math.max(0.5, Math.min(4, n));
  const nudgeScale = (id: string, delta: number) => { snapshot(); patch((l) => ({
    ...l, panels: l.panels.map((p) => (p.id === id ? { ...p, scale: clampScale((p.scale ?? 1) + delta) } : p)),
  })); };
  const resetFrame = (id: string) => { snapshot(); patch((l) => ({
    ...l, panels: l.panels.map((p) => (p.id === id ? { ...p, scale: 1, offsetX: 0, offsetY: 0, flipX: false, flipY: false } : p)),
  })); };
  const toggleFlip = (id: string, axis: 'flipX' | 'flipY') => { snapshot(); patch((l) => ({
    ...l, panels: l.panels.map((p) => (p.id === id ? { ...p, [axis]: !p[axis] } : p)),
  })); };

  // Prefills from the AI-generated script when this panel has one (description +
  // the cast members whose canonical names were detected in it).
  const openGen = (id: string) => {
    // Persisted script on the panel wins (survives reload); session map is the fallback.
    const panel = current?.layout.panels.find((p) => p.id === id);
    const script = panel?.script ?? panelScripts.get(id);
    setGenModal({ panelId: id, prompt: script?.description ?? '', refs: new Set(script?.castIds ?? []), useSketch: !!panel?.sketchKey });
  };

  // ── Generate the WHOLE PAGE from the script — every panel's art, in reading
  // order, driven by the persisted description + cast of each panel. Panels
  // that already have art are kept; if all have art, everything regenerates.
  const [genAll, setGenAll] = useState<{ done: number; total: number } | null>(null);
  const generateAllArts = async () => {
    if (!projectId || !current) return;
    // Coverage accounting: every panel is either scripted (has a usable
    // description), or reported as "no description" — never silently skipped.
    const withScript: { p: MangaPanel; desc: string; castIds: string[]; num: number }[] = [];
    const noScript: number[] = [];
    current.layout.panels.forEach((p, i) => {
      const s = p.script ?? panelScripts.get(p.id);
      if (s && s.description.trim()) withScript.push({ p, desc: s.description, castIds: s.castIds, num: i + 1 });
      else noScript.push(i + 1);
    });
    if (!withScript.length) { notify.info(t('manga.genAllNone', 'Gere o roteiro da página primeiro.')); return; }
    const missing = withScript.filter(({ p }) => !p.imageUrl);
    const targets = missing.length ? missing : withScript; // all done → regenerate all
    setGenAll({ done: 0, total: targets.length });
    const failed: number[] = [];
    let hitLimit = false;
    for (let i = 0; i < targets.length; i++) {
      const { p, desc, castIds, num } = targets[i];
      // One retry per panel (transient 5xx/timeouts happen on long image gens) —
      // a single failure never aborts the rest of the page.
      let ok = false;
      for (let attempt = 0; attempt < 2 && !ok; attempt++) {
        try {
          const { imageUrl, imageKey } = await api.generateMangaImage(projectId, desc + stagingForPanel(p.id), {
            referenceIds: castIds, size: sizeForPanel(p.id), sketchKey: p.sketchKey ?? undefined,
          });
          setPanelArt(p.id, imageKey, imageUrl);
          ok = true;
        } catch (e) {
          if (e instanceof AiLimitError) { hitLimit = true; break; }
          if (attempt === 0) await new Promise((r) => setTimeout(r, 1800));
        }
      }
      if (!ok) failed.push(num);
      setGenAll({ done: i + 1, total: targets.length });
      if (hitLimit) break;
    }
    setGenAll(null);
    // Honest report: exactly what was generated, what failed, what has no script.
    if (hitLimit) {
      notify.aiLimitReached('cover');
    } else if (!failed.length && !noScript.length) {
      notify.success(t('manga.genAllDone', 'Artes da página geradas!'));
    } else {
      const parts: string[] = [];
      if (failed.length) parts.push(t('manga.genAllFailed', 'Falharam: quadro(s) {{list}} — clique de novo para completar.', { list: failed.join(', ') }));
      if (noScript.length) parts.push(t('manga.genAllNoDesc', 'Sem descrição no roteiro: quadro(s) {{list}}.', { list: noScript.join(', ') }));
      notify.warning(
        t('manga.genAllPartial', '{{done}}/{{total}} artes geradas', { done: targets.length - failed.length, total: targets.length }),
        parts.join(' '),
      );
    }
  };

  // Thin wrappers over the module-level pure helpers, bound to the CURRENT page.
  const sizeForPanel = (panelId: string) => computeSizeForPanel(current, panelId);
  const stagingForPanel = (panelId: string) => computeStagingForPanel(current, panelId);

  const runGen = async () => {
    if (!projectId || !genModal) return;
    const { panelId, prompt, refs, useSketch } = genModal;
    if (!prompt.trim()) { notify.error(t('common.error', 'Erro'), t('manga.describeScene', 'Descreva a cena.')); return; }
    const panel = current?.layout.panels.find((p) => p.id === panelId);
    setBusy('gen');
    try {
      const { imageUrl, imageKey } = await api.generateMangaImage(projectId, prompt + stagingForPanel(panelId), {
        referenceIds: [...refs],
        size: sizeForPanel(panelId),
        sketchKey: useSketch && panel?.sketchKey ? panel.sketchKey : undefined,
      });
      setPanelArt(panelId, imageKey, imageUrl);
      setGenModal(null);
    } catch (e) {
      notify.error(t('common.error', 'Erro'), (e as { message?: string })?.message === 'ai_limit' ? t('manga.imageLimit', 'Limite de imagens atingido.') : t('manga.genFail', 'Falha ao gerar.'));
    } finally { setBusy(null); }
  };

  const onPanelUpload = (id: string, file: File) => {
    if (!projectId) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy('gen');
      try {
        const { imageKey, imageUrl } = await api.uploadMangaImage(projectId, reader.result as string);
        setPanelArt(id, imageKey, imageUrl);
      } catch { notify.error(t('common.error', 'Erro'), t('manga.uploadFail', 'Falha no upload.')); }
      finally { setBusy(null); }
    };
    reader.readAsDataURL(file);
  };

  // ── Rabisco (sketch) — a DIFFERENT state from the finished art: an
  // author-uploaded rough blueprint (stick-figure pose/composition) that can
  // later be "transformed" into the final illustration. Kept even after the
  // final art exists, so the author can regenerate from it again. ───────────
  const setPanelSketch = (id: string, sketchKey: string, sketchUrl: string) => { snapshot(); patch((l) => ({
    ...l, panels: l.panels.map((p) => (p.id === id ? { ...p, sketchKey, sketchUrl } : p)),
  })); };

  const onSketchUpload = (id: string, file: File) => {
    if (!projectId) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy('sketch');
      try {
        const { imageKey, imageUrl } = await api.uploadMangaImage(projectId, reader.result as string);
        setPanelSketch(id, imageKey, imageUrl);
      } catch { notify.error(t('common.error', 'Erro'), t('manga.uploadFail', 'Falha no upload.')); }
      finally { setBusy(null); }
    };
    reader.readAsDataURL(file);
  };

  // Single-panel "Transformar rabisco" — generates the final art straight from
  // the sketch + whatever script description already exists, no modal needed
  // (the modal's own checkbox covers the deliberate/manual path).
  const transformSketchPanel = async (id: string) => {
    if (!projectId || !current) return;
    const panel = current.layout.panels.find((p) => p.id === id);
    if (!panel?.sketchKey) return;
    const script = panel.script ?? panelScripts.get(id);
    const desc = (script?.description ?? genModal?.prompt ?? '').trim()
      || t('manga.sketchDefaultPrompt', 'Ilustre este quadro a partir do rabisco anexado, seguindo sua composição e enquadramento.');
    setBusy('gen');
    try {
      const { imageUrl, imageKey } = await api.generateMangaImage(projectId, desc + stagingForPanel(id), {
        referenceIds: script?.castIds ?? [],
        size: sizeForPanel(id),
        sketchKey: panel.sketchKey,
      });
      setPanelArt(id, imageKey, imageUrl);
    } catch (e) {
      notify.error(t('common.error', 'Erro'), e instanceof AiLimitError ? e.message : t('manga.genFail', 'Falha ao gerar.'));
    } finally { setBusy(null); }
  };

  // ── Transformar rabiscos (TODAS as páginas) — walks every page (not just
  // `current`), generating only panels that have a sketch and NO final art
  // yet, sequential with progress + a retry-then-report policy identical to
  // `generateAllArts`. Persists page-by-page via updateMangaPage directly
  // (mirrors it in local state too) since most target pages aren't the one
  // currently open (no undo/autosave plumbing for those).
  const [transformAll, setTransformAll] = useState<{ done: number; total: number } | null>(null);
  const anySketchNeedsTransform = pages.some((pg) => pg.layout.panels.some((p) => p.sketchKey && !p.imageUrl));
  const transformAllSketches = async () => {
    if (!projectId) return;
    const targets: Array<{ pageId: string; panelId: string; pageNum: number; panelNum: number }> = [];
    pages.forEach((pg, pgi) => {
      pg.layout.panels.forEach((p, pi) => {
        if (p.sketchKey && !p.imageUrl) targets.push({ pageId: pg.id, panelId: p.id, pageNum: pgi + 1, panelNum: pi + 1 });
      });
    });
    if (!targets.length) return;
    setTransformAll({ done: 0, total: targets.length });
    // LOCAL working copy — accumulates each successful panel's art across
    // iterations of the SAME page. `pages`/`current` (React state) only
    // updates on the NEXT render, so re-reading them mid-loop would silently
    // undo an earlier panel's just-generated art when a sibling panel on the
    // same page finishes right after.
    const workingPages = new Map(pages.map((pg) => [pg.id, pg]));
    const failed: string[] = [];
    let hitLimit = false;
    for (let i = 0; i < targets.length; i++) {
      const { pageId, panelId, pageNum, panelNum } = targets[i];
      const page = workingPages.get(pageId) ?? null;
      const panel = page?.layout.panels.find((p) => p.id === panelId);
      if (!page || !panel?.sketchKey || panel.imageUrl) { setTransformAll({ done: i + 1, total: targets.length }); continue; }
      const desc = (panel.script?.description ?? '').trim()
        || t('manga.sketchDefaultPrompt', 'Ilustre este quadro a partir do rabisco anexado, seguindo sua composição e enquadramento.');
      let ok = false;
      for (let attempt = 0; attempt < 2 && !ok; attempt++) {
        try {
          const { imageUrl, imageKey } = await api.generateMangaImage(projectId, desc + computeStagingForPanel(page, panelId), {
            referenceIds: panel.script?.castIds ?? [],
            size: computeSizeForPanel(page, panelId),
            sketchKey: panel.sketchKey,
          });
          const newLayout: MangaLayout = {
            ...page.layout,
            panels: page.layout.panels.map((p) => (p.id === panelId ? { ...p, imageKey, imageUrl } : p)),
          };
          const updatedPage = { ...page, layout: newLayout };
          await api.updateMangaPage(page.id, { layout: newLayout });
          workingPages.set(page.id, updatedPage);
          setPages((prev) => prev.map((pg) => (pg.id === page.id ? updatedPage : pg)));
          ok = true;
        } catch (e) {
          if (e instanceof AiLimitError) { hitLimit = true; break; }
          if (attempt === 0) await new Promise((r) => setTimeout(r, 1800));
        }
      }
      if (!ok && !hitLimit) failed.push(`${pageNum}.${panelNum}`);
      setTransformAll({ done: i + 1, total: targets.length });
      if (hitLimit) break;
    }
    setTransformAll(null);
    if (hitLimit) {
      notify.aiLimitReached('cover');
    } else if (!failed.length) {
      notify.success(t('manga.transformAllDone', 'Rabiscos transformados em arte!'));
    } else {
      notify.warning(
        t('manga.transformAllPartial', '{{done}}/{{total}} transformados', { done: targets.length - failed.length, total: targets.length }),
        t('manga.transformAllFailed', 'Falharam: página.quadro {{list}} — clique de novo para completar.', { list: failed.join(', ') }),
      );
    }
  };

  // ── Bubbles ─────────────────────────────────────────────────────────────────
  // Insertion recipe copied from the reference bubble-creator: the bubble drops
  // ready-made at the canvas center (zip sizes in px, converted to page
  // fractions), tail already pointing down-left with the curved style — the user
  // just drags, resizes by the corners and re-aims the tail handle.
  const addBubble = (type: BubbleType) => {
    const pageW = current?.width || 1000, pageH = current?.height || 1414;
    const isBox = type === 'narration' || type === 'caption';
    const isSfx = type === 'sfx';
    // SFX letreiro drops in BIG (~40% of the page width) and pre-tilted —
    // it's meant to read like a poster onomatopoeia, not a balloon.
    const wPx = isSfx ? pageW * 0.4 : isBox ? 180 : 200;
    const hPx = isSfx ? pageW * 0.4 * 0.45 : isBox ? 100 : 130;
    const w = wPx / pageW, h = hPx / pageH;
    const x = 0.5 - w / 2, y = 0.5 - h / 2;
    const bubble: MangaBubble = {
      id: uid(), type, x, y, w, h,
      text: type === 'shout' ? t('manga.defaultShout', 'AAAH!')
        : type === 'whisper' ? t('manga.defaultWhisper', 'sussurra baixinho…')
        : isSfx ? t('manga.defaultSfx', 'BOOM')
        : t('manga.defaultText', 'Texto…'),
      // tail tip 80px left / 120px below the center, like the zip's defaults
      tail: isBox || isSfx ? undefined : { x: -80 / pageW, y: 120 / pageH },
      tailStyle: 'curved',
      tailWidth: type === 'thought' ? 14 : 20,
      align: 'center',
      rotation: isSfx ? -8 : undefined,
    };
    snapshot();
    patch((l) => ({ ...l, bubbles: [...l.bubbles, bubble] }));
    setSelected({ kind: 'bubble', id: bubble.id });
  };
  const setText = (id: string, text: string) => { snapshot(); patch((l) => ({
    ...l, bubbles: l.bubbles.map((b) => (b.id === id ? { ...b, text } : b)),
  })); };
  // Liquid auto-size: grows a bubble's w/h (never shrinks) so its text fits.
  // Called from the Bubble component while typing (throttled) and on blur.
  const autoSizeBubble = useCallback((id: string, size: { w?: number; h?: number }) => patch((l) => ({
    ...l, bubbles: l.bubbles.map((b) => (b.id === id ? { ...b, ...size } : b)),
  })), [patch]);
  const deleteSelected = () => {
    if (!selected) return;
    snapshot();
    patch((l) => selected.kind === 'bubble'
      ? { ...l, bubbles: l.bubbles.filter((b) => b.id !== selected.id) }
      : { ...l, panels: l.panels.filter((p) => p.id !== selected.id) });
    setSelected(null);
  };
  // Twin bubble — duplicates the selected bubble flush against its side (or the
  // opposite side if it would overflow the page), tail mirrored — the classic
  // manga "double balloon" for back-and-forth dialogue.
  const duplicateTwin = () => {
    if (!selected || selected.kind !== 'bubble') return;
    const b = current?.layout.bubbles.find((x) => x.id === selected.id);
    if (!b) return;
    // Negative gap: the twin OVERLAPS the original slightly instead of just
    // touching it, so same-type/same-fill balloons (speech/whisper) are born
    // already FUSED — see `groupJoinedBubbles` (bubble-path.ts) — the seam
    // disappears automatically in every renderer, no extra step needed.
    const gap = -0.01;
    let nx = b.x + b.w + gap;
    if (nx + b.w > 1) nx = Math.max(0, b.x - b.w - gap);
    const twin: MangaBubble = {
      id: uid(), type: b.type, x: nx, y: b.y, w: b.w, h: b.h,
      text: t('manga.twinText', 'Texto…'),
      tail: b.tail ? { x: -b.tail.x, y: b.tail.y } : undefined,
      align: b.align,
      // Same look as the original — required for the fusion eligibility check
      // (same EFFECTIVE fill colour) to actually hold, and just makes sense
      // for a "twin" duplicate regardless.
      fillColor: b.fillColor, strokeColor: b.strokeColor, strokeWidth: b.strokeWidth, textColor: b.textColor,
      fontFamily: b.fontFamily, fontSize: b.fontSize, fontStyle: b.fontStyle, padding: b.padding,
      bodyShape: b.bodyShape, tailStyle: b.tailStyle, tailWidth: b.tailWidth,
    };
    snapshot();
    patch((l) => ({ ...l, bubbles: [...l.bubbles, twin] }));
    setSelected({ kind: 'bubble', id: twin.id });
  };
  // Text controls for the selected bubble — alignment + font family.
  const setBubbleAlign = (align: 'left' | 'center' | 'right') => {
    if (!selected || selected.kind !== 'bubble') return;
    snapshot();
    patch((l) => ({ ...l, bubbles: l.bubbles.map((b) => (b.id === selected.id ? { ...b, align } : b)) }));
  };
  const setBubbleFontFamily = (fontFamily: string) => {
    if (!selected || selected.kind !== 'bubble') return;
    snapshot();
    patch((l) => ({ ...l, bubbles: l.bubbles.map((b) => (b.id === selected.id ? { ...b, fontFamily } : b)) }));
  };
  // Layer ordering — bubbles render in array order, so "front"/"back" is just
  // swapping the selected bubble with its neighbor. One undo step per click.
  const moveLayerUp = (id: string) => {
    const list = current?.layout.bubbles ?? [];
    const idx = list.findIndex((b) => b.id === id);
    if (idx === -1 || idx === list.length - 1) return;
    snapshot();
    patch((l) => {
      const next = [...l.bubbles];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return { ...l, bubbles: next };
    });
  };
  const moveLayerDown = (id: string) => {
    const list = current?.layout.bubbles ?? [];
    const idx = list.findIndex((b) => b.id === id);
    if (idx <= 0) return;
    snapshot();
    patch((l) => {
      const next = [...l.bubbles];
      [next[idx], next[idx - 1]] = [next[idx - 1], next[idx]];
      return { ...l, bubbles: next };
    });
  };
  // Style panel controls (fill/stroke/text colours, stroke width, font size,
  // padding, tail width/style, spikes, border radius, font weight/italic).
  // Continuous controls (sliders/colour pickers) call `snapshot()` ONCE on
  // pointerdown (gesture start) and this on every subsequent tick — so a drag
  // across a slider is a SINGLE undo step, not one per intermediate value.
  const updateSelectedBubbleStyle = (updates: Partial<MangaBubble>) => {
    if (!selected || selected.kind !== 'bubble') return;
    patch((l) => ({ ...l, bubbles: l.bubbles.map((b) => (b.id === selected.id ? { ...b, ...updates } : b)) }));
  };

  // ── AI script assistant — turns a scene description into panel descriptions
  // + ready-made, positioned dialogue bubbles for the current page. ──────────
  const generateScriptForPage = async () => {
    if (!projectId || !current) return;
    const desc = sceneText.trim();
    if (!desc) { notify.error(t('manga.scriptNeedTextTitle', 'Erro'), t('manga.scriptNeedText', 'Descreva o que acontece nesta página.')); return; }
    setScriptBusy(true);
    try {
      const { panels: scriptPanels, newCast } = await api.generateMangaScript(projectId, desc, scenePanelCount, [...scriptCast], { pageIndex: pages.findIndex((p) => p.id === currentId) });
      snapshot();

      // The script may INTRODUCE new characters/places — the server already
      // created them in the Universe; refresh the cast and tell the author.
      if (newCast?.length) {
        refreshCastSoon();
        notify.success(
          t('manga.newCastTitle', 'Universo atualizado'),
          t('manga.newCastAdded', 'Novo(s) no Universo: {{names}} — a imagem de referência deles está sendo gerada automaticamente.', { names: newCast.map((c) => c.name).join(', ') }),
        );
      }

      // Ensure the page has exactly `scenePanelCount` panels — regenerate the
      // layout (preserving existing art by reading order) only if it differs.
      const panelsForScript: MangaPanel[] = current.layout.panels.length === scenePanelCount
        ? current.layout.panels
        : generateMangaLayout(scenePanelCount, dir).map((points, i) => {
            const prev = current.layout.panels[i];
            return {
              id: prev?.id ?? uid(), points,
              imageKey: prev?.imageKey, imageUrl: prev?.imageUrl,
              scale: prev?.scale, offsetX: prev?.offsetX, offsetY: prev?.offsetY,
              flipX: prev?.flipX, flipY: prev?.flipY,
            };
          });
      // The script is the SOURCE OF TRUTH of the page: persist each panel's
      // description + cast INSIDE the panel (survives reload, drives art
      // generation), and build ready-made bubbles carrying WHO speaks each line
      // (character / NARRATOR / SFX) in reading order — via the SAME recipe
      // (`buildDialogueBubble`) the manual "+ Fala" form uses.
      const nextScripts = new Map(panelScripts);
      const newBubbles: MangaBubble[] = [];
      const panelsWithScript = panelsForScript.map((panel, i) => {
        const sp = scriptPanels[i];
        if (!sp) return panel;
        nextScripts.set(panel.id, { description: sp.description, castIds: sp.castIds });
        sp.dialogues.forEach((d, j) => newBubbles.push(buildDialogueBubble(panel, j, d)));
        return { ...panel, script: { description: sp.description, castIds: sp.castIds } };
      });
      setPanelScripts(nextScripts);
      const nextBubbles = [...current.layout.bubbles, ...newBubbles];
      // One patch: panels (shapes + persisted scripts) and bubbles together.
      patch((l) => ({ ...l, panels: panelsWithScript, bubbles: nextBubbles }));
      // The AI just rewrote the WHOLE page — re-sync every block's draft from
      // the fresh result so they show immediately, no stale text (a dirty
      // draft the author is mid-typing would be an edge case here too, but
      // the AI having just overwritten the page is the expected outcome).
      const freshPage = { ...current, layout: { panels: panelsWithScript, bubbles: nextBubbles } };
      setScriptDrafts(new Map(panelsWithScript.map((p, i) => [p.id, serializePanelBlock(freshPage, i)])));
      setScriptDirty(new Set());

      notify.success(t('manga.scriptApplied', 'Roteiro aplicado'), t('manga.scriptAppliedHint', 'Ajuste os balões e gere as artes.'));
    } catch (e) {
      notify.error('Erro', e instanceof AiLimitError ? e.message : t('manga.scriptError', 'Falha ao gerar roteiro. Tente novamente.'));
    } finally { setScriptBusy(false); }
  };

  // ── Roteiro block → panel — parses ONE panel's free-write block and patches
  // JUST that panel's description/cast + dialogue bubbles in one go,
  // reconciling existing script-managed bubbles (those with a `speaker`) in
  // this panel by speaker+text so untouched lines KEEP their position/size/
  // style across re-applies. Never grows/shrinks the layout (a marker-less
  // block is always exactly one panel's worth of content — see
  // manga-script.ts). Shared by the per-block blur handler and, indirectly,
  // by the AI assistant's bulk write (which builds bubbles the same way via
  // `buildDialogueBubble` directly, since it fills every panel at once). ────
  const applyPanelBlock = (panelIndex: number, parsed: { description: string; dialogues: ParsedDialogue[] }): { bubbleCount: number; normalized: string } | null => {
    if (!current) return null;
    const panel = current.layout.panels[panelIndex];
    if (!panel) return null;

    const findCastIds = (text: string): string[] => {
      const lower = text.toLowerCase();
      return cast.filter((c) => c.name && lower.includes(c.name.toLowerCase())).map((c) => c.id);
    };

    const xs = panel.points.map((pt) => pt.x), ys = panel.points.map((pt) => pt.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
    const existingBubbles = current.layout.bubbles;
    const managedInPanel = existingBubbles.filter((b) => {
      if (!b.speaker) return false;
      const cx = b.x + b.w / 2, cy = b.y + b.h / 2;
      return cx >= minX && cx <= maxX && cy >= minY && cy <= maxY;
    });
    const usedExistingIds = new Set<string>();
    const scriptedBubbles: MangaBubble[] = [];
    parsed.dialogues.forEach((d, j) => {
      const reused = d.speaker
        ? managedInPanel.find((b) => !usedExistingIds.has(b.id) && b.speaker === d.speaker && b.text === d.text)
        : undefined;
      if (reused) {
        usedExistingIds.add(reused.id);
        scriptedBubbles.push(reused.type === d.type ? reused : { ...reused, type: d.type });
      } else {
        scriptedBubbles.push(buildDialogueBubble(panel, j, d));
      }
    });

    const castText = [parsed.description, ...parsed.dialogues.map((d) => `${d.speaker} ${d.text}`)].join(' ');
    const nextPanels = current.layout.panels.map((p, i) => (
      i === panelIndex ? { ...p, script: { description: parsed.description, castIds: findCastIds(castText) } } : p
    ));
    // Everything OUTSIDE this panel — manual bubbles and other panels' script
    // bubbles — stays exactly as-is; only THIS panel's managed bubbles are
    // replaced by the freshly reconciled set.
    const managedSet = new Set(managedInPanel.map((b) => b.id));
    const outsideBubbles = existingBubbles.filter((b) => !managedSet.has(b.id));
    const nextBubbles = [...outsideBubbles, ...scriptedBubbles];

    patch((l) => ({ ...l, panels: nextPanels, bubbles: nextBubbles }));
    // Re-serialize what was just applied — normalizes whitespace/order so the
    // block reflects exactly what's now on the page (e.g. trimmed prose).
    const normalized = serializePanelBlock({ ...current, layout: { panels: nextPanels, bubbles: nextBubbles } }, panelIndex);
    return { bubbleCount: scriptedBubbles.length, normalized };
  };

  // ── Bulk version of applyPanelBlock's reconciliation — applies MULTIPLE
  // panels' dialogues in ONE patch, computed off a SINGLE consistent snapshot
  // of `current`. Calling applyPanelBlock in a loop would be unsafe here:
  // each call captures `current` from this render's closure and overwrites
  // the WHOLE panels/bubbles arrays with it, so a second call in the same
  // synchronous pass would silently discard the first call's changes. Used by
  // the "Gerar falas com IA" flow (bulk + per-panel, same underlying call). ─
  const applyDialoguesBulk = (
    results: Array<{ panelIndex: number; description: string; dialogues: ParsedDialogue[] }>,
  ): { totalBubbles: number; drafts: Map<string, string> } | null => {
    if (!current) return null;
    const findCastIds = (text: string): string[] => {
      const lower = text.toLowerCase();
      return cast.filter((c) => c.name && lower.includes(c.name.toLowerCase())).map((c) => c.id);
    };
    const byIndex = new Map(results.map((r) => [r.panelIndex, r]));
    const existingBubbles = current.layout.bubbles;
    const allScriptedBubbles: MangaBubble[] = [];
    const managedIdsToRemove = new Set<string>();
    let totalBubbles = 0;
    const nextPanels = current.layout.panels.map((p, i) => {
      const r = byIndex.get(i);
      if (!r) return p;
      const xs = p.points.map((pt) => pt.x), ys = p.points.map((pt) => pt.y);
      const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
      const managedInPanel = existingBubbles.filter((b) => {
        if (!b.speaker) return false;
        const cx = b.x + b.w / 2, cy = b.y + b.h / 2;
        return cx >= minX && cx <= maxX && cy >= minY && cy <= maxY;
      });
      const usedExistingIds = new Set<string>();
      r.dialogues.forEach((d, j) => {
        const reused = d.speaker
          ? managedInPanel.find((b) => !usedExistingIds.has(b.id) && b.speaker === d.speaker && b.text === d.text)
          : undefined;
        if (reused) {
          usedExistingIds.add(reused.id);
          allScriptedBubbles.push(reused.type === d.type ? reused : { ...reused, type: d.type });
        } else {
          allScriptedBubbles.push(buildDialogueBubble(p, j, d));
        }
      });
      managedInPanel.forEach((b) => managedIdsToRemove.add(b.id));
      totalBubbles += r.dialogues.length;
      const castText = [r.description, ...r.dialogues.map((d) => `${d.speaker} ${d.text}`)].join(' ');
      return { ...p, script: { description: r.description, castIds: findCastIds(castText) } };
    });
    const outsideBubbles = existingBubbles.filter((b) => !managedIdsToRemove.has(b.id));
    const nextBubbles = [...outsideBubbles, ...allScriptedBubbles];
    patch((l) => ({ ...l, panels: nextPanels, bubbles: nextBubbles }));
    const freshPage = { ...current, layout: { panels: nextPanels, bubbles: nextBubbles } };
    const drafts = new Map<string, string>();
    results.forEach((r) => { drafts.set(nextPanels[r.panelIndex].id, serializePanelBlock(freshPage, r.panelIndex)); });
    return { totalBubbles, drafts };
  };

  // Shows a brief "✓ 2 balões · descrição salva" note in a block's header,
  // auto-clearing after a few seconds (one timer per panel, replacing any
  // still-pending one from a previous apply on the same block).
  const flashScriptFeedback = (panelId: string, msg: string) => {
    setScriptFeedback((prev) => new Map(prev).set(panelId, msg));
    const timers = scriptFeedbackTimers.current;
    const existing = timers.get(panelId);
    if (existing) clearTimeout(existing);
    timers.set(panelId, setTimeout(() => {
      setScriptFeedback((prev) => { if (!prev.has(panelId)) return prev; const next = new Map(prev); next.delete(panelId); return next; });
      timers.delete(panelId);
    }, 2500));
  };

  // Current effective description for a panel — the LIVE draft when it's
  // mid-edit (dirty, not yet applied), otherwise whatever is persisted
  // (panel.script survives reload; the session-only panelScripts map is the
  // fallback for panels generated but not yet round-tripped through a save).
  const getPanelDescription = useCallback((panel: MangaPanel): string => {
    if (scriptDirty.has(panel.id)) {
      const draft = scriptDrafts.get(panel.id) ?? '';
      const { panels: parsedPanels } = parsePageScript(draft);
      return (parsedPanels[0]?.description ?? '').trim();
    }
    return (panel.script?.description ?? panelScripts.get(panel.id)?.description ?? '').trim();
  }, [scriptDirty, scriptDrafts, panelScripts]);

  // ── "Gerar falas com IA" — writes ONLY the dialogues for the given panels,
  // never touching their (already written) descriptions. Panels without a
  // description are skipped and reported, never silently dropped. Shared by
  // the section header/footer "✨ Gerar falas" (all panels) and each block's
  // compact "Falas" action (one panel). ────────────────────────────────────
  const generateDialoguesFor = async (panelIndexes: number[], busyKey: string) => {
    if (!projectId || !current) return;
    const targets = panelIndexes
      .map((i) => ({ i, panel: current.layout.panels[i] }))
      .filter((x): x is { i: number; panel: MangaPanel } => !!x.panel)
      .map((x) => ({ ...x, desc: getPanelDescription(x.panel) }));
    const withDesc = targets.filter((x) => x.desc.length > 0);
    const missing = targets.filter((x) => !x.desc.length).map((x) => x.i + 1);
    if (!withDesc.length) {
      notify.error(t('common.error', 'Erro'), t('manga.dialoguesNoDesc', 'Escreva a descrição do(s) quadro(s) primeiro.'));
      return;
    }
    setDialoguesBusy((prev) => new Set(prev).add(busyKey));
    try {
      const existingPanels = withDesc.map((x) => ({ description: x.desc }));
      const sceneDescription = withDesc.map((x) => x.desc).join('\n').slice(0, 3000);
      const { panels: resultPanels, newCast } = await api.generateMangaScript(
        projectId, sceneDescription, existingPanels.length, [...scriptCast],
        { mode: 'dialogues', existingPanels, pageIndex: pages.findIndex((p) => p.id === currentId) },
      );

      if (newCast?.length) {
        refreshCastSoon();
        notify.success(
          t('manga.newCastTitle', 'Universo atualizado'),
          t('manga.newCastAdded', 'Novo(s) no Universo: {{names}} — a imagem de referência deles está sendo gerada automaticamente.', { names: newCast.map((c) => c.name).join(', ') }),
        );
      }

      snapshot();
      const applyResults = withDesc.map((x, k) => ({
        panelIndex: x.i,
        description: x.desc,
        dialogues: (resultPanels[k]?.dialogues ?? []) as ParsedDialogue[],
      }));
      const result = applyDialoguesBulk(applyResults);
      if (result) {
        setScriptDrafts((prev) => { const next = new Map(prev); result.drafts.forEach((v, id) => next.set(id, v)); return next; });
        setScriptDirty((prev) => { const next = new Set(prev); result.drafts.forEach((_v, id) => next.delete(id)); return next; });
        const parts: string[] = [t('manga.dialoguesGenerated', { count: result.totalBubbles, defaultValue: '{{count}} balão gerado' })];
        if (missing.length) parts.push(t('manga.dialoguesSkipped', 'Sem descrição: quadro(s) {{list}}.', { list: missing.join(', ') }));
        notify.success(t('manga.dialoguesDoneTitle', 'Falas geradas'), parts.join(' '));
      }
    } catch (e) {
      notify.error(t('common.error', 'Erro'), e instanceof AiLimitError ? e.message : t('manga.dialoguesError', 'Falha ao gerar falas. Tente novamente.'));
    } finally {
      setDialoguesBusy((prev) => { const next = new Set(prev); next.delete(busyKey); return next; });
    }
  };

  // Auto-apply on blur — only when the block was actually edited (dirty).
  const handlePanelBlockBlur = (panelIndex: number, panelId: string) => {
    if (!scriptDirty.has(panelId)) return;
    const text = scriptDrafts.get(panelId) ?? '';
    const { panels: parsedPanels } = parsePageScript(text);
    const pp = parsedPanels[0] ?? { description: '', dialogues: [] };
    snapshot();
    const result = applyPanelBlock(panelIndex, pp);
    setScriptDirty((prev) => { if (!prev.has(panelId)) return prev; const next = new Set(prev); next.delete(panelId); return next; });
    if (!result) return;
    setScriptDrafts((prev) => new Map(prev).set(panelId, result.normalized));

    const hasDescription = !!pp.description.trim();
    const parts: string[] = [];
    if (result.bubbleCount > 0) parts.push(t('manga.scriptBlockBubblesCount', { count: result.bubbleCount, defaultValue: '{{count}} balão' }));
    if (hasDescription) parts.push(t('manga.scriptBlockDescSaved', 'descrição salva'));
    flashScriptFeedback(panelId, parts.length ? parts.join(' · ') : t('manga.scriptBlockCleared', 'quadro limpo'));

    if (!scriptFirstApplyRef.current) {
      scriptFirstApplyRef.current = true;
      notify.success(
        t('manga.scriptBlockFirstApplyTitle', 'Roteiro aplicado ao quadro'),
        t('manga.scriptBlockFirstApplyHint', 'As falas viram balões na página; a descrição vira o prompt da arte.'),
      );
    }
  };

  // Ctrl/Cmd+Enter inside a manuscript block: applies it (same as blur) and
  // jumps focus straight to the NEXT panel's block — writing in sequence,
  // hands never leave the keyboard. On the last panel, just blurs (done).
  const handleBlockKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, panelIndex: number, panelId: string) => {
    if (!(e.ctrlKey || e.metaKey) || e.key !== 'Enter') return;
    e.preventDefault();
    handlePanelBlockBlur(panelIndex, panelId);
    const nextPanel = current?.layout.panels[panelIndex + 1];
    if (!nextPanel) { e.currentTarget.blur(); return; }
    requestAnimationFrame(() => {
      const el = scriptTextareaRefs.current.get(nextPanel.id);
      if (el) { el.focus(); const len = el.value.length; el.setSelectionRange(len, len); }
    });
  };

  // ── Ghostwriter — completes the CURRENT page's manuscript from whatever the
  // author has already written (empty/partial descriptions, partial/complete
  // dialogues), RESPECTING and PRESERVING their words (server-side heuristic
  // guards against the model over-rewriting — see server/src/routes/manga.ts).
  // Reuses `applyDialoguesBulk`'s reconciliation so untouched/echoed dialogue
  // lines KEEP their bubble position/size/style — exactly like "Gerar falas".
  const runGhostwriter = async () => {
    if (!projectId || !current) return;
    const panelList = current.layout.panels;
    if (!panelList.length) { notify.error(t('common.error', 'Erro'), t('manga.scriptManualNoLayout', 'Escolha um layout primeiro.')); return; }
    // Collect EVERY block's current content — dirty (mid-typing) drafts win,
    // matching exactly what the author sees on screen right now.
    const existingPanels = panelList.map((panel, i) => {
      const draft = scriptDrafts.get(panel.id) ?? serializePanelBlock(current, i);
      const { panels: parsedPanels } = parsePageScript(draft);
      const pp = parsedPanels[0] ?? { description: '', dialogues: [] };
      return {
        description: pp.description,
        dialogues: pp.dialogues.map((d) => ({ speaker: d.speaker, type: d.type, text: d.text })),
      };
    });
    const sceneDescription = existingPanels.map((p) => p.description).filter(Boolean).join('\n').slice(0, 3000);
    setGhostwriterBusy(true);
    try {
      const { panels: resultPanels, newCast } = await api.generateMangaScript(
        projectId, sceneDescription, existingPanels.length, [...scriptCast],
        { mode: 'ghostwriter', existingPanels, pageIndex: pages.findIndex((p) => p.id === currentId) },
      );

      if (newCast?.length) {
        refreshCastSoon();
        notify.success(
          t('manga.newCastTitle', 'Universo atualizado'),
          t('manga.newCastAdded', 'Novo(s) no Universo: {{names}} — a imagem de referência deles está sendo gerada automaticamente.', { names: newCast.map((c) => c.name).join(', ') }),
        );
      }

      snapshot();
      const applyResults = resultPanels.map((rp, i) => ({
        panelIndex: i,
        description: rp.description,
        dialogues: rp.dialogues as ParsedDialogue[],
      }));
      const result = applyDialoguesBulk(applyResults);
      if (result) {
        setScriptDrafts((prev) => { const next = new Map(prev); result.drafts.forEach((v, id) => next.set(id, v)); return next; });
        setScriptDirty(new Set());
        notify.success(t('manga.ghostwriterDoneTitle', 'O ghostwriter completou a página'), t('manga.ghostwriterDoneHint', 'Revise e ajuste como quiser.'));
      }
    } catch (e) {
      notify.error(t('common.error', 'Erro'), e instanceof AiLimitError ? e.message : t('manga.ghostwriterError', 'Falha no ghostwriter. Tente novamente.'));
    } finally { setGhostwriterBusy(false); }
  };

  // ── Conversational Ghostwriter actions (from the AIChat) ────────────────────
  // [ACTION:MANGA_PAGE_SCRIPT] — the assistant wrote the WHOLE page in the
  // screenplay grammar; parse it, reconcile it onto the page (preserving
  // untouched balloons) and re-sync every block's draft — same reactive path
  // as runGhostwriter, but the source is the chat instead of the blocks.
  const applyMangaPageScript = useCallback((scriptText: string) => {
    if (!current) { notify.error(t('common.error', 'Erro'), t('manga.scriptManualNoLayout', 'Escolha um layout primeiro.')); return; }
    const { panels: parsed } = parsePageScript(scriptText);
    if (!parsed.length) return;
    snapshot();
    const { layout } = buildPageScriptLayout(current, parsed, dir, cast);
    patch(() => layout);
    const freshPage: MangaPage = { ...current, layout };
    setPanelScripts((prev) => {
      const next = new Map(prev);
      layout.panels.forEach((p) => { if (p.script) next.set(p.id, { description: p.script.description, castIds: p.script.castIds }); });
      return next;
    });
    setScriptDrafts(new Map(layout.panels.map((p, i) => [p.id, serializePanelBlock(freshPage, i)])));
    setScriptDirty(new Set());
    refreshCastSoon();
    notify.success(t('manga.ghostwriterDoneTitle', 'O ghostwriter completou a página'), t('manga.ghostwriterChatApplied', 'A página foi preenchida pelo chat. Revise e ajuste.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, dir, cast, snapshot, patch, refreshCastSoon, t]);

  // [ACTION:CREATE_CHARACTER] — add a new Universe entry, then refresh the cast
  // chips (its reference image is generated server-side, fire-and-forget).
  const handleCreateCharacterFromChat = useCallback(async (data: { name: string; characterType: string; description: string }) => {
    if (!projectId) return;
    const type = data.characterType === 'location' ? ('location' as const) : ('character' as const);
    await api.createBibleEntry(projectId, { type, name: data.name, content: data.description });
    refreshCastSoon();
    notify.success(t('manga.newCastTitle', 'Universo atualizado'), t('manga.newCastAdded', 'Novo(s) no Universo: {{names}} — a imagem de referência deles está sendo gerada automaticamente.', { names: data.name }));
  }, [projectId, refreshCastSoon, t]);

  // [ACTION:SET_PREMISE] — replace the project's premise, updating the sidebar
  // "Guia da história" reactively.
  const handleSetPremiseFromChat = useCallback(async (nextPremise: string) => {
    if (!projectId) return;
    await api.updateProject(projectId, { description: nextPremise });
    setPremise(nextPremise);
    notify.success(t('manga.premiseSetTitle', 'Premissa atualizada'));
  }, [projectId, t]);

  // ── Drag (panel corners + bubbles) ──────────────────────────────────────────
  const startDrag = (e: React.PointerEvent, d: Omit<Drag, 'rect' | 'startX' | 'startY'>) => {
    e.stopPropagation();
    if (!canvasRef.current) return;
    snapshot(); // one undo step per gesture, not per pointermove
    dragRef.current = { ...d, startX: e.clientX, startY: e.clientY, rect: canvasRef.current.getBoundingClientRect() };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  const onMove = (e: PointerEvent) => {
    const d = dragRef.current; if (!d) return;
    const dx = (e.clientX - d.startX) / d.rect.width;
    const dy = (e.clientY - d.startY) / d.rect.height;
    const mouseX = (e.clientX - d.rect.left) / d.rect.width;
    const mouseY = (e.clientY - d.rect.top) / d.rect.height;
    const cl = (n: number) => Math.max(0, Math.min(1, n));
    if (d.mode === 'corner') {
      const o = d.orig as { x: number; y: number };
      patch((l) => ({ ...l, panels: l.panels.map((p) => p.id === d.id ? { ...p, points: p.points.map((pt, i) => i === d.index ? { x: cl(o.x + dx), y: cl(o.y + dy) } : pt) } : p) }));
    } else if (d.mode === 'panelMove') {
      const o = d.orig as { x: number; y: number }[];
      patch((l) => ({ ...l, panels: l.panels.map((p) => p.id === d.id ? { ...p, points: o.map((pt) => ({ x: cl(pt.x + dx), y: cl(pt.y + dy) })) } : p) }));
    } else if (d.mode === 'artPan') {
      // Pan the art inside its bounding box; dx/dy are canvas fractions, the
      // transform translate is a fraction of the image box → divide by bw/bh.
      const o = d.orig as { ox: number; oy: number; bw: number; bh: number };
      const cf = (n: number) => Math.max(-1, Math.min(1, n));
      patch((l) => ({ ...l, panels: l.panels.map((p) => p.id === d.id
        ? { ...p, offsetX: cf(o.ox + dx / (o.bw || 1)), offsetY: cf(o.oy + dy / (o.bh || 1)) } : p) }));
    } else if (d.mode === 'bubMove' || d.mode === 'bubResize') {
      const o = d.orig as MangaBubble;
      // Magnetic snap: edges/centers grab other bubbles, panel bounding boxes and
      // the page edges/center. Hold Alt to move/resize freely without snapping.
      const snapEnabled = !e.altKey;
      const targets = snapEnabled
        ? collectSnapTargets(current?.layout.bubbles ?? [], current?.layout.panels ?? [], d.id)
        : { xs: [], ys: [] };
      const newGuides: SnapGuideLine[] = [];
      if (d.mode === 'bubMove') {
        let x = o.x + dx, y = o.y + dy;
        if (snapEnabled) {
          const sx = snapMoveAxis(x, o.w, targets.xs, SNAP_THRESHOLD);
          const sy = snapMoveAxis(y, o.h, targets.ys, SNAP_THRESHOLD);
          x = sx.pos; y = sy.pos;
          if (sx.guide !== undefined) newGuides.push({ axis: 'x', pos: sx.guide });
          if (sy.guide !== undefined) newGuides.push({ axis: 'y', pos: sy.guide });
        }
        setGuidesIfChanged(newGuides);
        // The tail tip stays ANCHORED on the page (it points at the character):
        // tail is stored relative to the center, so compensate the box shift.
        const tail = o.tail ? { x: o.tail.x + (o.x - x), y: o.tail.y + (o.y - y) } : o.tail;
        patch((l) => ({ ...l, bubbles: l.bubbles.map((b) => (b.id === d.id ? { ...b, x, y, tail } : b)) }));
      } else {
        // Resize — two families of handle, distinguished by `d.index`:
        // CORNERS (0=TL,1=TR,2=BL,3=BR — design-tool convention): the
        // grabbed corner follows the mouse, the diagonally opposite corner
        // stays perfectly fixed, BOTH axes change. SIDES (4=N,5=S,6=E,7=W):
        // only the ONE axis the handle sits on changes (the other edge/axis
        // stays exactly as it was) — lets a bubble be widened/heightened
        // independently, unlike the corner handles.
        const pageW = current?.width || 1000, pageH = current?.height || 1414;
        const minW = 60 / pageW, minH = 40 / pageH;
        const idx = d.index ?? 3;
        const isSide = idx >= 4;

        let x = o.x, y = o.y, w = o.w, h = o.h;
        if (isSide) {
          if (idx === 4 || idx === 5) {
            // N (4) / S (5) — height only, x/w untouched.
            const grabTop = idx === 4;
            const fixedY = grabTop ? o.y + o.h : o.y;
            const my = cl(grabTop ? Math.min(mouseY, fixedY - minH) : Math.max(mouseY, fixedY + minH));
            let hh = Math.abs(fixedY - my);
            if (snapEnabled) {
              const sh = snapResizeAxis(fixedY, hh, targets.ys, SNAP_THRESHOLD, !grabTop);
              hh = Math.max(minH, sh.size);
              if (sh.guide !== undefined) newGuides.push({ axis: 'y', pos: sh.guide });
            }
            y = grabTop ? fixedY - hh : fixedY;
            h = hh;
          } else {
            // E (6) / W (7) — width only, y/h untouched.
            const grabLeft = idx === 7;
            const fixedX = grabLeft ? o.x + o.w : o.x;
            const mx = cl(grabLeft ? Math.min(mouseX, fixedX - minW) : Math.max(mouseX, fixedX + minW));
            let ww = Math.abs(fixedX - mx);
            if (snapEnabled) {
              const sw = snapResizeAxis(fixedX, ww, targets.xs, SNAP_THRESHOLD, !grabLeft);
              ww = Math.max(minW, sw.size);
              if (sw.guide !== undefined) newGuides.push({ axis: 'x', pos: sw.guide });
            }
            x = grabLeft ? fixedX - ww : fixedX;
            w = ww;
          }
        } else {
          const grabLeft = idx === 0 || idx === 2; // TL/BL grabbed → the LEFT edge moves, right stays fixed
          const grabTop = idx === 0 || idx === 1; // TL/TR grabbed → the TOP edge moves, bottom stays fixed
          const fixedX = grabLeft ? o.x + o.w : o.x; // the OPPOSITE corner's x — never moves
          const fixedY = grabTop ? o.y + o.h : o.y; // the OPPOSITE corner's y — never moves

          const mx = cl(grabLeft ? Math.min(mouseX, fixedX - minW) : Math.max(mouseX, fixedX + minW));
          const my = cl(grabTop ? Math.min(mouseY, fixedY - minH) : Math.max(mouseY, fixedY + minH));
          let ww = Math.abs(fixedX - mx);
          let hh = Math.abs(fixedY - my);
          if (snapEnabled) {
            const sw = snapResizeAxis(fixedX, ww, targets.xs, SNAP_THRESHOLD, !grabLeft);
            const sh = snapResizeAxis(fixedY, hh, targets.ys, SNAP_THRESHOLD, !grabTop);
            ww = Math.max(minW, sw.size); hh = Math.max(minH, sh.size);
            if (sw.guide !== undefined) newGuides.push({ axis: 'x', pos: sw.guide });
            if (sh.guide !== undefined) newGuides.push({ axis: 'y', pos: sh.guide });
          }
          x = grabLeft ? fixedX - ww : fixedX;
          y = grabTop ? fixedY - hh : fixedY;
          w = ww; h = hh;
        }
        setGuidesIfChanged(newGuides);
        // The tail tip stays ANCHORED on the page during resize too (same
        // principle as bubMove above): tail is stored relative to the
        // center, so re-derive it against the NEW center from its unchanged
        // absolute position.
        const oldCx = o.x + o.w / 2, oldCy = o.y + o.h / 2;
        const newCx = x + w / 2, newCy = y + h / 2;
        const tail = o.tail ? { x: oldCx + o.tail.x - newCx, y: oldCy + o.tail.y - newCy } : o.tail;
        patch((l) => ({ ...l, bubbles: l.bubbles.map((b) => (b.id === d.id ? { ...b, x, y, w, h, tail } : b)) }));
      }
    } else if (d.mode === 'bubRotate') {
      // Rotate handle drag: angle = atan2(mouse - center) - atan2(dragStart -
      // center), added to the rotation the bubble already had — works in
      // SCREEN px (not normalized page fractions) so the angle is correct
      // regardless of the page's aspect ratio; the canvas element keeps the
      // page's own aspect ratio, so screen-space and page-space angles agree.
      const o = d.orig as MangaBubble;
      const cxScreen = d.rect.left + (o.x + o.w / 2) * d.rect.width;
      const cyScreen = d.rect.top + (o.y + o.h / 2) * d.rect.height;
      const startAngle = Math.atan2(d.startY - cyScreen, d.startX - cxScreen);
      const curAngle = Math.atan2(e.clientY - cyScreen, e.clientX - cxScreen);
      let rotation = normalizeDeg((o.rotation ?? 0) + ((curAngle - startAngle) * 180) / Math.PI);
      if (!e.altKey) {
        // Snap to every 15° (covers 0/±15/±30/±45/…) within 3° — Alt disables it.
        const nearest = Math.round(rotation / 15) * 15;
        if (Math.abs(rotation - nearest) < 3) rotation = nearest;
      }
      patch((l) => ({ ...l, bubbles: l.bubbles.map((b) => (b.id === d.id ? { ...b, rotation } : b)) }));
    } else {
      // bubTail — the tip follows the mouse ABSOLUTELY (not by delta), then
      // is stored relative to the (fixed, original) box center.
      const o = d.orig as MangaBubble;
      const cx = o.x + o.w / 2, cy = o.y + o.h / 2;
      patch((l) => ({ ...l, bubbles: l.bubbles.map((b) => (b.id === d.id
        ? { ...b, tail: { x: mouseX - cx, y: mouseY - cy } } : b)) }));
    }
  };
  const onUp = () => {
    dragRef.current = null;
    setGuidesIfChanged([]);
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
  };

  // ── Pages ───────────────────────────────────────────────────────────────────
  const addPage = async () => {
    if (!projectId) return;
    setBusy('page');
    try {
      // New pages inherit the current page's size (the project's page format).
      const { page } = await api.createMangaPage(projectId, current
        ? { width: current.width, height: current.height } : undefined);
      setPages((p) => [...p, page]); setCurrentId(page.id);
    } catch { notify.error(t('common.error', 'Erro'), t('manga.pageFail', 'Falha ao criar página.')); }
    finally { setBusy(null); }
  };
  const removePage = async (id: string) => {
    await api.deleteMangaPage(id).catch(() => {});
    setPages((prev) => { const next = prev.filter((p) => p.id !== id); if (currentId === id) setCurrentId(next[0]?.id ?? null); return next; });
  };
  // Duplicate the current page — new blank page inheriting size, then its layout
  // is replaced with a deep clone (fresh panel/bubble ids) of the current one.
  const duplicatePage = useCallback(async () => {
    if (!projectId || !current) return;
    setBusy('page');
    try {
      const { page } = await api.createMangaPage(projectId, { width: current.width, height: current.height });
      const clonedLayout: MangaLayout = {
        ...current.layout,
        panels: current.layout.panels.map((p) => ({ ...p, id: uid() })),
        bubbles: current.layout.bubbles.map((b) => ({ ...b, id: uid() })),
      };
      await api.updateMangaPage(page.id, { layout: clonedLayout });
      setPages((prev) => [...prev, { ...page, layout: clonedLayout }]);
      setCurrentId(page.id);
    } catch { notify.error(t('common.error', 'Erro'), t('manga.pageFail', 'Falha ao criar página.')); }
    finally { setBusy(null); }
  }, [projectId, current, t]);
  // Move to the previous/next page in reading order (page-number order).
  const goToPageDelta = useCallback((delta: 1 | -1) => {
    const idx = pages.findIndex((p) => p.id === currentId);
    if (idx === -1) return;
    const ni = idx + delta;
    if (ni < 0 || ni >= pages.length) return;
    setCurrentId(pages[ni].id);
    setSelected(null);
  }, [pages, currentId]);
  // Nudge the selected bubble by N screen pixels (converted to page fractions),
  // clamped to the page. One undo step per press. Shift multiplies to 10px.
  const nudgeSelectedBubble = useCallback((dxPx: number, dyPx: number) => {
    if (!selected || selected.kind !== 'bubble') return;
    const w = current?.width || 1000, h = current?.height || 1414;
    const dx = dxPx / w, dy = dyPx / h;
    snapshot();
    patch((l) => ({
      ...l,
      bubbles: l.bubbles.map((b) => (b.id === selected.id
        ? { ...b, x: Math.max(0, Math.min(1, b.x + dx)), y: Math.max(0, Math.min(1, b.y + dy)) }
        : b)),
    }));
  }, [selected, current, snapshot, patch]);

  // ── "Inverter direção de leitura" — mirrors mangá→HQ (or back), horizontally
  // flipping EVERY page's layout, art and bubble positions (never the text
  // itself). Applying it a second time undoes it. Persists page-by-page like
  // `transformAllSketches` (most target pages aren't the currently-open one,
  // so there's no undo/autosave plumbing for them) but only snapshots the
  // CURRENT page's own undo stack (Ctrl+Z), matching the spec.
  const mirrorAllPages = async () => {
    if (mirrorBusy || pages.length === 0) return;
    const ok = window.confirm(
      t('manga.mirrorConfirm', 'Espelha TODAS as páginas: layout, artes e posições dos balões. Aplicar de novo desfaz.')
    );
    if (!ok) return;
    snapshot(); // undo (Ctrl+Z) for the currently-open page only
    setMirrorBusy(true);
    setSaveStatus('saving');
    try {
      const mirrored = pages.map((pg) => mirrorPageHorizontally(pg));
      setPages(mirrored);
      await Promise.all(mirrored.map((pg) => api.updateMangaPage(pg.id, { layout: pg.layout })));
      setSaveStatus('saved');
      notify.success(t('manga.mirrorDone', 'Direção de leitura invertida!'));
    } catch {
      setSaveStatus('unsaved');
      notify.error(t('common.error', 'Erro'), t('manga.mirrorError', 'Falha ao inverter a direção de leitura.'));
    } finally {
      setMirrorBusy(false);
    }
  };

  // ── Export — PNG (current page / all pages) and a single multi-page PDF ────
  const exportBusy = exportProgress !== null;
  const pageFileName = (id: string) => {
    const idx = pages.findIndex((p) => p.id === id);
    return `pagina-${String(idx + 1).padStart(2, '0')}.png`;
  };
  const runExport = async (kind: 'pagePng' | 'allPng' | 'pdf' | 'html') => {
    setExportOpen(false);
    if (kind === 'pagePng' && !current) return;
    if (kind !== 'pagePng' && pages.length === 0) return;
    setExportProgress(t('manga.exporting', 'Exportando…'));
    try {
      if (kind === 'pagePng' && current) {
        await exportPagePng(current, pageFileName(current.id));
      } else if (kind === 'allPng') {
        await exportAllPngs(pages, 'pagina', (done, total) =>
          setExportProgress(t('manga.exportingProgress', 'Exportando {{n}}/{{m}}…', { n: done, m: total })));
      } else if (kind === 'pdf') {
        await exportPdf(pages, projectTitle || 'manga', (done, total) =>
          setExportProgress(t('manga.exportingProgress', 'Exportando {{n}}/{{m}}…', { n: done, m: total })));
      } else if (kind === 'html') {
        await exportHtml(pages, projectTitle || 'manga', dir, (done, total) =>
          setExportProgress(t('manga.exportingProgress', 'Exportando {{n}}/{{m}}…', { n: done, m: total })));
      }
      notify.success(t('manga.exportDone', 'Exportação concluída!'));
    } catch (e) {
      notify.error(t('common.error', 'Erro'), e instanceof Error ? e.message : t('manga.exportError', 'Falha ao exportar.'));
    } finally {
      setExportProgress(null);
    }
  };

  // Change the project's reading MODE (manga RTL vs comic/HQ LTR). This only
  // changes how the work is READ (panel order in newly-generated layouts, the
  // reader's advance side, page-flip spine, export runtime) — it NEVER touches
  // existing artwork/panels. Use "Inverter direção" to physically mirror them.
  const changeReadingDirection = async (next: 'rtl' | 'ltr') => {
    setDirMenuOpen(false);
    if (next === dir || !projectId) return;
    const prev = dir;
    setDir(next);
    try {
      await api.updateProject(projectId, { readingDirection: next });
      notify.success(t('manga.dirChanged', 'Direção de leitura atualizada.'));
    } catch {
      setDir(prev);
      notify.error(t('common.error', 'Erro'), t('manga.dirChangeFailed', 'Não foi possível atualizar a direção.'));
    }
  };

  const orderedForStrip = dir === 'rtl' ? [...pages].reverse() : pages;

  // ── Keyboard shortcuts — one declarative registry drives BOTH the behaviour
  // (via useEditorShortcuts) and the help overlay (published to the store). The
  // engine auto-guards single-key shortcuts against typing/modals, so the guards
  // below only encode editor context (a page must exist, the reader must be
  // closed, a bubble must be selected…). ──────────────────────────────────────
  const onCanvas = () => readerIdx === null && !!current; // page open, not reading
  const hasBubble = () => readerIdx === null && selected?.kind === 'bubble';
  const shortcuts: EditorShortcut[] = [
    // Creation — drop a ready-made bubble of each type at the page center.
    { id: 'mg-speech', combo: 'b', group: 'create', when: onCanvas, description: t('shortcuts.mgSpeech', 'Balão de fala'), handler: () => addBubble('speech') },
    { id: 'mg-thought', combo: 't', group: 'create', when: onCanvas, description: t('shortcuts.mgThought', 'Balão de pensamento'), handler: () => addBubble('thought') },
    { id: 'mg-shout', combo: 'g', group: 'create', when: onCanvas, description: t('shortcuts.mgShout', 'Balão de grito'), handler: () => addBubble('shout') },
    { id: 'mg-whisper', combo: 's', group: 'create', when: onCanvas, description: t('shortcuts.mgWhisper', 'Balão de sussurro'), handler: () => addBubble('whisper') },
    { id: 'mg-narration', combo: 'n', group: 'create', when: onCanvas, description: t('shortcuts.mgNarration', 'Caixa de narração'), handler: () => addBubble('narration') },
    { id: 'mg-caption', combo: 'l', group: 'create', when: onCanvas, description: t('shortcuts.mgCaption', 'Legenda'), handler: () => addBubble('caption') },
    { id: 'mg-sfx', combo: 'x', group: 'create', when: onCanvas, description: t('shortcuts.mgSfx', 'Efeito sonoro (SFX)'), handler: () => addBubble('sfx') },
    { id: 'mg-page', combo: 'p', group: 'create', when: () => readerIdx === null, description: t('shortcuts.mgNewPage', 'Nova página'), handler: () => { void addPage(); } },
    { id: 'mg-dup-page', combo: 'mod+d', group: 'create', when: () => readerIdx === null && !!current, description: t('shortcuts.mgDupPage', 'Duplicar página'), handler: () => { void duplicatePage(); } },
    // Edit — act on the current selection / page.
    { id: 'mg-twin', combo: 'd', group: 'edit', when: hasBubble, description: t('shortcuts.mgTwin', 'Duplicar balão (gêmeo colado)'), handler: () => duplicateTwin() },
    { id: 'mg-delete', combo: ['delete', 'backspace'], group: 'edit', when: () => readerIdx === null && !!selected, description: t('shortcuts.mgDelete', 'Excluir selecionado'), handler: () => deleteSelected() },
    // Arrow nudges — base = 1px, Shift = 10px. Only the first is shown in help
    // (as the representative "Setas"); the rest carry a '__hidden' description so
    // `mangaHelpSections` drops them from the overlay while keeping them active.
    { id: 'mg-nudge-left', combo: 'left', group: 'edit', when: hasBubble, description: t('shortcuts.mgNudge', 'Mover balão · Setas (Shift = 10px)'), handler: () => nudgeSelectedBubble(-1, 0) },
    { id: 'mg-nudge-right', combo: 'right', group: 'edit', when: hasBubble, description: '__hidden', handler: () => nudgeSelectedBubble(1, 0) },
    { id: 'mg-nudge-up', combo: 'up', group: 'edit', when: hasBubble, description: '__hidden', handler: () => nudgeSelectedBubble(0, -1) },
    { id: 'mg-nudge-down', combo: 'down', group: 'edit', when: hasBubble, description: '__hidden', handler: () => nudgeSelectedBubble(0, 1) },
    { id: 'mg-nudge-sleft', combo: 'shift+left', group: 'edit', when: hasBubble, description: '__hidden', handler: () => nudgeSelectedBubble(-10, 0) },
    { id: 'mg-nudge-sright', combo: 'shift+right', group: 'edit', when: hasBubble, description: '__hidden', handler: () => nudgeSelectedBubble(10, 0) },
    { id: 'mg-nudge-sup', combo: 'shift+up', group: 'edit', when: hasBubble, description: '__hidden', handler: () => nudgeSelectedBubble(0, -10) },
    { id: 'mg-nudge-sdown', combo: 'shift+down', group: 'edit', when: hasBubble, description: '__hidden', handler: () => nudgeSelectedBubble(0, 10) },
    { id: 'mg-undo', combo: 'mod+z', group: 'edit', blockInInput: true, when: () => readerIdx === null, description: t('shortcuts.mgUndo', 'Desfazer'), handler: (e) => timeTravel(e.shiftKey ? 'redo' : 'undo') },
    { id: 'mg-redo', combo: ['mod+shift+z', 'mod+y'], group: 'edit', blockInInput: true, when: () => readerIdx === null, description: t('shortcuts.mgRedo', 'Refazer'), handler: () => timeTravel('redo') },
    // Navigate — pages, panels, layout, script, ghostwriter, export.
    { id: 'mg-prev-page', combo: '[', group: 'navigate', when: () => readerIdx === null, description: t('shortcuts.mgPrevPage', 'Página anterior'), handler: () => goToPageDelta(-1) },
    { id: 'mg-next-page', combo: ']', group: 'navigate', when: () => readerIdx === null, description: t('shortcuts.mgNextPage', 'Próxima página'), handler: () => goToPageDelta(1) },
    { id: 'mg-script', combo: 'r', group: 'navigate', when: () => readerIdx === null, description: t('shortcuts.mgScript', 'Mostrar/ocultar roteiro'), handler: () => (window.matchMedia('(min-width: 768px)').matches ? setScriptDesktop((o) => !o) : setScriptOpen(true)) },
    { id: 'mg-layout', combo: 'mod+l', group: 'navigate', when: () => readerIdx === null && (current?.layout.panels.length ?? 0) > 0, description: t('shortcuts.mgLayout', 'Embaralhar layout'), handler: () => { const n = current?.layout.panels.length ?? 0; if (n > 0) genLayout(n); } },
    { id: 'mg-ghost', combo: 'mod+shift+g', group: 'navigate', when: () => readerIdx === null && !!current, description: t('shortcuts.mgGhost', 'Ghostwriter (abrir chat)'), handler: () => { setChatOpen((v) => !v); } },
    { id: 'mg-export', combo: 'mod+e', group: 'navigate', when: () => readerIdx === null && pages.length > 0, description: t('shortcuts.mgExport', 'Exportar'), handler: () => { setExportOpen(true); setTplOpen(false); setInsertOpen(false); setStyleOpen(false); } },
    // Help — open the shortcuts overlay (works even over the modal itself).
    { id: 'mg-help', combo: '?', group: 'help', allowInModal: true, description: t('shortcuts.showHelp', 'Mostrar esta ajuda'), handler: () => window.dispatchEvent(new CustomEvent('reformu:shortcuts-open')) },
  ];
  useEditorShortcuts(shortcuts);
  // Publish the grouped registry to the app-level help overlay. Hidden rows
  // (the extra arrow nudges) are filtered out so the overlay stays readable.
  const setShortcutsContext = useShortcutsStore((s) => s.setContext);
  const clearShortcutsContext = useShortcutsStore((s) => s.clearContext);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mangaHelpSections = useMemo(() => groupShortcuts(shortcuts.filter((s) => s.description !== '__hidden')), [t]);
  useEffect(() => {
    setShortcutsContext(mangaHelpSections, t('shortcuts.mangaTitle', 'Atalhos — Editor de mangá'));
    return () => clearShortcutsContext();
  }, [mangaHelpSections, setShortcutsContext, clearShortcutsContext, t]);

  if (view === 'universo') return <div className="h-full overflow-hidden bg-background"><StoryBibleView projectId={projectId ?? ''} /></div>;
  if (view === 'estrutura') return <div className="h-full overflow-hidden bg-background"><OutlineBoardView projectId={projectId ?? ''} /></div>;
  if (loading) return <div className="flex h-full items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  const panels = current?.layout.panels ?? [];
  const bubbles = current?.layout.bubbles ?? [];
  // Page pixel dimensions — the bubble SVG's viewBox, and the coordinate
  // space `toPathBubble`/`generateBubblePath` draw in (never a stretched
  // per-bubble box, so nothing distorts on resize).
  const pageW = current?.width || 1000;
  const pageH = current?.height || 1414;
  // "Balloon chain" groups (see `groupJoinedBubbles`) — either fully fused
  // (overlapping bboxes) or bridged by a connector "neck" (merely close,
  // see `connectorPathsForGroup`) — recomputed every render off the current
  // bubble list, cheap enough at manga-page scale.
  const bubbleGroups = groupJoinedBubbles(bubbles, pageW, pageH);
  // "Only ONE visible tail per connected group" (see `computeTailOmissions`)
  // — always the highest bubble (smallest box `y`); every other tail-bearing
  // member of the group renders tail-less. Purely a function of the CURRENT
  // bubbles/groups, recomputed fresh every render — selecting/dragging a
  // suppressed bubble can never "revive" its tail.
  const tailOmitIds = computeTailOmissions(bubbles, bubbleGroups);
  // Multi-member groups' connector bridges + the shared style they paint
  // with (every member of a group shares the same type/effective fill) —
  // computed once per render, reused by both the stroke pass and the
  // fill-only pass below (same 2-pass fusion technique, now covering bridges too).
  const fusedGroups = bubbleGroups
    .filter((g) => g.length > 1)
    .map((g) => {
      const members = g.map((idx) => bubbles[idx]);
      return { g, paths: connectorPathsForGroup(members, pageW, pageH), style: resolveBubbleStyle(members[0]) };
    });
  const selPanel = selected?.kind === 'panel' ? panels.find((p) => p.id === selected.id) : null;
  const selBubble = selected?.kind === 'bubble' ? bubbles.find((b) => b.id === selected.id) : null;
  const genModalPanel = genModal ? panels.find((p) => p.id === genModal.panelId) : null;
  // At least one panel has a description to work with — gates the bulk
  // "Gerar falas" action (nothing to voice without a scene to voice it in).
  const anyPanelHasDescription = panels.some((p) => getPanelDescription(p).length > 0);
  const anyPanelHasScript = panels.some((p) => p.script ?? panelScripts.get(p.id));

  // Conversational Ghostwriter context — the working page serialized as a
  // script + position + cast + premise, plus the reactive apply callbacks.
  const mangaChatMode: MangaChatMode | undefined = current ? {
    pageScript: serializePageScript(current),
    pageNumber: Math.max(1, pages.findIndex((p) => p.id === currentId) + 1),
    totalPages: pages.length,
    castNames: cast.map((c) => `${c.name}${c.type ? ` (${c.type})` : ''}`),
    premise,
    onApplyPageScript: applyMangaPageScript,
    onCreateCharacter: handleCreateCharacterFromChat,
    onSetPremise: handleSetPremiseFromChat,
  } : undefined;

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Editor topbar — SAME anatomy as the book editor: title + breadcrumb on
          the left, grouped ghost-icon tools in a pill on the right. The save
          indicator lives in the bottom status bar (like the book), not here. */}
      <header className="relative flex-shrink-0 border-b border-border/60 bg-background">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 px-3 py-2 md:h-14 md:flex-nowrap md:gap-3 md:px-4 md:py-0">
        {/* Left — book/manga title + page breadcrumb (mirrors the book header) */}
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="truncate max-w-[160px] font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground" title={projectTitle}>
            {projectTitle || t('projects.untitled', 'Sem título')}
          </span>
          {current && (
            <span className="hidden shrink-0 items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground/70 sm:flex">
              <span aria-hidden className="text-border">·</span>
              {t('manga.breadcrumbPage', 'Página {{n}}/{{total}}', { n: Math.max(1, pages.findIndex((p) => p.id === currentId) + 1), total: pages.length })}
              {/* Reading-direction selector — changes the reading MODE (manga
                  RTL vs comic/HQ LTR); existing art is never altered. */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => { setDirMenuOpen((o) => !o); setTplOpen(false); setExportOpen(false); setStyleOpen(false); setInsertOpen(false); }}
                  title={t('manga.dirSelectTitle', 'Direção de leitura')}
                  aria-haspopup="menu"
                  aria-expanded={dirMenuOpen}
                  className="flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[9px] tracking-[0.14em] text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground">
                  {dir === 'rtl' ? t('manga.rtlShort', 'RTL ←') : t('manga.ltrShort', 'LTR →')}
                  <ChevronDown size={10} className={cn('transition-transform', dirMenuOpen && 'rotate-180')} />
                </button>
                {dirMenuOpen && (
                  <div
                    className="absolute left-0 top-full z-40 mt-1.5 w-[min(320px,92vw)] rounded-xl border border-border bg-popover p-2 shadow-xl normal-case tracking-normal"
                    onPointerDown={(e) => e.stopPropagation()}>
                    {([
                      { value: 'rtl' as const, title: t('manga.dirMangaTitle', 'Mangá'), sub: t('manga.dirMangaSub', 'Direita → Esquerda') },
                      { value: 'ltr' as const, title: t('manga.dirComicTitle', 'HQ / Comic'), sub: t('manga.dirComicSub', 'Esquerda → Direita') },
                    ]).map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        role="menuitemradio"
                        aria-checked={dir === opt.value}
                        onClick={() => changeReadingDirection(opt.value)}
                        className={cn(
                          'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
                          dir === opt.value ? 'bg-primary/10' : 'hover:bg-muted',
                        )}>
                        <DirectionGlyph rtl={opt.value === 'rtl'} active={dir === opt.value} />
                        <span className="min-w-0 flex-1">
                          <span className="block text-xs font-medium text-foreground">{opt.title}</span>
                          <span className="block font-mono text-[10px] text-muted-foreground">{opt.sub}</span>
                        </span>
                        {dir === opt.value && <Check size={14} className="shrink-0 text-primary" />}
                      </button>
                    ))}
                    <p className="mt-1 px-2.5 pb-1 pt-1.5 text-[10px] leading-snug text-muted-foreground">
                      {t('manga.dirWarning', 'Muda a ordem de leitura. O conteúdo existente não é alterado — use "Inverter direção" para espelhar o desenho.')}
                    </p>
                  </div>
                )}
              </div>
            </span>
          )}
        </div>

        <div className="flex-1" />

        {/* Right — grouped ghost-icon tools in a pill, exactly like the book header */}
        <div className="flex items-center gap-0.5 rounded-xl border border-border/50 bg-muted/50 p-1">
        <button onClick={() => { setTplOpen((o) => !o); setExportOpen(false); setStyleOpen(false); setInsertOpen(false); }} disabled={!current}
          title={t('manga.layout', 'Layout')} aria-label={t('manga.layout', 'Layout')}
          className={cn(GHOST_BTN, tplOpen && GHOST_ACTIVE)}>
          <LayoutGrid size={17} />
        </button>
        {current && panels.length > 0 && (
          <button onClick={() => genLayout(panels.length)} title={t('manga.shuffleKbd', 'Embaralhar layout (Ctrl+L)')} aria-label={t('manga.shuffle', 'Embaralhar')}
            className={GHOST_BTN}>
            <Shuffle size={17} />
          </button>
        )}
        {pages.length > 0 && (
          <button onClick={() => setReaderIdx(Math.max(0, pages.findIndex((p) => p.id === currentId)))}
            title={t('manga.read', 'Ler (pré-visualização)')} aria-label={t('manga.read', 'Ler')}
            className={GHOST_BTN}>
            <BookOpen size={17} />
          </button>
        )}
        {/* Export — PNG (this page / all pages) or a single multi-page PDF. */}
        {pages.length > 0 && (
          <div className="relative">
            <button onClick={() => { setExportOpen((o) => !o); setTplOpen(false); setStyleOpen(false); setInsertOpen(false); }} disabled={exportBusy}
              title={t('manga.exportKbd', 'Exportar (Ctrl+E)')} aria-label={t('manga.export', 'Exportar')}
              className={cn(GHOST_BTN, exportOpen && GHOST_ACTIVE)}>
              {exportBusy ? <Loader2 size={17} className="animate-spin" /> : <Download size={17} />}
            </button>
            {exportOpen && (
              <div className="absolute left-0 top-full z-30 mt-1 w-56 rounded-xl border border-border bg-popover p-1.5 shadow-xl" onPointerDown={(e) => e.stopPropagation()}>
                <button onClick={() => runExport('pagePng')} disabled={!current || exportBusy}
                  className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs text-foreground hover:bg-muted disabled:opacity-40">
                  <Download size={13} />{t('manga.exportPagePng', 'PNG desta página')}
                </button>
                <button onClick={() => runExport('allPng')} disabled={exportBusy}
                  className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs text-foreground hover:bg-muted disabled:opacity-40">
                  <Download size={13} />{t('manga.exportAllPng', 'PNG de todas as páginas')}
                </button>
                <button onClick={() => runExport('pdf')} disabled={exportBusy}
                  className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs text-foreground hover:bg-muted disabled:opacity-40">
                  <Download size={13} />{t('manga.exportPdf', 'PDF do mangá')}
                </button>
                <button onClick={() => runExport('html')} disabled={exportBusy}
                  className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs text-foreground hover:bg-muted disabled:opacity-40">
                  <Download size={13} />{t('manga.exportHtml', 'HTML interativo (animado)')}
                </button>
              </div>
            )}
          </div>
        )}
        {/* Inverter direção de leitura — mirrors ALL pages (layout + art + bubbles). */}
        {pages.length > 0 && (
          <button onClick={mirrorAllPages} disabled={mirrorBusy}
            title={t('manga.mirrorPages', 'Inverter direção de leitura')} aria-label={t('manga.mirrorPages', 'Inverter direção de leitura')}
            className={GHOST_BTN}>
            {mirrorBusy ? <Loader2 size={17} className="animate-spin" /> : <FlipHorizontal2 size={17} />}
          </button>
        )}
        <ToolSep />
        {/* Insert bubble — card picker (visual/structure ported from the
            bubble-creator reference), dropped ready-made at the page center. */}
        <div className="relative">
          <button onClick={() => { setInsertOpen((o) => !o); setTplOpen(false); setExportOpen(false); setStyleOpen(false); }} disabled={!current}
            title={t('manga.insertBubbleKbd', 'Balão + (B fala · T pensamento · G grito · S sussurro · N narração · L legenda · X SFX)')} aria-label={t('manga.insertBubble', 'Balão +')}
            className={cn(GHOST_BTN, insertOpen && GHOST_ACTIVE)}>
            <MessageCircle size={17} />
          </button>
          {insertOpen && (
            <div className="absolute left-0 top-full z-30 mt-1 max-h-[80vh] w-[min(320px,92vw)] overflow-y-auto rounded-xl border border-border bg-popover p-3 shadow-xl" onPointerDown={(e) => e.stopPropagation()}>
              <p className="mb-2 flex items-center text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                <Plus size={13} className="mr-1 text-primary" />{t('manga.insertTitle', 'Inserir Balões')}
              </p>
              <div className="grid grid-cols-1 gap-2">
                <button onClick={() => { addBubble('speech'); setInsertOpen(false); }} title={t('shortcuts.mgSpeechKbd', 'Balão de fala (B)')}
                  className="group flex items-center justify-between rounded-2xl border border-border bg-card p-3 text-left transition hover:border-primary/50 hover:bg-muted/60">
                  <div className="flex items-center gap-3">
                    <span className="relative inline-block h-5 w-7 shrink-0 overflow-hidden rounded-full border border-border bg-white">
                      <span className="absolute bottom-0.5 right-1 block h-1.5 w-1.5 rotate-45 border-b border-r border-border bg-white" />
                    </span>
                    <div>
                      <p className="text-xs font-semibold text-foreground">{t('manga.cardSpeechTitle', 'Diálogo Clássico')}</p>
                      <p className="text-[10px] text-muted-foreground">{t('manga.cardSpeechDesc', 'Oval com cauda curvilínea')}</p>
                    </div>
                  </div>
                  <Plus size={15} className="shrink-0 text-muted-foreground transition group-hover:text-primary" />
                </button>

                <button onClick={() => { addBubble('shout'); setInsertOpen(false); }} title={t('shortcuts.mgShoutKbd', 'Balão de grito (G)')}
                  className="group flex items-center justify-between rounded-2xl border border-border bg-card p-3 text-left transition hover:border-primary/50 hover:bg-muted/60">
                  <div className="flex items-center gap-3">
                    <span className="inline-block h-5 w-7 shrink-0 border-2 border-border bg-white" style={{ clipPath: 'polygon(50% 0%, 80% 20%, 100% 0%, 90% 50%, 100% 100%, 70% 80%, 50% 100%, 20% 80%, 0% 100%, 10% 50%, 0% 0%, 35% 25%)' }} />
                    <div>
                      <p className="text-xs font-semibold text-foreground">{t('manga.cardShoutTitle', 'Grito Shonen (Spiky)')}</p>
                      <p className="text-[10px] text-muted-foreground">{t('manga.cardShoutDesc', 'Explosão pontiaguda de ação')}</p>
                    </div>
                  </div>
                  <Plus size={15} className="shrink-0 text-muted-foreground transition group-hover:text-primary" />
                </button>

                <button onClick={() => { addBubble('thought'); setInsertOpen(false); }} title={t('shortcuts.mgThoughtKbd', 'Balão de pensamento (T)')}
                  className="group flex items-center justify-between rounded-2xl border border-border bg-card p-3 text-left transition hover:border-primary/50 hover:bg-muted/60">
                  <div className="flex items-center gap-3">
                    <span className="relative inline-block h-5 w-7 shrink-0 border border-border bg-white" style={{ borderRadius: '10px / 6px' }}>
                      <span className="absolute -bottom-1 -left-1 h-1 w-1 rounded-full bg-border" />
                      <span className="absolute -bottom-2 -left-2 h-0.5 w-0.5 rounded-full bg-border" />
                    </span>
                    <div>
                      <p className="text-xs font-semibold text-foreground">{t('manga.cardThoughtTitle', 'Pensamento (Nuvem)')}</p>
                      <p className="text-[10px] text-muted-foreground">{t('manga.cardThoughtDesc', 'Bordas macias e bolhas')}</p>
                    </div>
                  </div>
                  <Plus size={15} className="shrink-0 text-muted-foreground transition group-hover:text-primary" />
                </button>

                <button onClick={() => { addBubble('whisper'); setInsertOpen(false); }} title={t('shortcuts.mgWhisperKbd', 'Balão de sussurro (S)')}
                  className="group flex items-center justify-between rounded-2xl border border-border bg-card p-3 text-left transition hover:border-primary/50 hover:bg-muted/60">
                  <div className="flex items-center gap-3">
                    <span className="inline-block h-5 w-7 shrink-0 rounded-full border-2 border-dashed border-border bg-white" />
                    <div>
                      <p className="text-xs font-semibold text-foreground">{t('manga.cardWhisperTitle', 'Sussurro / Segredo')}</p>
                      <p className="text-[10px] text-muted-foreground">{t('manga.cardWhisperDesc', 'Bordas pontilhadas suaves')}</p>
                    </div>
                  </div>
                  <Plus size={15} className="shrink-0 text-muted-foreground transition group-hover:text-primary" />
                </button>

                <button onClick={() => { addBubble('narration'); setInsertOpen(false); }} title={t('shortcuts.mgNarrationKbd', 'Caixa de narração (N)')}
                  className="group flex items-center justify-between rounded-2xl border border-border bg-card p-3 text-left transition hover:border-primary/50 hover:bg-muted/60">
                  <div className="flex items-center gap-3">
                    <span className="inline-block h-4 w-7 shrink-0 border-2 border-border bg-white" />
                    <div>
                      <p className="text-xs font-semibold text-foreground">{t('manga.cardNarrationTitle', 'Narrador (Retângulo)')}</p>
                      <p className="text-[10px] text-muted-foreground">{t('manga.cardNarrationDesc', 'Bloco sólido para descrição')}</p>
                    </div>
                  </div>
                  <Plus size={15} className="shrink-0 text-muted-foreground transition group-hover:text-primary" />
                </button>

                <button onClick={() => { addBubble('caption'); setInsertOpen(false); }} title={t('shortcuts.mgCaptionKbd', 'Legenda (L)')}
                  className="group flex items-center justify-between rounded-2xl border border-border bg-card p-3 text-left transition hover:border-primary/50 hover:bg-muted/60">
                  <div className="flex items-center gap-3">
                    <span className="inline-block h-4 w-7 shrink-0 border-2 border-border bg-foreground/80" />
                    <div>
                      <p className="text-xs font-semibold text-foreground">{t('manga.cardCaptionTitle', 'Legenda')}</p>
                      <p className="text-[10px] text-muted-foreground">{t('manga.cardCaptionDesc', 'Rótulo compacto de cena')}</p>
                    </div>
                  </div>
                  <Plus size={15} className="shrink-0 text-muted-foreground transition group-hover:text-primary" />
                </button>

                <button onClick={() => { addBubble('sfx'); setInsertOpen(false); }} title={t('shortcuts.mgSfxKbd', 'SFX / Efeito (X)')}
                  className="group flex items-center justify-between rounded-2xl border border-border bg-card p-3 text-left transition hover:border-primary/50 hover:bg-muted/60">
                  <div className="flex items-center gap-3">
                    <span className="inline-block shrink-0 -rotate-6 text-base font-black italic tracking-tight text-foreground" style={{ WebkitTextStroke: '1px var(--border)', fontFamily: "'Bangers', cursive" }}>SFX</span>
                    <div>
                      <p className="text-xs font-semibold text-foreground">{t('manga.cardSfxTitle', 'SFX / Efeito')}</p>
                      <p className="text-[10px] text-muted-foreground">{t('manga.cardSfxDesc', 'Letreiro gigante que vaza os quadros')}</p>
                    </div>
                  </div>
                  <Plus size={15} className="shrink-0 text-muted-foreground transition group-hover:text-primary" />
                </button>
              </div>

              {/* Quick tutorial */}
              <div className="mt-3 space-y-1.5 rounded-2xl border border-border bg-muted/40 p-3 text-[11px]">
                <p className="flex items-center font-bold text-foreground">
                  <Info size={13} className="mr-1 text-primary" />{t('manga.insertTutorialTitle', 'Como usar?')}
                </p>
                <ul className="list-disc space-y-1 pl-4 text-muted-foreground">
                  <li>{t('manga.insertTutorial1', 'Selecione clicando no balão.')}</li>
                  <li>{t('manga.insertTutorial2', 'Arraste para mover.')}</li>
                  <li>{t('manga.insertTutorial3', 'Arraste o ponto da cauda para apontar a fala ao personagem.')}</li>
                  <li>{t('manga.insertTutorial4', 'Arraste os cantos para redimensionar sem distorcer.')}</li>
                </ul>
              </div>
            </div>
          )}
        </div>
        <ToolSep />
        {/* Roteiro toggle — desktop shows/hides the left column (like the book
            editor's chapter sidebar); mobile opens the full-screen drawer. */}
        <button
          onClick={() => (window.matchMedia('(min-width: 768px)').matches
            ? setScriptDesktop((o) => !o)
            : setScriptOpen(true))}
          title={t('manga.scriptToggleKbd', 'Mostrar/ocultar o roteiro (R)')} aria-label={t('manga.scriptPanelTitle', 'Roteiro')}
          className={cn(GHOST_BTN, scriptDesktop && GHOST_ACTIVE)}>
          <NotebookText size={17} />
        </button>
        {/* Keyboard shortcuts overlay ("?") */}
        <ShortcutsHelpButton />
        </div>
      </div>

      {/* Contextual selection tools — a slim row that appears when a panel or a
          bubble is selected, echoing the book editor's floating format toolbar
          but kept as a grouped, ghost-icon strip below the header. */}
      {selected && (
      <div className="flex flex-wrap items-center gap-1.5 border-t border-border/60 bg-muted/20 px-3 py-1.5 md:px-4">
        {selPanel && (
          <>
            <span className="mr-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground/70">{t('manga.ctxPanel', 'Quadro')}</span>
            {/* Gerar arte — the panel's primary action (accent, like the book's CTA) */}
            <button onClick={() => openGen(selPanel.id)} disabled={busy === 'gen'}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {busy === 'gen' ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}{t('manga.genArt', 'Gerar arte')}
            </button>
            <div className="flex items-center gap-0.5 rounded-xl border border-border/50 bg-muted/50 p-1">
              <button onClick={() => fileRef.current?.click()} title={t('manga.uploadArt', 'Enviar')} aria-label={t('manga.uploadArt', 'Enviar')} className={GHOST_BTN}><Upload size={17} /></button>
              {/* Rabisco: um estado DIFERENTE da arte final — sobe um esboço de
                  referência (bonequinho de pauzinhos) para depois "transformar". */}
              <button onClick={() => sketchFileRef.current?.click()} title={t('manga.uploadSketch', 'Enviar rabisco de referência')} aria-label={t('manga.sketch', 'Rabisco')} className={GHOST_BTN}>
                <PenLine size={17} />
              </button>
              {selPanel.sketchUrl && (
                <button onClick={() => transformSketchPanel(selPanel.id)} disabled={busy === 'gen'}
                  title={selPanel.imageUrl ? t('manga.regenerateFromSketch', 'Regerar do rabisco') : t('manga.transformSketch', 'Transformar rabisco')}
                  aria-label={selPanel.imageUrl ? t('manga.regenerateFromSketch', 'Regerar do rabisco') : t('manga.transformSketch', 'Transformar rabisco')}
                  className={cn(GHOST_BTN, !selPanel.imageUrl && GHOST_PRIMARY)}>
                  {busy === 'gen' ? <Loader2 size={17} className="animate-spin" /> : <Wand2 size={17} />}
                </button>
              )}
              {selPanel.imageUrl && <button onClick={() => clearPanelArt(selPanel.id)} title={t('manga.removeArt', 'Remover arte')} aria-label={t('manga.removeArt', 'Remover arte')} className={GHOST_BTN}><ImageOff size={17} /></button>}
              {/* Ajustar arte dentro do quadro: pan (arraste) + zoom */}
              {selPanel.imageUrl && (
                <button onClick={() => setFraming((f) => !f)} title={t('manga.adjustArt', 'Ajustar imagem no quadro (arraste p/ posicionar)')} aria-label={t('manga.adjust', 'Ajustar')}
                  className={cn(GHOST_BTN, framing && GHOST_ACTIVE)}>
                  <Move size={17} />
                </button>
              )}
            </div>
            {selPanel.imageUrl && framing && (
              <div className="flex items-center gap-0.5 rounded-xl border border-border/50 bg-muted/50 p-1">
                <button onClick={() => nudgeScale(selPanel.id, -0.15)} title={t('manga.zoomOut', 'Menos zoom')} aria-label={t('manga.zoomOut', 'Menos zoom')} className={GHOST_BTN}><ZoomOut size={17} /></button>
                <button onClick={() => nudgeScale(selPanel.id, 0.15)} title={t('manga.zoomIn', 'Mais zoom')} aria-label={t('manga.zoomIn', 'Mais zoom')} className={GHOST_BTN}><ZoomIn size={17} /></button>
                <button onClick={() => toggleFlip(selPanel.id, 'flipX')} title={t('manga.flipH', 'Inverter horizontal')} aria-label={t('manga.flipH', 'Inverter horizontal')} className={cn(GHOST_BTN, selPanel.flipX && GHOST_ACTIVE)}><FlipHorizontal2 size={17} /></button>
                <button onClick={() => toggleFlip(selPanel.id, 'flipY')} title={t('manga.flipV', 'Inverter vertical')} aria-label={t('manga.flipV', 'Inverter vertical')} className={cn(GHOST_BTN, selPanel.flipY && GHOST_ACTIVE)}><FlipVertical2 size={17} /></button>
                <button onClick={() => resetFrame(selPanel.id)} title={t('manga.resetFrame', 'Redefinir enquadramento')} aria-label={t('manga.resetFrame', 'Redefinir enquadramento')} className={GHOST_BTN}><X size={16} /></button>
              </div>
            )}
          </>
        )}
        {selBubble && (
          <>
            <span className="mr-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground/70">{t('manga.ctxBubble', 'Balão')}</span>
            {/* Bubble style popover — text, typography, colours/borders, layers
                and actions, in the same order/grouping as the reference. */}
            <div className="relative">
              <button onClick={() => { setStyleOpen((o) => !o); setTplOpen(false); setExportOpen(false); setInsertOpen(false); }}
                title={t('manga.bubbleStyleTitle', 'Estilo do balão')} aria-label={t('manga.bubbleStyle', 'Estilo')}
                className={cn('inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium', styleOpen ? 'bg-primary/15 text-primary hover:bg-primary/20' : 'text-muted-foreground hover:bg-muted hover:text-foreground')}>
                <Palette size={16} />{t('manga.bubbleStyle', 'Estilo')}
              </button>
              {styleOpen && selBubble && (
                <div className="absolute right-0 top-full z-30 mt-1 max-h-[80vh] w-72 overflow-y-auto rounded-xl border border-border bg-popover p-4 shadow-xl" onPointerDown={(e) => e.stopPropagation()}>
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t('manga.bubbleStyleTitle', 'Estilo do balão')}</p>
                    <span className="flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[9px] font-mono uppercase text-muted-foreground">
                      {BUBBLE_TYPES.find((bt) => bt.type === selBubble.type)?.icon}
                      {t(BUBBLE_TYPES.find((bt) => bt.type === selBubble.type)?.labelKey ?? '', BUBBLE_TYPES.find((bt) => bt.type === selBubble.type)?.label ?? selBubble.type)}
                    </span>
                  </div>

                  <div className="space-y-3.5 text-xs">
                    {/* A. Text content */}
                    <div>
                      <label className="mb-1 block text-[9px] font-semibold uppercase text-muted-foreground">{t('manga.styleTextContent', 'Texto do Balão')}</label>
                      <textarea value={selBubble.text} rows={3}
                        onFocus={() => snapshot()}
                        onChange={(e) => updateSelectedBubbleStyle({ text: e.target.value })}
                        placeholder={t('manga.styleTextPlaceholder', 'Escreva a fala…')}
                        className="w-full resize-y rounded-lg border border-border bg-background p-2.5 text-xs text-foreground outline-none focus:border-primary" />
                    </div>

                    {/* B. Typography */}
                    <div className="space-y-3 border-t border-border pt-3.5">
                      <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <TypeIcon size={12} className="text-primary" />{t('manga.styleSectionTypography', 'Tipografia')}
                      </p>

                      {/* Font family */}
                      <div>
                        <label className="mb-1 block text-[9px] font-semibold uppercase text-muted-foreground">{t('manga.bubbleFont', 'Fonte do texto')}</label>
                        <select value={selBubble.fontFamily ?? 'comic'} onChange={(e) => { snapshot(); setBubbleFontFamily(e.target.value); }}
                          className="w-full rounded-lg border border-border bg-background p-2 text-xs text-foreground">
                          {BUBBLE_FONT_OPTIONS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                        </select>
                      </div>

                      {/* Font size + alignment (alignment doesn't apply to 'sfx' — it's
                          one big centered display word, not a wrapped paragraph) */}
                      <div className={cn('grid gap-3', selBubble.type === 'sfx' ? 'grid-cols-1' : 'grid-cols-2')}>
                        <div>
                          <label className="mb-1 block text-[9px] font-semibold uppercase text-muted-foreground">
                            {t('manga.styleFontSize', 'Tamanho da fonte')} ({Math.round(selBubble.fontSize ?? Math.max(8, Math.min(26, selBubble.h * 44)))}px)
                          </label>
                          <input type="range" min={10} max={32}
                            value={Math.round(selBubble.fontSize ?? Math.max(8, Math.min(26, selBubble.h * 44)))}
                            onPointerDown={() => snapshot()}
                            onChange={(e) => updateSelectedBubbleStyle({ fontSize: Number(e.target.value) })}
                            className="h-1.5 w-full cursor-pointer rounded-full accent-primary" />
                        </div>
                        {selBubble.type !== 'sfx' && (
                          <div>
                            <label className="mb-1 block text-[9px] font-semibold uppercase text-muted-foreground">{t('manga.styleTextAlign', 'Alinhamento')}</label>
                            <div className="flex items-center gap-0.5 rounded-lg border border-border bg-background p-0.5">
                              {([
                                ['left', AlignLeft, t('manga.alignLeft', 'Alinhar à esquerda')],
                                ['center', AlignCenter, t('manga.alignCenter', 'Centralizar')],
                                ['right', AlignRight, t('manga.alignRight', 'Alinhar à direita')],
                              ] as const).map(([a, Icon, label]) => (
                                <button key={a} onClick={() => { snapshot(); setBubbleAlign(a); }} title={label}
                                  className={cn('flex flex-1 items-center justify-center rounded-md py-1', (selBubble.align ?? 'center') === a ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}>
                                  <Icon size={14} />
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Font style (bold/italic) + padding — meaningless for 'sfx'
                          (always Bangers, no wrapped padding box to speak of) */}
                      {selBubble.type !== 'sfx' && (
                        <div className="grid grid-cols-4 gap-2">
                          <button
                            onClick={() => { snapshot(); updateSelectedBubbleStyle({ fontStyle: (selBubble.fontStyle ?? '').includes('bold') ? ((selBubble.fontStyle ?? '').includes('italic') ? 'italic' : 'normal') : ((selBubble.fontStyle ?? '').includes('italic') ? 'bold-italic' : 'bold') }); }}
                            title={t('manga.styleFontStyle', 'Estilo do texto')}
                            className={cn('rounded-lg border py-1 text-xs font-bold', (selBubble.fontStyle ?? '').includes('bold') ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background text-muted-foreground hover:bg-muted')}>
                            B
                          </button>
                          <button
                            onClick={() => { snapshot(); updateSelectedBubbleStyle({ fontStyle: (selBubble.fontStyle ?? '').includes('italic') ? ((selBubble.fontStyle ?? '').includes('bold') ? 'bold' : 'normal') : ((selBubble.fontStyle ?? '').includes('bold') ? 'bold-italic' : 'italic') }); }}
                            title={t('manga.styleFontStyle', 'Estilo do texto')}
                            className={cn('rounded-lg border py-1 text-xs italic font-semibold', (selBubble.fontStyle ?? '').includes('italic') ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background text-muted-foreground hover:bg-muted')}>
                            I
                          </button>
                          <div className="col-span-2">
                            <label className="mb-1 block text-[9px] font-semibold uppercase text-muted-foreground">
                              {t('manga.stylePadding', 'Espaçamento')} ({Math.round(selBubble.padding ?? defaultBubblePaddingPx(selBubble, pageW))}px)
                            </label>
                            <input type="range" min={4} max={40}
                              value={Math.round(selBubble.padding ?? defaultBubblePaddingPx(selBubble, pageW))}
                              onPointerDown={() => snapshot()}
                              onChange={(e) => updateSelectedBubbleStyle({ padding: Number(e.target.value) })}
                              className="h-1.5 w-full cursor-pointer rounded-full accent-primary" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* C. Colors & Borders */}
                    <div className="space-y-3 border-t border-border pt-3.5">
                      <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <Palette size={12} className="text-primary" />{t('manga.styleSectionColors', 'Cores & Bordas')}
                      </p>

                      {/* Colours — 'sfx' has no separate text colour (the fill/stroke
                          pair below ARE its fill/outline, there's no balloon body). */}
                      <div className={cn('grid gap-2', selBubble.type === 'sfx' ? 'grid-cols-2' : 'grid-cols-3')}>
                        <div>
                          <label className="mb-1 block text-[9px] font-semibold uppercase text-muted-foreground">{t('manga.styleFill', 'Preench.')}</label>
                          <input type="color" value={selBubble.fillColor ?? resolveBubbleStyle(selBubble).fill}
                            onPointerDown={() => snapshot()}
                            onChange={(e) => updateSelectedBubbleStyle({ fillColor: e.target.value })}
                            className="h-7 w-full cursor-pointer rounded-lg border-0 bg-transparent" />
                        </div>
                        <div>
                          <label className="mb-1 block text-[9px] font-semibold uppercase text-muted-foreground">{t('manga.styleStroke', 'Contorno')}</label>
                          <input type="color" value={selBubble.strokeColor ?? resolveBubbleStyle(selBubble).stroke}
                            onPointerDown={() => snapshot()}
                            onChange={(e) => updateSelectedBubbleStyle({ strokeColor: e.target.value })}
                            className="h-7 w-full cursor-pointer rounded-lg border-0 bg-transparent" />
                        </div>
                        {selBubble.type !== 'sfx' && (
                          <div>
                            <label className="mb-1 block text-[9px] font-semibold uppercase text-muted-foreground">{t('manga.styleText', 'Texto')}</label>
                            <input type="color" value={selBubble.textColor ?? resolveBubbleStyle(selBubble).textColor}
                              onPointerDown={() => snapshot()}
                              onChange={(e) => updateSelectedBubbleStyle({ textColor: e.target.value })}
                              className="h-7 w-full cursor-pointer rounded-lg border-0 bg-transparent" />
                          </div>
                        )}
                      </div>

                      {/* Body shape toggle — speech/whisper only: a square "manga
                          panel" balloon body instead of the classic oval, keeping the tail. */}
                      {(selBubble.type === 'speech' || selBubble.type === 'whisper') && (
                        <div>
                          <label className="mb-1 block text-[9px] font-semibold uppercase text-muted-foreground">{t('manga.styleBodyShape', 'Formato')}</label>
                          <div className="flex items-center gap-1 rounded-lg border border-border bg-background p-0.5 text-[11px]">
                            {(['oval', 'rect'] as const).map((s) => (
                              <button key={s} onClick={() => { snapshot(); updateSelectedBubbleStyle({ bodyShape: s === 'oval' ? undefined : s }); }}
                                className={cn('flex-1 rounded-md py-1 text-center font-semibold', (selBubble.bodyShape ?? 'oval') === s ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}>
                                {s === 'oval' ? t('manga.shapeOval', 'Oval') : t('manga.shapeRect', 'Retângulo')}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Stroke width + rotation — rotation is UNIVERSAL (every bubble
                          type can spin, either via this slider or the editor's own
                          drag handle above the selected bubble). */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="mb-1 block text-[9px] font-semibold uppercase text-muted-foreground">{t('manga.styleStrokeWidth', 'Espessura')} ({selBubble.strokeWidth ?? (selBubble.type === 'sfx' ? 8 : 3)}px)</label>
                          <input type="range" min={1} max={selBubble.type === 'sfx' ? 20 : 8} value={selBubble.strokeWidth ?? (selBubble.type === 'sfx' ? 8 : 3)}
                            onPointerDown={() => snapshot()}
                            onChange={(e) => updateSelectedBubbleStyle({ strokeWidth: Number(e.target.value) })}
                            className="h-1.5 w-full cursor-pointer rounded-full accent-primary" />
                        </div>
                        <div>
                          <label className="mb-1 block text-[9px] font-semibold uppercase text-muted-foreground">{t('manga.styleRotation', 'Rotação')} ({selBubble.rotation ?? 0}°)</label>
                          <input type="range" min={-180} max={180} value={selBubble.rotation ?? 0}
                            onPointerDown={() => snapshot()}
                            onChange={(e) => updateSelectedBubbleStyle({ rotation: Number(e.target.value) })}
                            className="h-1.5 w-full cursor-pointer rounded-full accent-primary" />
                        </div>
                      </div>

                      {/* Type-specific slider (spikes / radius) — independent from
                          rotation now that rotation applies to every type above. */}
                      {selBubble.type === 'shout' ? (
                        <div>
                          <label className="mb-1 block text-[9px] font-semibold uppercase text-muted-foreground">{t('manga.styleSpikes', 'Pontas')} ({selBubble.screamSpikes ?? 18})</label>
                          <input type="range" min={8} max={24} value={selBubble.screamSpikes ?? 18}
                            onPointerDown={() => snapshot()}
                            onChange={(e) => updateSelectedBubbleStyle({ screamSpikes: Number(e.target.value) })}
                            className="h-1.5 w-full cursor-pointer rounded-full accent-primary" />
                        </div>
                      ) : (selBubble.type === 'narration' || selBubble.type === 'caption' || ((selBubble.type === 'speech' || selBubble.type === 'whisper') && selBubble.bodyShape === 'rect')) ? (
                        <div>
                          <label className="mb-1 block text-[9px] font-semibold uppercase text-muted-foreground">{t('manga.styleRadius', 'Raio da borda')} ({selBubble.borderRadius ?? 8})</label>
                          <input type="range" min={0} max={40} value={selBubble.borderRadius ?? 8}
                            onPointerDown={() => snapshot()}
                            onChange={(e) => updateSelectedBubbleStyle({ borderRadius: Number(e.target.value) })}
                            className="h-1.5 w-full cursor-pointer rounded-full accent-primary" />
                        </div>
                      ) : null}

                      {/* Tail width/style — only for bubbles that actually have a tail */}
                      {selBubble.type !== 'narration' && selBubble.type !== 'caption' && selBubble.type !== 'sfx' && (
                        <>
                          <div>
                            <label className="mb-1 block text-[9px] font-semibold uppercase text-muted-foreground">
                              {t('manga.styleTailWidth', 'Largura da cauda')} ({Math.round(selBubble.tailWidth ?? (selBubble.type === 'thought' ? 14 : 20) * (pageW / 800))}px)
                            </label>
                            <input type="range" min={8} max={48}
                              value={Math.round(selBubble.tailWidth ?? (selBubble.type === 'thought' ? 14 : 20) * (pageW / 800))}
                              onPointerDown={() => snapshot()}
                              onChange={(e) => updateSelectedBubbleStyle({ tailWidth: Number(e.target.value) })}
                              className="h-1.5 w-full cursor-pointer rounded-full accent-primary" />
                          </div>
                          <div>
                            <label className="mb-1 block text-[9px] font-semibold uppercase text-muted-foreground">{t('manga.styleTailStyle', 'Estilo da cauda')}</label>
                            <div className="flex items-center gap-1 rounded-lg border border-border bg-background p-0.5 text-[11px]">
                              {(['straight', 'curved'] as const).map((s) => (
                                <button key={s} onClick={() => { snapshot(); updateSelectedBubbleStyle({ tailStyle: s }); }}
                                  className={cn('flex-1 rounded-md py-1 text-center font-semibold', (selBubble.tailStyle ?? 'curved') === s ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}>
                                  {s === 'straight' ? t('manga.styleTailStraight', 'Reta') : t('manga.styleTailCurved', 'Curva')}
                                </button>
                              ))}
                            </div>
                            {(selBubble.tailStyle ?? 'curved') === 'curved' && (
                              <button onClick={() => { snapshot(); updateSelectedBubbleStyle({ tailFlip: !selBubble.tailFlip }); }}
                                className={cn('mt-1.5 flex w-full items-center justify-center gap-1.5 rounded-lg border py-1 text-[11px] font-semibold transition',
                                  selBubble.tailFlip ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-muted')}>
                                <FlipHorizontal2 size={12} />{t('manga.styleTailFlip', 'Inverter curvatura')}
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {/* D. Layers */}
                    <div className="space-y-1.5 border-t border-border pt-3.5">
                      <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <Copy size={12} className="text-primary" />{t('manga.styleSectionLayers', 'Camadas')}
                      </p>
                      <div className="flex items-center gap-2">
                        <button onClick={() => moveLayerUp(selBubble.id)} title={t('manga.layerFront', 'Trazer para frente')}
                          className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-border bg-background py-1.5 text-[11px] font-semibold text-foreground hover:bg-muted">
                          <ChevronUp size={13} />{t('manga.layerFront', 'Trazer para frente')}
                        </button>
                        <button onClick={() => moveLayerDown(selBubble.id)} title={t('manga.layerBack', 'Enviar para trás')}
                          className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-border bg-background py-1.5 text-[11px] font-semibold text-foreground hover:bg-muted">
                          <ChevronDown size={13} />{t('manga.layerBack', 'Enviar para trás')}
                        </button>
                      </div>
                    </div>

                    {/* E. Actions */}
                    <div className="flex items-center gap-2 border-t border-border pt-3.5">
                      <button onClick={duplicateTwin} title={t('manga.twinBubbleKbd', 'Duplicar colado (balão gêmeo) (D)')}
                        className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border bg-background py-2 text-xs font-semibold text-foreground hover:bg-muted">
                        <Copy size={14} className="text-primary" />{t('manga.twin', 'Duplicar')}
                      </button>
                      <button onClick={deleteSelected} title={t('manga.deleteBubbleKbd', 'Excluir (Del)')}
                        className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-destructive/30 bg-destructive/10 py-2 text-xs font-semibold text-destructive hover:bg-destructive/20">
                        <Trash2 size={14} />{t('manga.deleteBubble', 'Excluir')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        <div className="flex-1" />
        {selected && (
          <button onClick={deleteSelected} title={t('manga.deleteBubble', 'Excluir')} aria-label={t('manga.deleteBubble', 'Excluir')}
            className="inline-flex items-center justify-center h-8 w-8 rounded-md text-destructive hover:bg-destructive/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <Trash2 size={16} />
          </button>
        )}
      </div>
      )}
      {/* Hidden file inputs — always mounted so panel uploads work regardless of
          the contextual row's mount state. */}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f && selPanel) onPanelUpload(selPanel.id, f); e.target.value = ''; }} />
      <input ref={sketchFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f && selPanel) onSketchUpload(selPanel.id, f); e.target.value = ''; }} />

      {/* Layout generator — pick how many panels, then generate an organic layout */}
      {tplOpen && (
        <div className="absolute left-4 top-full z-30 mt-1 w-[min(340px,92vw)] rounded-xl border border-border bg-popover p-4 shadow-xl" onPointerDown={(e) => e.stopPropagation()}>
            <p className="mb-1 text-sm font-semibold text-foreground">{t('manga.panelsOnPage', 'Quadros na página')}</p>
            <p className="mb-3 text-[11px] text-muted-foreground">{t('manga.panelsHint', 'Escolha quantos quadros. O layout é gerado de forma dinâmica (RTL) — embaralhe até gostar.')}</p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 rounded-lg border border-border">
                <button onClick={() => setPanelCount((n) => Math.max(1, n - 1))} className="rounded-l-lg px-2.5 py-2 text-foreground hover:bg-muted"><Minus size={14} /></button>
                <span className="w-8 text-center font-mono text-lg font-bold text-foreground">{panelCount}</span>
                <button onClick={() => setPanelCount((n) => Math.min(12, n + 1))} className="rounded-r-lg px-2.5 py-2 text-foreground hover:bg-muted"><Plus size={14} /></button>
              </div>
              <button onClick={() => genLayout(panelCount)} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
                <Shuffle size={15} />{t('manga.generateLayout', 'Gerar layout')}
              </button>
            </div>
            {/* Stable previews — regenerated ONLY when the count changes or on
                explicit reshuffle, never on unrelated re-renders. Clicking one
                APPLIES it keeping the page's art (applyLayout). */}
            <div className="mt-3 grid grid-cols-4 gap-2">
              {previews.map((preview, k) => (
                <button key={k} onClick={() => applyLayout(preview)}
                  className="overflow-hidden rounded-md border border-border bg-white transition hover:border-primary" style={{ aspectRatio: '3 / 4' }}>
                  <svg viewBox="0 0 100 133" className="h-full w-full" preserveAspectRatio="none">
                    {preview.map((pts, i) => <polygon key={i} points={pts.map((p) => `${p.x * 100},${p.y * 133}`).join(' ')} fill="#e9e4d6" stroke="#111" strokeWidth={1.4} />)}
                  </svg>
                </button>
              ))}
            </div>
          <button onClick={() => setPreviewSeed((s) => s + 1)}
            className="mt-2 w-full rounded-lg border border-border py-1.5 text-xs text-muted-foreground hover:bg-muted">
            {t('manga.morePreviews', 'Ver outras propostas')}
          </button>
        </div>
      )}
      </header>

      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        {/* Roteiro — story guide (premise + page list) + AI page-script assistant.
            Desktop: permanent left column (like a book editor's chapter sidebar).
            Mobile: full-screen drawer, opened via the "Roteiro" toolbar button. */}
        <div className={cn(
          'z-[110] flex-col overflow-y-auto border-border bg-card',
          'md:relative md:z-auto md:w-72 md:shrink-0 md:border-r',
          scriptOpen ? 'fixed inset-0 flex' : 'hidden',
          scriptDesktop ? 'md:flex' : 'md:hidden',
        )}>
          <div className="flex shrink-0 items-center justify-between border-b border-border px-3 py-2 md:hidden">
            <span className="font-serif text-sm font-bold text-foreground">{t('manga.scriptPanelTitle', 'Roteiro')}</span>
            <button onClick={() => setScriptOpen(false)} className="rounded-full p-1 text-muted-foreground hover:bg-muted" aria-label={t('common.close', 'Fechar')}>
              <X size={16} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 md:p-3" style={{ scrollbarWidth: 'thin' }}>
            {/* Project card — the SAME "toc-book" identity card the book editor's
                sidebar opens with: mini cover tile + serif title + mono meta. */}
            <div className="mx-1 mb-3 flex items-center gap-2.5 rounded-[10px] bg-background px-2.5 py-2 shadow-[0_0_0_1px_var(--border)_inset]">
              <span className="grid h-8 w-6 shrink-0 place-items-center rounded-[3px] bg-gradient-to-br from-primary/80 to-accent/70 text-primary-foreground shadow-sm">
                <BookOpen size={12} />
              </span>
              <div className="min-w-0">
                <h2 className="truncate font-serif text-[12.5px] font-medium leading-tight text-foreground">
                  {projectTitle || t('projects.untitled', 'Sem título')}
                </h2>
                <p className="mt-0.5 font-mono text-[7.5px] uppercase tracking-[0.14em] text-muted-foreground">
                  {t('manga.sidebarKind', 'Mangá')} · {t('manga.breadcrumbPage', 'Página {{n}}/{{total}}', { n: Math.max(1, pages.findIndex((p) => p.id === currentId) + 1), total: pages.length })}
                </p>
              </div>
            </div>

            {/* Guia — premissa do projeto (dedupada: a lista de páginas some
                daqui, já vive na tira de páginas à direita do canvas) +
                contexto compacto da página ATUAL. */}
            <p className="mb-1.5 flex items-center gap-1.5 font-mono text-[8.5px] font-medium uppercase tracking-[0.24em] text-muted-foreground">
              <BookOpen size={14} />{t('manga.scriptGuideTitle', 'Guia da história')}
            </p>
            {premise ? (
              <p className="mb-1.5 line-clamp-2 text-xs leading-relaxed text-muted-foreground" title={premise}>{premise}</p>
            ) : (
              <button type="button"
                onClick={() => notify.info(t('manga.definePremiseHint', 'Defina a premissa nas informações do projeto (Estante → editar livro).'))}
                title={t('manga.definePremiseHint', 'Defina a premissa nas informações do projeto (Estante → editar livro).')}
                className="mb-1.5 text-[11px] font-medium text-primary hover:underline">
                {t('manga.definePremise', 'Definir premissa')}
              </button>
            )}
            {current && (
              <p className="mb-4 font-mono text-[9.5px] tracking-wide text-muted-foreground/70">
                {t('manga.scriptPageContext', 'Página {{n}} de {{total}} · {{q}} quadros · {{b}} balões', {
                  n: Math.max(1, pages.findIndex((p) => p.id === currentId) + 1), total: pages.length,
                  q: current.layout.panels.length, b: current.layout.bubbles.length,
                })}
              </p>
            )}

            {/* Roteiro — a área REAL de criação manual, com a identidade do
                editor de livro (papel, serif, respiro): um bloco de escrita
                livre POR QUADRO, sem marcador — cada bloco É o quadro.
                Aplica sozinho ao sair do campo, ou via Ctrl/Cmd+Enter (ver
                handlePanelBlockBlur / handleBlockKeyDown). O Ghostwriter e o
                "Criar com IA" (abaixo) escrevem nesses MESMOS blocos — manual
                é o padrão, IA é ajuda. */}
            <div className="border-t border-border pt-3">
              <div className="mb-0.5 flex items-center justify-between gap-2">
                <p className="flex items-center gap-1.5 font-mono text-[8.5px] font-medium uppercase tracking-[0.24em] text-muted-foreground">
                  <NotebookText size={14} />{t('manga.scriptManualTitle', 'Roteiro')}
                </p>
                {/* Ghostwriter — SAME ✨ branding/position as the book editor's AI
                    assistant. Now it OPENS THE CONVERSATIONAL CHAT (talk by text
                    or voice; it can fill the page, the Universe and the premise).
                    The deterministic "complete this page" flow lives on as the
                    secondary link beside it (runGhostwriter). */}
                <div className="flex shrink-0 items-center gap-0.5">
                  <button onClick={runGhostwriter} disabled={!current || panels.length === 0 || ghostwriterBusy}
                    title={t('manga.ghostwriterHintKbd', 'Completa o roteiro desta página preservando o que você já escreveu.')}
                    aria-label={t('manga.ghostwriterCompletePage', 'Completar página')}
                    className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[10px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40">
                    {ghostwriterBusy ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
                    <span className="hidden sm:inline">{t('manga.ghostwriterCompletePage', 'Completar página')}</span>
                  </button>
                  <button onClick={() => { setScriptOpen(false); setChatOpen(true); }} disabled={!current}
                    title={t('manga.ghostwriterChatHint', 'Converse com o Ghostwriter (texto ou voz) — ele preenche Universo, roteiro e premissa. (Ctrl+Shift+G)')}
                    aria-label={t('ai.ghostwriter', 'Ghostwriter')}
                    className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-medium text-primary transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40">
                    <Sparkles size={13} />
                    {t('ai.ghostwriter', 'Ghostwriter')}
                  </button>
                </div>
              </div>
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <p className="font-serif text-[12px] italic text-muted-foreground">{t('manga.scriptManualSubtitle', 'Escreva sua história, quadro a quadro')}</p>
                <button onClick={() => setScriptHelpOpen((o) => !o)}
                  className="shrink-0 text-[10px] font-medium text-primary hover:underline">
                  {t('manga.scriptHowTo', 'Como escrever?')}
                </button>
              </div>
              {scriptHelpOpen && (
                <div className="mb-2 rounded-lg border border-dashed border-border bg-muted/30 p-2">
                  <pre className="whitespace-pre-wrap font-mono text-[10px] leading-relaxed text-muted-foreground">
{t('manga.scriptHowToExample', 'Rami no topo da mansão, plano aberto, cidade ao fundo à noite.\nRami: Hoje eu termino isso.\n(pensamento) Rami: Se eu falhar… ninguém saberá.\nSFX: CRASH!\nNarrador: A cidade nunca dorme.\n(legenda) Cidade Alta, 23h.')}
                  </pre>
                  <p className="mt-1.5 border-t border-dashed border-border pt-1.5 text-[10px] leading-relaxed text-muted-foreground/80">
                    {t('manga.scriptHowToCtrlEnter', 'Dica: Ctrl+Enter (⌘+Enter no Mac) aplica o quadro e já leva o cursor para o próximo — escreva a página inteira sem tirar as mãos do teclado.')}
                  </p>
                </div>
              )}
              {!current ? null : panels.length === 0 ? (
                <div className="rounded-[10px] border border-dashed border-border bg-muted/30 p-3 text-center">
                  <p className="mb-2 text-[11px] text-muted-foreground">{t('manga.scriptManualNoLayout', 'Escolha um layout primeiro.')}</p>
                  <button onClick={() => setTplOpen(true)}
                    className="rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90">
                    <LayoutGrid size={13} className="mr-1 inline" />{t('manga.layout', 'Layout')}
                  </button>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {panels.map((panel, i) => {
                    const draft = scriptDrafts.get(panel.id) ?? '';
                    const feedback = scriptFeedback.get(panel.id);
                    const rows = Math.min(12, Math.max(4, draft.split('\n').length + 1));
                    const hasDesc = getPanelDescription(panel).length > 0;
                    const dialoguesBusyHere = dialoguesBusy.has(panel.id) || dialoguesBusy.has('all');
                    return (
                      <div key={panel.id} className="rounded-[10px] bg-background p-2.5 ring-1 ring-inset ring-border transition focus-within:ring-[1.5px] focus-within:ring-primary">
                        <div className="mb-1.5 flex items-center justify-between gap-1.5">
                          <div className="flex items-center gap-1.5">
                            <span className="font-serif text-[13px] font-medium text-foreground">{t('manga.scriptBlockPanelLabel', 'Quadro {{n}}', { n: i + 1 })}</span>
                            {panel.imageUrl && (
                              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" title={t('manga.scriptBlockHasArt', 'Tem arte')} />
                            )}
                          </div>
                          <div className="flex shrink-0 items-center gap-0.5">
                            {feedback && (
                              <span className="flex items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-medium text-primary">
                                <Check size={10} />{feedback}
                              </span>
                            )}
                            <button onClick={() => generateDialoguesFor([i], panel.id)} disabled={!hasDesc || dialoguesBusyHere}
                              title={t('manga.dialoguesGenerateOne', 'Gerar falas deste quadro')}
                              className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground/70 transition hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30">
                              {dialoguesBusyHere
                                ? <Loader2 size={12} className="animate-spin" />
                                : <span className="relative inline-flex"><MessageCircle size={12} /><Sparkles size={7} className="absolute -right-1 -top-1" /></span>}
                            </button>
                            <button onClick={() => openGen(panel.id)} disabled={busy === 'gen'}
                              title={t('manga.genArt', 'Gerar arte')}
                              className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground/70 transition hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30">
                              <Sparkles size={12} />
                            </button>
                          </div>
                        </div>
                        <textarea
                          ref={(el) => { if (el) scriptTextareaRefs.current.set(panel.id, el); else scriptTextareaRefs.current.delete(panel.id); }}
                          value={draft}
                          onChange={(e) => {
                            const { value } = e.target;
                            setScriptDrafts((prev) => new Map(prev).set(panel.id, value));
                            setScriptDirty((prev) => new Set(prev).add(panel.id));
                          }}
                          onBlur={() => handlePanelBlockBlur(i, panel.id)}
                          onKeyDown={(e) => handleBlockKeyDown(e, i, panel.id)}
                          placeholder={t('manga.scriptBlockPlaceholder', 'O que vemos neste quadro? Cenário, ação, enquadramento…\nRami: — e as falas assim')}
                          spellCheck={false}
                          rows={rows}
                          className="min-h-[96px] w-full resize-y border-0 bg-transparent px-3 py-2.5 text-[13px] font-normal leading-relaxed text-foreground outline-none placeholder:text-muted-foreground/45" />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Criar com IA — colapsável (chevron animado + resumo de 1 linha
                quando fechado): descreve a cena e a IA escreve diretamente no
                documento do roteiro acima. */}
            <div className="border-t border-border pt-3">
              <button onClick={() => setAiAssistOpen((o) => !o)}
                className="flex w-full items-center justify-between gap-1.5 font-mono text-[8.5px] font-medium uppercase tracking-[0.24em] text-muted-foreground hover:text-foreground">
                <span className="flex items-center gap-1.5"><Sparkles size={14} />{t('manga.scriptAssistantTitle', 'Criar com IA')}</span>
                <ChevronDown size={13} className={cn('shrink-0 transition-transform duration-200', aiAssistOpen && 'rotate-180')} />
              </button>
              {!aiAssistOpen && (
                <p className="mt-1 truncate text-[10px] normal-case tracking-normal text-muted-foreground/70">
                  {sceneText.trim() || t('manga.aiAssistClosedHint', 'Descreva a cena e a IA escreve o roteiro da página.')}
                </p>
              )}
              {aiAssistOpen && (
                <div className="mt-2">
                  <p className="mb-1.5 text-[10px] leading-relaxed text-muted-foreground">
                    {t('manga.scriptAssistantHint', 'A IA preenche o roteiro da página abaixo — você pode editar tudo depois.')}
                  </p>
                  <textarea value={sceneText} onChange={(e) => setSceneText(e.target.value)} disabled={!current}
                    placeholder={t('manga.scriptPlaceholder', 'O que acontece nesta página?')}
                    rows={4}
                    className="w-full resize-none rounded-lg border border-border bg-background px-2.5 py-2 text-xs text-foreground outline-none focus:border-primary disabled:opacity-50" />
                  {/* Tick who appears on this page — the script (and later the art)
                      will feature exactly these Universe characters/settings. */}
                  {cast.length > 0 && (
                    <div className="mt-2">
                      <p className="mb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{t('manga.scriptCast', 'Quem aparece nesta página')}</p>
                      <div className="flex max-h-28 flex-wrap gap-1 overflow-y-auto">
                        {cast.map((e) => {
                          const on = scriptCast.has(e.id);
                          return (
                            <button key={e.id}
                              onClick={() => setScriptCast((prev) => { const next = new Set(prev); if (on) next.delete(e.id); else next.add(e.id); return next; })}
                              className={cn('flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] transition', on ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-muted')}>
                              {e.imageUrl ? <img src={e.imageUrl} alt="" className="h-3.5 w-3.5 rounded-full object-cover" /> : <span className="h-3.5 w-3.5 rounded-full bg-muted" />}
                              {e.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-[11px] text-muted-foreground">{t('manga.scriptPanelCount', 'Quadros')}</span>
                    <div className="flex items-center gap-1 rounded-lg border border-border">
                      <button onClick={() => setScenePanelCount((n) => Math.max(1, n - 1))} disabled={!current} className="rounded-l-lg px-2 py-1 text-foreground hover:bg-muted disabled:opacity-40"><Minus size={12} /></button>
                      <span className="w-6 text-center font-mono text-xs font-bold text-foreground">{scenePanelCount}</span>
                      <button onClick={() => setScenePanelCount((n) => Math.min(12, n + 1))} disabled={!current} className="rounded-r-lg px-2 py-1 text-foreground hover:bg-muted disabled:opacity-40"><Plus size={12} /></button>
                    </div>
                  </div>
                  <button onClick={generateScriptForPage} disabled={!current || scriptBusy || !sceneText.trim()}
                    className="mt-2.5 flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                    {scriptBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                    {t('manga.scriptGenerate', 'Gerar roteiro da página')}
                  </button>
                </div>
              )}
            </div>

            {/* Rodapé — hierarquia de ações: "Gerar falas" (ghost, todos os
                quadros com descrição) + "Gerar página inteira" (primária),
                juntas num grupo com divisor, cada uma com seu próprio estado. */}
            {current && panels.length > 0 && (
              <div className="border-t border-border pt-3">
                <div className="flex items-stretch gap-1.5 rounded-[10px] bg-card p-1.5 shadow-[0_0_0_1px_var(--border)_inset]">
                  <button onClick={() => generateDialoguesFor(panels.map((_, i) => i), 'all')}
                    disabled={!anyPanelHasDescription || dialoguesBusy.has('all')}
                    title={anyPanelHasDescription
                      ? t('manga.dialoguesAllHint', 'Gera as falas de todos os quadros com descrição.')
                      : t('manga.dialoguesNoDesc', 'Escreva a descrição do(s) quadro(s) primeiro.')}
                    className="btn-ghost-mock flex flex-1 items-center justify-center gap-1.5 disabled:cursor-not-allowed disabled:opacity-40"
                    style={{ padding: '8px 8px', fontSize: '8.5px' }}>
                    {dialoguesBusy.has('all') ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                    {t('manga.dialoguesGenerateAll', 'Gerar falas')}
                  </button>
                  <div className="w-px shrink-0 bg-border" />
                  <button onClick={generateAllArts} disabled={!!genAll || busy === 'gen' || !anyPanelHasScript}
                    title={t('manga.genAllHint', 'Gera a arte de cada quadro a partir do roteiro. Quadros com arte são mantidos; se todos tiverem, regenera tudo.')}
                    className="btn-ember flex flex-1 items-center justify-center gap-1.5 disabled:cursor-not-allowed disabled:opacity-50"
                    style={{ padding: '8px 8px', fontSize: '8.5px' }}>
                    {genAll
                      ? <><Loader2 size={13} className="animate-spin" />{t('manga.genAllProgress', 'Quadro {{n}}/{{m}}…', { n: Math.min(genAll.done + 1, genAll.total), m: genAll.total })}</>
                      : <><LayoutGrid size={13} />{t('manga.genAllArts', 'Gerar página inteira')}</>}
                  </button>
                </div>
              </div>
            )}

            {/* Transformar rabiscos — cross-page bulk action, visible whenever
                ANY page (not just the current one) has a sketch without final
                art yet. Walks all pages sequentially (see transformAllSketches). */}
            {anySketchNeedsTransform && (
              <div className="border-t border-border pt-3">
                <button onClick={transformAllSketches} disabled={!!transformAll || !!genAll || busy === 'gen'}
                  title={t('manga.transformAllHint', 'Gera a arte de cada quadro com rabisco (e sem arte final) em TODAS as páginas.')}
                  className="btn-ember flex w-full items-center justify-center gap-1.5 disabled:cursor-not-allowed disabled:opacity-50"
                  style={{ padding: '8px 8px', fontSize: '8.5px' }}>
                  {transformAll
                    ? <><Loader2 size={13} className="animate-spin" />{t('manga.transformAllProgress', 'Quadro {{n}}/{{m}}…', { n: Math.min(transformAll.done + 1, transformAll.total), m: transformAll.total })}</>
                    : <><Wand2 size={13} />{t('manga.transformAllSketches', 'Transformar rabiscos (todas as páginas)')}</>}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Canvas */}
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-muted/40 p-3 md:p-6" onPointerDown={() => { setSelected(null); setTplOpen(false); setExportOpen(false); setStyleOpen(false); setInsertOpen(false); setDirMenuOpen(false); }}>
          {current ? (
            <div ref={canvasRef} className="relative w-[min(94vw,540px)] max-w-full shrink-0 border border-border bg-white shadow-xl md:h-[min(82vh,1150px)] md:w-auto"
              style={{ aspectRatio: `${current.width || 1000} / ${current.height || 1414}` }}
              onPointerDown={(e) => e.stopPropagation()}>
              {panels.length === 0 && (
                <button onClick={() => setTplOpen(true)} className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                  <LayoutGrid size={30} /><span className="text-sm">{t('manga.pickLayout', 'Escolha um layout')}</span>
                </button>
              )}
              {/* Panels — art fills each panel's bounding box (object-cover) and is
                  clipped to the polygon. offsetX/offsetY + scale frame the art. */}
              {panels.map((p) => {
                const xs = p.points.map((pt) => pt.x), ys = p.points.map((pt) => pt.y);
                const minX = Math.min(...xs), minY = Math.min(...ys);
                const bw = Math.max(...xs) - minX, bh = Math.max(...ys) - minY;
                const isFraming = framing && selected?.kind === 'panel' && selected.id === p.id && !!p.imageUrl;
                return (
                  <div key={p.id}
                    onPointerDown={(e) => isFraming
                      ? startDrag(e, { mode: 'artPan', id: p.id, orig: { ox: p.offsetX ?? 0, oy: p.offsetY ?? 0, bw, bh } })
                      : startDrag(e, { mode: 'panelMove', id: p.id, orig: p.points })}
                    onClick={(e) => { e.stopPropagation(); setSelected({ kind: 'panel', id: p.id }); }}
                    className={cn('absolute inset-0', isFraming ? 'cursor-move' : 'cursor-pointer')}
                    style={{ clipPath: toPolygonCss(p.points) }}>
                    {p.imageUrl
                      ? <img src={p.imageUrl} alt="" draggable={false}
                          style={{
                            position: 'absolute', left: `${minX * 100}%`, top: `${minY * 100}%`,
                            width: `${bw * 100}%`, height: `${bh * 100}%`, objectFit: 'cover',
                            transform: `translate(${(p.offsetX ?? 0) * 100}%, ${(p.offsetY ?? 0) * 100}%) scale(${(p.scale ?? 1) * (p.flipX ? -1 : 1)}, ${(p.scale ?? 1) * (p.flipY ? -1 : 1)})`,
                            transformOrigin: 'center',
                          }} />
                      : p.sketchUrl
                        ? (
                          // Rabisco — estado DIFERENTE da arte final: mostrado
                          // dessaturado + com um distintivo inequívoco, nunca
                          // confundível com a ilustração acabada.
                          <>
                            <img src={p.sketchUrl} alt="" draggable={false}
                              className="grayscale"
                              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.8 }} />
                            <span className="pointer-events-none absolute bottom-1 right-1 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.14em] text-white">
                              {t('manga.sketchBadge', 'Rabisco')}
                            </span>
                          </>
                        )
                        : <div className="absolute inset-0 bg-[repeating-linear-gradient(45deg,#f3f0e8,#f3f0e8_10px,#ece7db_10px,#ece7db_20px)]" />}
                  </div>
                );
              })}
              {/* Crisp panel borders */}
              <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                {panels.map((p) => (
                  <polygon key={p.id} points={p.points.map((pt) => `${pt.x * 100},${pt.y * 100}`).join(' ')}
                    fill="none" stroke="#111" strokeWidth={selected?.id === p.id ? 2.4 : 1.4}
                    style={{ stroke: selected?.id === p.id ? 'var(--ember-hot, #c96)' : '#111' }} vectorEffect="non-scaling-stroke" />
                ))}
              </svg>
              {/* Reading-order badges (RTL) — a small primary dot marks panels that
                  already have an AI script (description + matched cast). */}
              {panels.map((p, i) => {
                const c = centroid(p.points);
                return (
                  <span key={p.id} className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 rounded-full bg-black/55 px-1.5 text-[10px] font-bold text-white" style={{ left: `${c.x * 100}%`, top: `${c.y * 100}%` }}>
                    {i + 1}
                    {(p.script || panelScripts.has(p.id)) && <span className="absolute -right-1 -top-1 h-1.5 w-1.5 rounded-full bg-primary" />}
                  </span>
                );
              })}
              {/* Corner handles for the selected panel */}
              {selPanel && selPanel.points.length <= 8 && selPanel.points.map((pt, i) => (
                <div key={i} onPointerDown={(e) => startDrag(e, { mode: 'corner', id: selPanel.id, index: i, orig: pt })}
                  className="absolute z-20 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2 border-white bg-primary shadow"
                  style={{ left: `${pt.x * 100}%`, top: `${pt.y * 100}%` }} />
              ))}
              {/* Bubbles — ONE svg in PAGE PIXEL coordinates (viewBox = the page's
                  own width/height, preserveAspectRatio="none" matching the
                  container's own aspect ratio) so bodies/tails are real vector
                  paths that NEVER distort on resize, unlike a per-bubble
                  stretched 0..100 box. Passes across ALL bubbles (not
                  interleaved per-bubble) so "balloon chain" fusion/bridging
                  works: 1) every shape (real interactive hit target), grouped
                  so fused/bridged members sit together; 1b) connector bridges
                  (see `connectorPathsForGroup`), stroked; 2) a fill-only patch
                  for multi-member groups' bodies, erasing the internal seam;
                  2b) the same bridges again, fill-only, erasing the seam where
                  they meet each body; 3) every bubble's text + selection
                  handles, original array order (front/back layering among
                  DIFFERENT bubbles is unaffected by fusion/bridge grouping). */}
              <svg viewBox={`0 0 ${pageW} ${pageH}`} preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
                {bubbleGroups.flatMap((g) => g.map((idx) => {
                  const b = bubbles[idx];
                  return (
                    <BubbleShape key={`shape-${b.id}`} b={b} pageW={pageW} pageH={pageH} editing={editingBubbleId === b.id}
                      omitTail={tailOmitIds.has(b.id)}
                      onSelect={() => setSelected({ kind: 'bubble', id: b.id })}
                      onMove={(e) => startDrag(e, { mode: 'bubMove', id: b.id, orig: b })}
                      onEnterEdit={() => setEditingBubbleId(b.id)} />
                  );
                }))}
                {fusedGroups.flatMap(({ g, paths, style }) => paths.map((d, i) => (
                  <path key={`conn-s-${g[0]}-${i}`} d={d} fill={style.fill} stroke={style.stroke} strokeWidth={style.strokeWidth} strokeLinejoin="round" pointerEvents="none" />
                )))}
                {bubbleGroups.filter((g) => g.length > 1).flatMap((g) => g.map((idx) => {
                  const b = bubbles[idx];
                  return <BubbleShape key={`fuse-${b.id}`} b={b} pageW={pageW} pageH={pageH} editing={false} fillOnly omitTail={tailOmitIds.has(b.id)} />;
                }))}
                {fusedGroups.flatMap(({ g, paths, style }) => paths.map((d, i) => (
                  <path key={`conn-f-${g[0]}-${i}`} d={d} fill={style.fill} pointerEvents="none" />
                )))}
                {bubbles.map((b) => (
                  <BubbleTextHandles key={`text-${b.id}`} b={b} selected={selected?.id === b.id} editing={editingBubbleId === b.id}
                    pageW={pageW} pageH={pageH} canvasRef={canvasRef}
                    onSelect={() => setSelected({ kind: 'bubble', id: b.id })}
                    onResize={(e, corner) => startDrag(e, { mode: 'bubResize', id: b.id, index: corner, orig: b })}
                    onTail={(e) => startDrag(e, { mode: 'bubTail', id: b.id, orig: b })}
                    onRotate={(e) => startDrag(e, { mode: 'bubRotate', id: b.id, orig: b })}
                    onText={(txt) => setText(b.id, txt)}
                    onAutoSize={(size) => autoSizeBubble(b.id, size)}
                    onExitEdit={() => setEditingBubbleId(null)} />
                ))}
              </svg>
              {/* Magnetic-snap guide lines — shown while dragging/resizing a bubble */}
              {guides.map((g, i) => g.axis === 'x'
                ? <div key={i} className="pointer-events-none absolute inset-y-0 z-30 w-px bg-primary" style={{ left: `${g.pos * 100}%` }} />
                : <div key={i} className="pointer-events-none absolute inset-x-0 z-30 h-px bg-primary" style={{ top: `${g.pos * 100}%` }} />)}
            </div>
          ) : (
            <div className="text-center text-muted-foreground">
              <p className="mb-3">{t('manga.noPages', 'Nenhuma página ainda.')}</p>
              <button onClick={addPage} className="rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground">{t('manga.createFirstPage', 'Criar primeira página')}</button>
            </div>
          )}
        </div>

        {/* Page strip — horizontal bottom bar on mobile, vertical column on desktop */}
        <div className="flex shrink-0 gap-2 overflow-x-auto border-t border-border bg-card p-2 md:w-24 md:flex-col md:overflow-x-visible md:overflow-y-auto md:border-l md:border-t-0">
          <button onClick={addPage} disabled={busy === 'page'} className="flex w-16 shrink-0 items-center justify-center gap-1 rounded-lg border border-dashed border-border text-xs text-muted-foreground hover:bg-muted md:w-full" style={{ aspectRatio: '3 / 4' }}><Plus size={14} /></button>
          {orderedForStrip.map((p) => {
            const idx = pages.findIndex((x) => x.id === p.id);
            return (
              <div key={p.id} onClick={() => { setCurrentId(p.id); setSelected(null); }}
                className={cn('group relative w-16 shrink-0 cursor-pointer overflow-hidden rounded-lg border bg-white md:w-full', currentId === p.id ? 'border-primary ring-1 ring-primary' : 'border-border')}
                style={{ aspectRatio: '3 / 4' }}>
                <svg viewBox="0 0 100 133" className="h-full w-full" preserveAspectRatio="none">
                  {(p.layout.panels ?? []).map((pn, i) => (
                    <polygon key={i} points={pn.points.map((pt) => `${pt.x * 100},${pt.y * 133}`).join(' ')} fill={pn.imageUrl ? '#cfc8b8' : '#efeadd'} stroke="#111" strokeWidth={1.2} />
                  ))}
                </svg>
                <span className="absolute left-1 top-1 rounded bg-black/55 px-1 text-[9px] text-white">{idx + 1}</span>
                <button onClick={(e) => { e.stopPropagation(); removePage(p.id); }} className="absolute right-0.5 top-0.5 hidden rounded bg-destructive p-0.5 text-white group-hover:block"><X size={10} /></button>
              </div>
            );
          })}
        </div>

        {/* Conversational Ghostwriter — DESKTOP right-side panel (same slide-in
            AIChat the book editor uses). Mobile uses the Drawer below. */}
        {!isMobile && (
          <AnimatePresence>
            {chatOpen && mangaChatMode && (
              <AIChat
                chapterContent=""
                onApplyAction={() => {}}
                onClose={() => setChatOpen(false)}
                projectId={projectId ?? ''}
                manga={mangaChatMode}
              />
            )}
          </AnimatePresence>
        )}
      </div>

      {/* Conversational Ghostwriter — MOBILE drawer (vaul), mirrors editor-view. */}
      {isMobile && (
        <Drawer open={chatOpen && !!mangaChatMode} onOpenChange={(o) => { if (!o) setChatOpen(false); }}>
          <DrawerContent className="max-h-[90dvh] p-0 bg-card">
            <DrawerTitle className="sr-only">{t('ai.ghostwriter', 'Ghostwriter')}</DrawerTitle>
            {mangaChatMode && (
              <AIChat
                embedded
                chapterContent=""
                onApplyAction={() => {}}
                onClose={() => setChatOpen(false)}
                projectId={projectId ?? ''}
                manga={mangaChatMode}
              />
            )}
          </DrawerContent>
        </Drawer>
      )}

      {/* Status bar — mirrors the book editor's footer: glowing save-ping dot +
          label, then page context, then the reading-direction badge. Tokenized
          so it themes light/dark like the rest of the editor. */}
      <div className="flex flex-shrink-0 items-center gap-2 border-t border-border/60 bg-background px-3 py-2 font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground select-none md:gap-5 md:px-5 md:text-[9.5px] pb-[calc(0.5rem+env(safe-area-inset-bottom))] md:pb-2">
        <span className="flex shrink-0 items-center gap-2" style={{ color: saveStatus === 'unsaved' ? 'var(--ember)' : 'var(--aurora)' }}>
          <i key={saveStatus} className="save-ping h-1.5 w-1.5 shrink-0 rounded-full"
            style={{
              background: saveStatus === 'unsaved' ? 'var(--ember)' : 'var(--aurora)',
              boxShadow: `0 0 8px ${saveStatus === 'unsaved' ? 'var(--ember)' : 'var(--aurora)'}`,
            }} />
          {saveStatus === 'saved' ? t('editor.saved', 'Salvo') : saveStatus === 'saving' ? t('editor.saving', 'Salvando...') : t('editor.unsaved', 'Não salvo')}
        </span>
        {current && (
          <span className="hidden shrink-0 truncate sm:inline">
            {t('manga.scriptPageContext', 'Página {{n}} de {{total}} · {{q}} quadros · {{b}} balões', {
              n: Math.max(1, pages.findIndex((p) => p.id === currentId) + 1), total: pages.length,
              q: current.layout.panels.length, b: current.layout.bubbles.length,
            })}
          </span>
        )}
        <span className="ml-auto shrink-0" style={{ color: 'var(--ember)' }}>
          {dir === 'rtl' ? t('manga.rtlBadge', '← direita p/ esquerda') : t('manga.ltrShort', 'LTR →')}
        </span>
      </div>

      {/* Reader — full-screen internal preview. Respects reading direction: in RTL
          the LEFT side / left-arrow advances (like a real manga reader). */}
      {readerIdx !== null && pages[readerIdx] && (() => {
        const panelCount = pages[readerIdx].layout.panels.length;
        // Only an in-range panel index counts as "focused" for display — the
        // -1/panelCount sentinels `readerStep` also stores in `panelFocus`
        // both mean "whole page" (see the comment on `readerStep`).
        const focusIndex = readerGuided && panelFocus !== null && panelFocus >= 0 && panelFocus < panelCount ? panelFocus : null;
        return (
        <div className="fixed inset-0 z-[120] flex flex-col bg-[#0b0b0f]">
          <div className="flex items-center justify-between px-4 py-2 text-white/80">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em]">
              {t('manga.readerTitle', 'Leitura')} {dir === 'rtl' && <span className="ml-2 rounded bg-white/10 px-1.5 py-0.5">{t('manga.rtlBadge', '← direita p/ esquerda')}</span>}
            </span>
            <div className="flex items-center gap-3">
              {readerGuided && focusIndex !== null && (
                <span className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-[10px]">
                  {t('manga.panelCounter', 'Quadro {{n}}/{{m}}', { n: focusIndex + 1, m: panelCount })}
                </span>
              )}
              <button onClick={() => { setReaderGuided((g) => !g); setPanelFocus(null); }}
                title={readerGuided ? t('manga.guidedOff', 'Ver página inteira') : t('manga.guidedOn', 'Ler quadro a quadro')}
                className={cn('rounded-lg px-2 py-1 font-mono text-[10px] uppercase tracking-wider transition', readerGuided ? 'bg-primary text-primary-foreground' : 'bg-white/10 hover:bg-white/20')}>
                {t('manga.guided', 'Quadro a quadro')}
              </button>
              <button onClick={toggleReaderAnim}
                title={t('manga.animsTitle', 'Animar a entrada dos balões e do texto')}
                className={cn('rounded-lg px-2 py-1 font-mono text-[10px] uppercase tracking-wider transition', readerAnim ? 'bg-primary text-primary-foreground' : 'bg-white/10 hover:bg-white/20')}>
                {t('manga.anims', 'Animações')}
              </button>
              <span className="font-mono text-xs">{readerIdx + 1} / {pages.length}</span>
              <button onClick={() => { cancelPageTurn(); setReaderIdx(null); setPanelFocus(null); }} className="rounded-full p-1.5 hover:bg-white/10" aria-label={t('common.close', 'Fechar')}><X size={16} /></button>
            </div>
          </div>
          <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden p-2 md:p-4">
            {pageTurn ? (
              // Page-turn overlay — Moleskine-style 3D flip (see the
              // `.manga-flip-*` layout classes in styles.css and their 1:1
              // twin, READER_CSS, in manga-export.ts; the actual transform/
              // box-shadow/shade numbers are owned by `runPageFlip`,
              // src/lib/page-flip.ts, driven by the `pageTurn` effect above).
              // The INCOMING page is real, live DOM, mounted UNDERNEATH in
              // normal flow from this very render — a page is on screen
              // every frame, nothing here is ever rasterized or awaited. The
              // OUTGOING (current) page's own live DOM is duplicated into
              // the rotating sheet's front face (`flipSheetRef`) and spun
              // over the incoming page by `runPageFlip`.
              <div className="manga-flip-stage">
                <div className="manga-flip-slot manga-flip-under">
                  <ReaderPage ref={flipIncomingRef} page={pages[readerIdx + pageTurn.dir]} focusIndex={null} animate={false} />
                </div>
                <div className="manga-flip-slot manga-flip-sheet-slot">
                  <div
                    ref={flipSheetRef}
                    className={cn(READER_PAGE_BOX_CLASS, 'manga-flip-sheet')}
                    style={{ aspectRatio: `${pages[readerIdx].width || 1000} / ${pages[readerIdx].height || 1414}` }}>
                    <div className="manga-flip-face manga-flip-face-front">
                      <ReaderPage page={pages[readerIdx]} focusIndex={null} animate={false} boxClassName="absolute inset-0" />
                      <div ref={flipShadeRef} className="manga-flip-shade" />
                    </div>
                    <div className="manga-flip-face manga-flip-face-back" />
                  </div>
                </div>
              </div>
            ) : (
              <ReaderPage page={pages[readerIdx]} focusIndex={focusIndex} animate={readerAnim} />
            )}
            {/* click zones — advance side follows the reading direction (`dir`):
                LTR comics advance on the RIGHT, RTL manga on the LEFT (navFlip).
                Disabled (pointer-events-none) while a page-turn animation is in
                flight. */}
            <button aria-label={navFlip ? t('manga.back', 'Voltar') : t('manga.forward', 'Avançar')}
              onClick={() => readerStep(navFlip ? -1 : 1)}
              className={cn('group absolute inset-y-0 right-0 w-1/3', pageTurn && 'pointer-events-none')}>
              <ChevronRight size={30} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/0 transition group-hover:text-white/50" />
            </button>
            <button aria-label={navFlip ? t('manga.forward', 'Avançar') : t('manga.back', 'Voltar')}
              onClick={() => readerStep(navFlip ? 1 : -1)}
              className={cn('group absolute inset-y-0 left-0 w-1/3', pageTurn && 'pointer-events-none')}>
              <ChevronLeft size={30} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/0 transition group-hover:text-white/50" />
            </button>
          </div>
          {/* progress — fills toward the reading direction */}
          <div className="h-1 w-full bg-white/10">
            <div className="h-full bg-primary transition-all" style={{ width: `${((readerIdx + 1) / pages.length) * 100}%`, marginLeft: navFlip ? 'auto' : undefined }} />
          </div>
        </div>
        );
      })()}

      {/* Generate-panel modal — scene description + pick the cast/settings from the
          Universe so their locked reference sheets keep the art consistent. */}
      {genModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" onClick={() => busy !== 'gen' && setGenModal(null)}>
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-1 font-serif text-lg font-bold text-foreground">{t('manga.genPanelTitle', 'Gerar arte do quadro')}</h3>
            <p className="mb-3 text-xs text-muted-foreground">{t('manga.genPanelHint', 'Descreva a cena e escolha quem/onde aparece — as referências do Universo mantêm tudo consistente.')}</p>
            <textarea autoFocus value={genModal.prompt} onChange={(e) => setGenModal((m) => m && { ...m, prompt: e.target.value })}
              placeholder={t('manga.genPanelPlaceholder', 'Ex: Kaito encara o rival na chuva, close no rosto…')}
              rows={3} className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-primary" />
            {genModalPanel?.sketchUrl && (
              <div className="mt-3 flex items-center gap-2.5 rounded-lg border border-border bg-muted/40 p-2">
                <img src={genModalPanel.sketchUrl} alt="" className="h-12 w-12 shrink-0 rounded object-cover grayscale opacity-80" />
                <label className="flex flex-1 cursor-pointer items-center gap-2 text-xs text-foreground">
                  <input type="checkbox" checked={genModal.useSketch}
                    onChange={(e) => setGenModal((m) => m && { ...m, useSketch: e.target.checked })}
                    className="h-3.5 w-3.5 accent-primary" />
                  {t('manga.useSketchAsBase', 'Usar rabisco como base')}
                </label>
              </div>
            )}
            {cast.length > 0 ? (
              <div className="mt-3">
                <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t('manga.genPanelCast', 'Personagens / cenários na cena')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {cast.map((e) => {
                    const on = genModal.refs.has(e.id);
                    return (
                      <button key={e.id} onClick={() => setGenModal((m) => { if (!m) return m; const refs = new Set(m.refs); if (on) refs.delete(e.id); else refs.add(e.id); return { ...m, refs }; })}
                        className={cn('flex items-center gap-1.5 rounded-full border px-2 py-1 text-xs transition', on ? 'border-primary bg-primary/10 text-primary' : 'border-border text-foreground hover:bg-muted', !e.imageUrl && 'opacity-70')}
                        title={e.imageUrl ? '' : t('manga.genPanelNoRef', 'Sem referência — gere a imagem no Universo pra travar a aparência')}>
                        {e.imageUrl ? <img src={e.imageUrl} alt="" className="h-4 w-4 rounded-full object-cover" /> : <span className="h-4 w-4 rounded-full bg-muted" />}
                        {e.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="mt-3 text-[11px] text-muted-foreground">{t('manga.genPanelEmptyCast', 'Dica: crie personagens no Universo (com imagem de referência) para gerar painéis consistentes.')}</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setGenModal(null)} disabled={busy === 'gen'} className="rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-muted disabled:opacity-40">{t('common.cancel', 'Cancelar')}</button>
              <button onClick={runGen} disabled={busy === 'gen'} className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                {busy === 'gen' ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}{t('manga.genPanelGo', 'Gerar')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * A bubble node inside the EDITOR's shared page-space `<svg>` (see
 * `MangaEditor`'s canvas render). Draws a single vector path (body + tail —
 * `generateBubblePath`/`toPathBubble` for the standard types, or a smoothed
 * point-drawn body + ray-cast tail for `b.shape`), the text as an
 * editable `<foreignObject>`, and — while selected — the corner (resize
 * from center) and tail drag handles, all in real PAGE PIXELS so nothing
 * ever gets stretched/distorted.
 */
interface BubbleGeometry {
  boxX: number; boxY: number; boxW: number; boxH: number;
  cx: number; cy: number; tailX: number; tailY: number;
  pathD: string;
  thoughtCircles: { cx: number; cy: number; r: number }[];
  customTail: { poly: string; outline: string } | null;
  foX: number; foY: number; foW: number; foH: number;
  fontPx: number; scale: number;
}

/**
 * Shared geometry math for a bubble's body/tail/text-box in PAGE PIXELS — the
 * single source of truth used by BOTH the editable `BubbleShape`/
 * `BubbleTextHandles` (canvas) and the read-only `ReaderBubbleShape`/
 * `ReaderBubbleText` (internal reader), so they ALWAYS draw the exact same
 * shape. Either the vector system (`generateBubblePath`, for the standard
 * types) or, for point-drawn shapes, a smoothed path through the
 * (px-converted) body points with a ray-cast tail from its centroid.
 */
function computeBubbleGeometry(b: MangaBubble, pageW: number, pageH: number, omitTail = false): BubbleGeometry {
  const boxX = b.x * pageW, boxY = b.y * pageH, boxW = b.w * pageW, boxH = b.h * pageH;
  const cx = boxX + boxW / 2, cy = boxY + boxH / 2;
  // Kept as the REAL stored tail position regardless of `omitTail` — the tail
  // drag handle (`BubbleTextHandles`) still needs it to stay interactive even
  // while the body's tail is visually suppressed (see `computeTailOmissions`),
  // so the author can keep adjusting where it WOULD point.
  const tailX = cx + (b.tail?.x ?? 0) * pageW;
  const tailY = cy + (b.tail?.y ?? 0) * pageH;
  const scale = pageW / 800;
  const hasCustomShape = !!(b.shape && b.shape.body.length >= 3);

  let pathD = '';
  let thoughtCircles: { cx: number; cy: number; r: number }[] = [];
  let customTail: { poly: string; outline: string } | null = null;
  if (b.type === 'sfx') {
    // No body at all — 'sfx' is giant text only, see `computeSfxLayout` and
    // the dedicated 'sfx' branch in `BubbleShape`/`BubbleTextHandles` below.
  } else if (hasCustomShape) {
    const pts = b.shape!.body.map((p) => ({ x: boxX + p.x * boxW, y: boxY + p.y * boxH }));
    pathD = smoothClosedPath(pts);
    if (!omitTail) {
      const c = centroidOf(pts);
      const ang = Math.atan2(tailY - c.y, tailX - c.x);
      if (b.type === 'thought') {
        const p0 = rayPolygonExit(c, ang, pts);
        thoughtCircles = [0.55, 0.78, 1].map((tt, i) => ({
          cx: p0.x + (tailX - p0.x) * tt, cy: p0.y + (tailY - p0.y) * tt, r: (7 - i * 2) * scale,
        }));
      } else if (b.type !== 'narration' && b.type !== 'caption' && b.type !== 'shout') {
        const a1 = rayPolygonExit(c, ang - 0.35, pts), a2 = rayPolygonExit(c, ang + 0.35, pts);
        customTail = {
          poly: `${a1.x},${a1.y} ${tailX},${tailY} ${a2.x},${a2.y}`,
          outline: `M ${a1.x} ${a1.y} L ${tailX} ${tailY} L ${a2.x} ${a2.y}`,
        };
      }
    }
  } else {
    const pb = toPathBubble(b, pageW, pageH, omitTail);
    pathD = generateBubblePath(pb);
    if (pb.type === 'thought') thoughtCircles = thoughtTailCircles(pb);
  }

  const padPx = b.padding ?? defaultBubblePaddingPx(b, pageW);
  const foX = boxX + padPx, foY = boxY + padPx;
  const foW = Math.max(4, boxW - 2 * padPx), foH = Math.max(4, boxH - 2 * padPx);
  const fontPx = b.fontSize ?? defaultBubbleFontPx(b, pageW);

  return { boxX, boxY, boxW, boxH, cx, cy, tailX, tailY, pathD, thoughtCircles, customTail, foX, foY, foW, foH, fontPx, scale };
}

/**
 * Shape-only half of a bubble — body/tail/thought-circles, the REAL
 * interactive hit target (select/move/enter-edit). Split from the text/
 * handles half (`BubbleTextHandles`) so "balloon chain" fusion (see
 * `groupJoinedBubbles`) can render every group member's shape, THEN a
 * fill-only patch for every member (`fillOnly`), THEN every member's text —
 * three clean passes across bubbles, not interleaved per-bubble, which is
 * the only way the fill-only patch can erase an internal seam WITHOUT also
 * painting over some other member's already-drawn text. `editing` is now
 * OWNED BY THE PARENT (not local state) precisely so this half and the text
 * half — separate component instances for the same bubble — stay in sync.
 */
function BubbleShape({ b, pageW, pageH, editing, fillOnly, omitTail, onSelect, onMove, onEnterEdit }: {
  b: MangaBubble; pageW: number; pageH: number; editing: boolean; fillOnly?: boolean; omitTail?: boolean;
  onSelect?: () => void; onMove?: (e: React.PointerEvent) => void; onEnterEdit?: () => void;
}) {
  const st = resolveBubbleStyle(b);
  // ROTATION — universal (any bubble type), applied to the WHOLE visual
  // group (body + tail + thought-circles here; text is handled the same way
  // in `BubbleTextHandles`) via an SVG <g transform="rotate(...)"> around the
  // box's own center. DESIGN NOTE (scope kept deliberately contained): the
  // resize/side/tail drag HANDLES (rendered by `BubbleTextHandles`) stay on
  // the UN-rotated, axis-aligned box instead of rotating with the body — that
  // keeps every drag gesture a simple 1:1 mouse-delta → w/h/tail update, no
  // inverse-rotation math needed. The tradeoff: handles sit on the bounding
  // box, not flush against a heavily-rotated body's own edges — an accepted,
  // much simpler UX than fully rotated interaction.
  const rotation = b.rotation ?? 0;

  // 'sfx' has NO visual body — just a transparent hit-box for select/move/
  // enter-edit (it never joins a fusion group, so `fillOnly` never applies).
  if (b.type === 'sfx') {
    if (fillOnly) return null;
    const boxX = b.x * pageW, boxY = b.y * pageH, boxW = Math.max(1, b.w * pageW), boxH = Math.max(1, b.h * pageH);
    const cx = boxX + boxW / 2, cy = boxY + boxH / 2;
    return (
      <g transform={rotation ? `rotate(${rotation} ${cx} ${cy})` : undefined}>
        <rect x={boxX} y={boxY} width={boxW} height={boxH} fill="transparent"
          className={editing ? 'cursor-text' : 'cursor-move'}
          onPointerDown={(e) => { if (editing) return; onSelect?.(); onMove?.(e); }}
          onDoubleClick={(e) => { e.stopPropagation(); onSelect?.(); onEnterEdit?.(); }} />
      </g>
    );
  }

  const { cx, cy, pathD, customTail, thoughtCircles } = computeBubbleGeometry(b, pageW, pageH, omitTail);
  return (
    <g transform={rotation ? `rotate(${rotation} ${cx} ${cy})` : undefined}>
      <g pointerEvents={fillOnly ? 'none' : undefined}>
        <path
          d={pathD}
          fill={st.fill}
          stroke={fillOnly ? 'none' : st.stroke}
          strokeWidth={st.strokeWidth}
          strokeDasharray={!fillOnly && b.type === 'whisper' ? '6 6' : undefined}
          strokeLinejoin="round"
          strokeLinecap="round"
          className={fillOnly ? undefined : (editing ? 'cursor-text' : 'cursor-move')}
          style={fillOnly ? undefined : { filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.12))' }}
          onPointerDown={fillOnly ? undefined : (e) => { if (editing) return; onSelect?.(); onMove?.(e); }}
          onDoubleClick={fillOnly ? undefined : (e) => { e.stopPropagation(); onSelect?.(); onEnterEdit?.(); }}
        />
        {customTail && (
          <>
            {/* fill hides the body's edge under the tail, then stroke only the two sides */}
            <polygon points={customTail.poly} fill={st.fill} />
            {!fillOnly && <path d={customTail.outline} fill="none" stroke={st.stroke} strokeWidth={st.strokeWidth} strokeLinejoin="round" strokeLinecap="round" />}
          </>
        )}
        {!fillOnly && thoughtCircles.map((c2, i) => (
          <circle key={i} cx={c2.cx} cy={c2.cy} r={Math.max(3, c2.r)} fill={st.fill} stroke={st.stroke} strokeWidth={st.strokeWidth} />
        ))}
      </g>
    </g>
  );
}

/**
 * Text + selection-handles half of a bubble — the editable `<foreignObject>`
 * (or, for 'sfx', giant stroked `<text>` lines instead — no foreignObject at
 * all), the script-speaker debug label, and (while selected) the resize/tail
 * drag handles. Pairs with `BubbleShape` (see its docstring for why they're
 * split). `editing` is lifted state from the parent — `onExitEdit` is how
 * this half hands control back after blur.
 */
function BubbleTextHandles({ b, selected, editing, pageW, pageH, canvasRef, onSelect, onResize, onTail, onRotate, onText, onAutoSize, onExitEdit }: {
  b: MangaBubble; selected: boolean; editing: boolean; pageW: number; pageH: number;
  canvasRef: { current: HTMLDivElement | null };
  onSelect: () => void;
  onResize: (e: React.PointerEvent, corner: number) => void; onTail: (e: React.PointerEvent) => void;
  onRotate: (e: React.PointerEvent) => void;
  onText: (t: string) => void; onAutoSize: (size: { w?: number; h?: number }) => void; onExitEdit: () => void;
}) {
  const { t } = useTranslation();
  const textRef = useRef<HTMLDivElement>(null);
  const autoSizeFrame = useRef<number | null>(null);

  // Cancel any pending auto-size rAF on unmount.
  useEffect(() => () => { if (autoSizeFrame.current) cancelAnimationFrame(autoSizeFrame.current); }, []);
  // Focus + select-all the moment this bubble enters edit mode (triggered by
  // a double-click on the SHAPE half, which lives in a different component).
  useEffect(() => {
    if (!editing) return;
    requestAnimationFrame(() => {
      const el = textRef.current; if (!el) return;
      el.focus();
      const r = document.createRange(); r.selectNodeContents(el);
      const sel = window.getSelection(); sel?.removeAllRanges(); sel?.addRange(r);
    });
  }, [editing]);
  // Liquid auto-size: grows the bubble (never shrinks mid-edit) so the text
  // always fits — ONLY while no explicit `fontSize` is set (the style panel's
  // slider); an explicit size clamps/clips the text instead, like the reference.
  // `canvasRef`'s on-screen rect gives the px-per-fraction scale directly
  // (fraction w of the whole canvas == fraction w of its on-screen pixels) —
  // no per-bubble DOM ref needed.
  const runAutoSize = (text: string) => {
    if (b.fontSize !== undefined) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const syntheticBoxRect = { width: b.w * rect.width, height: b.h * rect.height } as DOMRect;
    const size = computeAutoSize(text, b.type, b.w, b.h, syntheticBoxRect, pageW, b.fontSize, b.padding, b.fontFamily);
    if (size) onAutoSize(size);
  };
  const onTextInput = (e: React.FormEvent<HTMLDivElement>) => {
    const text = e.currentTarget.textContent || '';
    if (autoSizeFrame.current) cancelAnimationFrame(autoSizeFrame.current);
    autoSizeFrame.current = requestAnimationFrame(() => runAutoSize(text));
  };

  const st = resolveBubbleStyle(b);
  const { boxX, boxY, boxW, boxH, cx, cy, tailX, tailY, foX, foY, foW, foH, fontPx: preferredFontPx, scale } =
    computeBubbleGeometry(b, pageW, pageH);
  // Fit-to-box: `preferredFontPx` (explicit b.fontSize, or the default clamp)
  // is a CEILING — the text only shrinks from it, down to a floor, so it never
  // gets cropped/suppressed. The liquid auto-size above is the only thing that
  // ever changes w/h; this never writes back to state, purely a render-time size.
  const fittedFontPx = useMemo(
    () => fitBubbleFontPx(b.text, foW, foH, resolveFontFamily(b.type, b.fontFamily), String(bubbleFontWeight(b)), bubbleFontStyleCss(b), preferredFontPx),
    [b.text, foW, foH, b.type, b.fontFamily, b.fontStyle, preferredFontPx],
  );

  // Handles are rendered on the UN-rotated, axis-aligned box regardless of
  // `b.rotation` (see the rotation design note in `BubbleShape`) — simple
  // straight overlays instead of chasing a rotated silhouette.
  const cornerHandles = selected && (
    <>
      {/* 4 corner handles — resize anchored to the OPPOSITE corner (design-tool
          convention): the grabbed corner follows the mouse, the diagonal one
          stays put. Index order (0=TL,1=TR,2=BL,3=BR) is passed to onResize
          so `onMove`'s bubResize branch knows which corner is fixed. */}
      {[
        { hx: boxX, hy: boxY, cursor: 'nwse-resize' }, // 0 TL
        { hx: boxX + boxW, hy: boxY, cursor: 'nesw-resize' }, // 1 TR
        { hx: boxX, hy: boxY + boxH, cursor: 'nesw-resize' }, // 2 BL
        { hx: boxX + boxW, hy: boxY + boxH, cursor: 'nwse-resize' }, // 3 BR
      ].map((h, i) => (
        <rect key={i} x={h.hx - 6 * scale} y={h.hy - 6 * scale} width={12 * scale} height={12 * scale}
          fill="#0ea5e9" stroke="#ffffff" strokeWidth={1.5} style={{ cursor: h.cursor }}
          onPointerDown={(e) => { e.stopPropagation(); onSelect(); onResize(e, i); }} />
      ))}
    </>
  );

  // 4 SIDE handles (N/S/E/W, index 4-7) — resize a SINGLE axis (height-only
  // for N/S, width-only for E/W), the opposite edge/whole other axis stays
  // fixed (see `onMove`'s `bubResize` branch for the math).
  const sideHandles = selected && (
    <>
      {[
        { hx: boxX + boxW / 2, hy: boxY, idx: 4, cursor: 'ns-resize', w: 16, h: 8 }, // N
        { hx: boxX + boxW / 2, hy: boxY + boxH, idx: 5, cursor: 'ns-resize', w: 16, h: 8 }, // S
        { hx: boxX + boxW, hy: boxY + boxH / 2, idx: 6, cursor: 'ew-resize', w: 8, h: 16 }, // E
        { hx: boxX, hy: boxY + boxH / 2, idx: 7, cursor: 'ew-resize', w: 8, h: 16 }, // W
      ].map((h) => (
        <rect key={h.idx} x={h.hx - (h.w * scale) / 2} y={h.hy - (h.h * scale) / 2} width={h.w * scale} height={h.h * scale}
          rx={2 * scale} fill="#0ea5e9" stroke="#ffffff" strokeWidth={1.5} style={{ cursor: h.cursor }}
          onPointerDown={(e) => { e.stopPropagation(); onSelect(); onResize(e, h.idx); }} />
      ))}
    </>
  );

  // Rotation drag handle — a small dot ~30px above the box's own top edge
  // (on screen), connected by a thin line, itself rotated by the bubble's
  // CURRENT rotation (via `rotatePoint`, same matrix SVG's own rotate()
  // transform uses) so it stays glued to the top of the spinning bubble —
  // universal, works for every bubble type (sfx included).
  const rotation = b.rotation ?? 0;
  const rotAnchor = rotatePoint(cx, boxY, cx, cy, rotation);
  const rotHandlePt = rotatePoint(cx, boxY - 30 * scale, cx, cy, rotation);
  const rotateHandle = selected && (
    <g>
      <line x1={rotAnchor.x} y1={rotAnchor.y} x2={rotHandlePt.x} y2={rotHandlePt.y}
        stroke="#0ea5e9" strokeWidth={1.5} pointerEvents="none" />
      <circle cx={rotHandlePt.x} cy={rotHandlePt.y} r={7 * scale}
        fill="#0ea5e9" stroke="#ffffff" strokeWidth={2} style={{ cursor: 'grab' }}
        onPointerDown={(e) => { e.stopPropagation(); onSelect(); onRotate(e); }}>
        <title>{t('manga.rotateHandle', 'Girar')}</title>
      </circle>
    </g>
  );

  // 'sfx' — giant display text, no foreignObject/balloon body: big stroked
  // SVG <text>, one per line, rotated around the box's own center via a
  // NESTED <g transform="rotate(...)"> (SVG presentation attribute, kept
  // separate from this <g>'s own no animation here in the editor, but the
  // same split as the exports so all four renderers agree pixel-for-pixel —
  // see `computeSfxLayout`'s docstring). No tail handle (sfx has no tail).
  if (b.type === 'sfx') {
    const layout = computeSfxLayout(b, pageW, pageH);
    const lineHeightPx = layout.fontSizePx * 0.95;
    const totalH = lineHeightPx * layout.lines.length;
    const startY = layout.cy - totalH / 2 + lineHeightPx / 2;
    return (
      <g>
        <g transform={`rotate(${layout.rotation} ${layout.cx} ${layout.cy})`} pointerEvents="none">
          {layout.lines.map((line, i) => (
            <text key={i} x={layout.cx} y={startY + i * lineHeightPx}
              textAnchor="middle" dominantBaseline="middle"
              fontFamily={layout.fontFamily} fontSize={layout.fontSizePx}
              fill={layout.fill} stroke={layout.stroke} strokeWidth={layout.strokeWidth}
              style={{ paintOrder: 'stroke', strokeLinejoin: 'round' }}>{line}</text>
          ))}
        </g>
        {selected && (
          <g className="interactive-handles">
            <g transform={rotation ? `rotate(${rotation} ${cx} ${cy})` : undefined} pointerEvents="none">
              <rect x={boxX} y={boxY} width={boxW} height={boxH} fill="none" stroke="#0ea5e9" strokeWidth={2} strokeDasharray="6 6" />
            </g>
            {cornerHandles}
            {sideHandles}
            {rotateHandle}
          </g>
        )}
      </g>
    );
  }

  return (
    <g>
      <g transform={rotation ? `rotate(${rotation} ${cx} ${cy})` : undefined}>
        <foreignObject x={foX} y={foY} width={foW} height={foH} style={{ pointerEvents: editing ? 'auto' : 'none' }}>
          <div
            ref={textRef}
            contentEditable={editing}
            suppressContentEditableWarning
            onPointerDown={(e) => { if (editing) e.stopPropagation(); }}
            onInput={onTextInput}
            onBlur={(e) => {
              const text = e.currentTarget.textContent || '';
              onText(text);
              runAutoSize(text);
              onExitEdit();
            }}
            className={cn('flex h-full w-full items-center overflow-hidden outline-none', bubbleAlignClasses(b.align))}
            style={{
              fontFamily: resolveFontFamily(b.type, b.fontFamily),
              color: st.textColor,
              fontWeight: bubbleFontWeight(b),
              fontStyle: bubbleFontStyleCss(b),
              textTransform: b.type === 'shout' ? 'uppercase' : 'none',
              letterSpacing: b.type === 'shout' ? '0.02em' : 0,
              fontSize: `${fittedFontPx}px`,
              lineHeight: BUBBLE_LINE_HEIGHT,
              wordBreak: 'break-word',
            }}
          >{b.text}</div>
        </foreignObject>
      </g>
      {/* Who says this line — from the page script (editor-only, never printed). */}
      {selected && b.speaker && (
        <text x={cx} y={boxY + boxH + 13 * scale} textAnchor="middle" fontSize={9 * scale} fontWeight={700}
          fill="var(--ember-hot, #c96)" style={{ pointerEvents: 'none' }}>{b.speaker}</text>
      )}
      {selected && (
        <g className="interactive-handles">
          <g transform={rotation ? `rotate(${rotation} ${cx} ${cy})` : undefined} pointerEvents="none">
            <rect x={boxX} y={boxY} width={boxW} height={boxH} fill="none" stroke="#0ea5e9" strokeWidth={2} strokeDasharray="6 6" />
          </g>
          {cornerHandles}
          {sideHandles}
          {rotateHandle}
          {/* Tail handle + dashed guide line from the center to the tip (thought gets a distinct color) */}
          <line x1={cx} y1={cy} x2={tailX} y2={tailY}
            stroke={b.type === 'thought' ? '#6366f1' : '#f59e0b'} strokeWidth={1.5} strokeDasharray="3 3" pointerEvents="none" />
          <circle cx={tailX} cy={tailY} r={9 * scale}
            fill={b.type === 'thought' ? '#6366f1' : '#f59e0b'} stroke="#ffffff" strokeWidth={2.5}
            style={{ cursor: 'crosshair' }}
            onPointerDown={(e) => { e.stopPropagation(); onSelect(); onTail(e); }} />
        </g>
      )}
    </g>
  );
}

/**
 * Read-only SHAPE half of a bubble for the internal reader — SAME geometry as
 * the editable `BubbleShape` (via `computeBubbleGeometry`), no handles, no
 * editing. `fillOnly` renders pass 2 of "balloon chain" fusion (see
 * `groupJoinedBubbles`) — a static, non-animated fill-only patch on top of
 * the group's shapes. Split from `ReaderBubbleText` for the same reason as
 * the editor: the fusion patch must sit strictly between ALL of a group's
 * shapes and ALL of its text.
 */
function ReaderBubbleShape({ b, pageW, pageH, animDelay, fillOnly, omitTail }: { b: MangaBubble; pageW: number; pageH: number; animDelay?: number; fillOnly?: boolean; omitTail?: boolean }) {
  const st = resolveBubbleStyle(b);
  const animated = !fillOnly && animDelay !== undefined;
  const groupStyle = animated ? {
    transformBox: 'view-box' as const, transformOrigin: `${b.x * pageW + (b.w * pageW) / 2}px ${b.y * pageH + (b.h * pageH) / 2}px`,
    animation: 'bubble-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both', animationDelay: `${animDelay}s`,
  } : undefined;

  // 'sfx' — no body at all (see `BubbleShape`'s editor twin); it never joins
  // a fusion group, so the fill-only pass is simply skipped.
  if (b.type === 'sfx') {
    if (fillOnly) return null;
    const layout = computeSfxLayout(b, pageW, pageH);
    const lineHeightPx = layout.fontSizePx * 0.95;
    const totalH = lineHeightPx * layout.lines.length;
    const startY = layout.cy - totalH / 2 + lineHeightPx / 2;
    return (
      <g style={groupStyle}>
        <g transform={`rotate(${layout.rotation} ${layout.cx} ${layout.cy})`}>
          {layout.lines.map((line, i) => (
            <text key={i} x={layout.cx} y={startY + i * lineHeightPx}
              textAnchor="middle" dominantBaseline="middle"
              fontFamily={layout.fontFamily} fontSize={layout.fontSizePx}
              fill={layout.fill} stroke={layout.stroke} strokeWidth={layout.strokeWidth}
              style={{ paintOrder: 'stroke', strokeLinejoin: 'round' }}>{line}</text>
          ))}
        </g>
      </g>
    );
  }

  const { cx, cy, pathD, customTail, thoughtCircles } = computeBubbleGeometry(b, pageW, pageH, omitTail);
  const rotation = b.rotation ?? 0;
  return (
    // Same split as 'sfx' above (and every renderer): the OUTER <g> owns the
    // entrance pop animation (CSS transform: scale, via `groupStyle`), the
    // INNER <g> owns the static rotation (SVG transform="rotate(...)") — two
    // different transform mechanisms on the same element would fight, so
    // they're never on the same node.
    <g style={groupStyle}>
      <g transform={rotation ? `rotate(${rotation} ${cx} ${cy})` : undefined} pointerEvents={fillOnly ? 'none' : undefined}>
        <path
          d={pathD}
          fill={st.fill}
          stroke={fillOnly ? 'none' : st.stroke}
          strokeWidth={st.strokeWidth}
          strokeDasharray={!fillOnly && b.type === 'whisper' ? '6 6' : undefined}
          strokeLinejoin="round"
          strokeLinecap="round"
          style={fillOnly ? undefined : { filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.12))' }}
        />
        {customTail && (
          <>
            {/* fill hides the body's edge under the tail, then stroke only the two sides */}
            <polygon points={customTail.poly} fill={st.fill} />
            {!fillOnly && <path d={customTail.outline} fill="none" stroke={st.stroke} strokeWidth={st.strokeWidth} strokeLinejoin="round" strokeLinecap="round" />}
          </>
        )}
        {!fillOnly && thoughtCircles.map((c2, i) => (
          <circle key={i} cx={c2.cx} cy={c2.cy} r={Math.max(3, c2.r)} fill={st.fill} stroke={st.stroke} strokeWidth={st.strokeWidth} />
        ))}
      </g>
    </g>
  );
}

/** Read-only TEXT half of a bubble for the internal reader — pairs with
 * `ReaderBubbleShape`. 'sfx' has no foreignObject text layer at all (its
 * text IS its shape, drawn entirely in `ReaderBubbleShape`). Always the FULL
 * `b.text` — NO typewriter/progressive reveal; the reader's entrance queue
 * (see `useReaderQueue`) shows it all at once with a `bubble-text-in`
 * fade-in (opacity + a light translateY) when `animDelay` is set. */
function ReaderBubbleText({ b, pageW, pageH, animDelay }: { b: MangaBubble; pageW: number; pageH: number; animDelay?: number }) {
  if (b.type === 'sfx') return null;
  const st = resolveBubbleStyle(b);
  const { cx, cy, foX, foY, foW, foH, fontPx: preferredFontPx } = computeBubbleGeometry(b, pageW, pageH);
  const fittedFontPx = useMemo(
    () => fitBubbleFontPx(b.text, foW, foH, resolveFontFamily(b.type, b.fontFamily), String(bubbleFontWeight(b)), bubbleFontStyleCss(b), preferredFontPx),
    [b.text, foW, foH, b.type, b.fontFamily, b.fontStyle, preferredFontPx],
  );
  const animated = animDelay !== undefined;
  const rotation = b.rotation ?? 0;
  return (
    <g style={animated ? { transformBox: 'view-box', transformOrigin: `${cx}px ${cy}px` } : undefined}>
      {/* Rotation on its OWN nested <g> — same reasoning as `ReaderBubbleShape`
          (never mix the CSS entrance animation and the static SVG rotate on
          one node). */}
      <g transform={rotation ? `rotate(${rotation} ${cx} ${cy})` : undefined}>
      <foreignObject x={foX} y={foY} width={foW} height={foH} style={{ pointerEvents: 'none' }}>
        <div
          className={cn('flex h-full w-full items-center overflow-hidden outline-none', bubbleAlignClasses(b.align))}
          style={{
            fontFamily: resolveFontFamily(b.type, b.fontFamily),
            color: st.textColor,
            fontWeight: bubbleFontWeight(b),
            fontStyle: bubbleFontStyleCss(b),
            textTransform: b.type === 'shout' ? 'uppercase' : 'none',
            letterSpacing: b.type === 'shout' ? '0.02em' : 0,
            fontSize: `${fittedFontPx}px`,
            lineHeight: BUBBLE_LINE_HEIGHT,
            wordBreak: 'break-word',
            ...(animated ? { animation: 'bubble-text-in 0.3s ease both', animationDelay: `${animDelay + 0.22}s` } : {}),
          }}
        >{b.text}</div>
      </foreignObject>
      </g>
    </g>
  );
}

// ── Reader entrance queue — sequential bubble pop + fade-in (no typewriter) ─
// Timing constants (ms) for the reader's bubble entrance queue. MUST match
// their 1:1 twins in manga-export.ts's `readerRuntime` (which can't import
// this module — it's stringified and embedded verbatim into the exported
// HTML — so the constants/logic are kept as sibling copies, same convention
// as `computeBubbleGeometryPx` there).
const READER_POP_MS = 400;           // matches the `bubble-pop` CSS animation duration
const READER_TEXT_IN_DELAY_MS = 220; // matches the text-in `+0.22s` offset — fade-in starts here
const READER_GAP_MS = 200;           // breathing room after one bubble's read time, before the next pops in
const READER_QUEUE_BASE_MS = 150;    // first bubble's own pop delay (matches the old fixed stagger's base offset)
const READ_BASE_MS = 450;            // how long a bubble holds the "current" slot: base…
const READ_PER_CHAR_MS = 14;         // …plus this much per character of its FULL text…
const READ_CAP_MS = 1600;            // …capped, so one wall of text can't stall the whole queue.

/** Read-time (ms) a bubble holds the "current" slot before the next one pops
 * in — proportional to text length, replacing the old per-character
 * typewriter's own duration as the queue's pacing signal. */
function readDurationMs(text: string): number {
  return Math.min(READ_CAP_MS, READ_BASE_MS + text.length * READ_PER_CHAR_MS);
}

interface ReaderQueueState {
  /** bubble id → pop delay, in MILLISECONDS (convert to seconds for the
   * `animDelay` prop — see `ReaderPage`'s `renderBubbleLayer`). */
  popDelay: Map<string, number>;
}

/** Drives the reader's SEQUENTIAL bubble entrance queue: bubbles pop in ONE
 * AT A TIME, in reading order (`bubbles`' own array order) — each bubble's
 * FULL text fades in immediately (no progressive reveal) and holds for
 * `readDurationMs` before the next bubble's pop starts. 'sfx' bubbles have no
 * text layer at all, so they just hand off to the next bubble as soon as
 * their own pop finishes. Restarts from scratch whenever `resetKey` changes
 * (new page/panel-focus scene) — see `ReaderPage`, which builds `resetKey`
 * from `page.id` + `focusIndex`. When `animate` is off, every bubble is
 * handed back fully visible immediately, no queue. Every animation here is
 * declarative (CSS `animation-delay`) — no per-frame timers needed.
 */
function useReaderQueue(bubbles: MangaBubble[], animate: boolean, resetKey: string): ReaderQueueState {
  const [popDelay, setPopDelay] = useState<Map<string, number>>(new Map());

  // `useLayoutEffect` (not `useEffect`): the delay reset below must land
  // BEFORE the browser paints, or the very first frame of a new scene would
  // flash every bubble fully-formed (initial state) for one tick.
  useLayoutEffect(() => {
    if (!animate) { setPopDelay(new Map()); return; }
    const delays = new Map<string, number>();
    let cursor = READER_QUEUE_BASE_MS;
    bubbles.forEach((b) => {
      delays.set(b.id, cursor);
      if (b.type === 'sfx') { cursor += READER_POP_MS + READER_GAP_MS; return; }
      const typingStart = cursor + READER_TEXT_IN_DELAY_MS;
      cursor = typingStart + readDurationMs(b.text || '') + READER_GAP_MS;
    });
    setPopDelay(delays);
    // `bubbles` intentionally excluded — `resetKey` already identifies the
    // scene (page + panel focus); the bubble set for a given scene doesn't
    // change mid-session, so re-keying off it would only cause redundant reruns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, animate]);

  return { popDelay };
}

/** Tailwind classes for the reader's own page box — `ReaderPage`'s default
 * wrapper size/look (also reused, verbatim, by the page-turn flip's `sheet`
 * and `incoming` elements — see the `pageTurn` JSX above — so every layer of
 * the flip is mounted at EXACTLY the same on-screen size the live page would
 * have had; each caller adds its own `aspectRatio` inline style on top). */
const READER_PAGE_BOX_CLASS = 'relative w-[min(96vw,560px)] max-w-full shrink-0 bg-white shadow-2xl md:h-full md:w-auto';

// Bubbles that just left a focused panel (guided mode) fade out over this
// many ms instead of vanishing instantly — see the `fading` state below.
const READER_FADE_OUT_MS = 180;

/** One page rendered read-only for the reader (panels + borders + bubbles).
 * When `focusIndex` targets a panel, the page ZOOMS into it and a dark veil
 * covers everything else (even-odd hole over the panel polygon) — guided
 * view. Forwards its root box's ref (used by the page-turn flip to animate
 * the incoming page's "settle" pop — see `runPageFlip`). `boxClassName`
 * lets the flip's sheet front face override the default `READER_PAGE_BOX_CLASS`
 * sizing with a plain `absolute inset-0` fill instead, since that face is
 * already sized by its (pre-sized) `.manga-flip-sheet` parent. */
const ReaderPage = forwardRef<HTMLDivElement, { page: MangaPage; focusIndex: number | null; animate: boolean; boxClassName?: string }>(
  function ReaderPage({ page, focusIndex, animate, boxClassName }, ref) {
  const panels = page.layout.panels ?? [];
  const bubbles = page.layout.bubbles ?? [];
  const pageW = page.width || 1000;
  const pageH = page.height || 1414;

  const focused = focusIndex !== null ? panels[focusIndex] : undefined;

  // Bubbles that belong to the focused panel (center inside its bbox).
  const inFocus = (b: MangaBubble): boolean => {
    if (!focused) return true;
    const xs = focused.points.map((pt) => pt.x), ys = focused.points.map((pt) => pt.y);
    const cx = b.x + b.w / 2, cy = b.y + b.h / 2;
    return cx >= Math.min(...xs) && cx <= Math.max(...xs) && cy >= Math.min(...ys) && cy <= Math.max(...ys);
  };
  // The "active scene": whole-page view queues ALL bubbles; guided-focus view
  // queues only the FOCUSED panel's bubbles (the rest are hidden entirely —
  // see the `invisible` branch of `renderBubbleLayer` below). Reading order =
  // `bubbles`' own array order (untouched by fusion grouping).
  const activeBubbles = focused ? bubbles.filter(inFocus) : bubbles;
  // The entrance QUEUE only ever runs for a FOCUSED panel (guided "quadro a
  // quadro" mode) — the whole page (focusIndex=null: unguided mode, first
  // render, or the page just landed on after a turn) always shows EVERY
  // balloon instantly, full text, no entrance animation at all, regardless
  // of the `animate` toggle.
  const queue = useReaderQueue(activeBubbles, animate && !!focused, `${page.id}:${focusIndex ?? 'all'}`);

  // Fade-out snapshot: the panel we just navigated AWAY from (guided mode
  // only) — its own bubbles linger for READER_FADE_OUT_MS with a CSS opacity
  // transition instead of vanishing the instant focus moves on. Whole-page
  // view is unaffected — it still appears/disappears instantly (nothing was
  // ever queued there to fade). `ReaderPage` is never given an explicit
  // `key` by its caller, so this component instance (and its refs/state)
  // persists across page/focus changes — exactly what lets us diff
  // "previous vs. current" focused panel here.
  const prevFocusedRef = useRef<{ panelId: string; bubbles: MangaBubble[] } | null>(null);
  const [fading, setFading] = useState<{ bubbles: MangaBubble[]; out: boolean } | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeRafRef = useRef<number[]>([]);
  useLayoutEffect(() => {
    const prev = prevFocusedRef.current;
    if (animate && prev && prev.panelId !== (focused?.id ?? null)) {
      setFading({ bubbles: prev.bubbles, out: false });
      // Double rAF: let the browser paint at opacity 1 FIRST, then flip to 0
      // — a single rAF (or none) risks both state updates coalescing into
      // one paint, and the CSS opacity transition never actually animating.
      const r1 = requestAnimationFrame(() => {
        const r2 = requestAnimationFrame(() => setFading((f) => (f ? { ...f, out: true } : f)));
        fadeRafRef.current.push(r2);
      });
      fadeRafRef.current.push(r1);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = setTimeout(() => setFading(null), READER_FADE_OUT_MS + 60);
    }
    prevFocusedRef.current = focused ? { panelId: focused.id, bubbles: bubbles.filter(inFocus) } : null;
    return () => {
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
      fadeRafRef.current.forEach((id) => cancelAnimationFrame(id));
      fadeRafRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page.id, focused?.id, animate]);

  // One bubble layer (shapes → bridges → fusion patch → bridges again → text)
  // for a subset of bubbles — rendered twice: below the veil (other panels')
  // and above it (focused panel's own bubbles, never darkened even when they
  // overflow neighbours). `invisible` (guided view, non-focused panels that
  // were NEVER the focus) hides the WHOLE layer instantly — no fade, no
  // animation; a panel we just LEFT fades out separately (see `fading`
  // above), not through this flag.
  const renderBubbleLayer = (subset: MangaBubble[], layerKey: string, invisible = false) => {
    if (!subset.length) return null;
    const groups = groupJoinedBubbles(subset, pageW, pageH);
    // Same "one visible tail per connected group" rule as the editor (see
    // `computeTailOmissions`) — computed off THIS subset's own groups, since
    // that's what's actually rendered together here.
    const tailOmitIds = computeTailOmissions(subset, groups);
    const fused = groups
      .filter((g) => g.length > 1)
      .map((g) => {
        const members = g.map((idx) => subset[idx]);
        return { g, paths: connectorPathsForGroup(members, pageW, pageH), style: resolveBubbleStyle(members[0]) };
      });
    const delayFor = (id: string): number | undefined => {
      if (invisible) return undefined;
      const ms = queue.popDelay.get(id);
      return ms === undefined ? undefined : ms / 1000; // ms → seconds, for the `${animDelay}s` CSS templates
    };
    return (
      <svg key={`${page.id}:${focusIndex ?? 'all'}:${layerKey}`} className="pointer-events-none absolute inset-0 h-full w-full"
        viewBox={`0 0 ${pageW} ${pageH}`} preserveAspectRatio="none"
        style={invisible ? { opacity: 0 } : undefined}>
        {groups.flatMap((g) => g.map((idx) => {
          const b = subset[idx];
          return <ReaderBubbleShape key={`shape-${b.id}`} b={b} pageW={pageW} pageH={pageH} animDelay={delayFor(b.id)} omitTail={tailOmitIds.has(b.id)} />;
        }))}
        {fused.flatMap(({ g, paths, style }) => paths.map((d, i) => (
          <path key={`conn-s-${g[0]}-${i}`} d={d} fill={style.fill} stroke={style.stroke} strokeWidth={style.strokeWidth} strokeLinejoin="round" />
        )))}
        {groups.filter((g) => g.length > 1).flatMap((g) => g.map((idx) => {
          const b = subset[idx];
          return <ReaderBubbleShape key={`fuse-${b.id}`} b={b} pageW={pageW} pageH={pageH} fillOnly omitTail={tailOmitIds.has(b.id)} />;
        }))}
        {fused.flatMap(({ g, paths, style }) => paths.map((d, i) => (
          <path key={`conn-f-${g[0]}-${i}`} d={d} fill={style.fill} />
        )))}
        {subset.map((b) => (
          <ReaderBubbleText key={`text-${b.id}`} b={b} pageW={pageW} pageH={pageH} animDelay={delayFor(b.id)} />
        ))}
      </svg>
    );
  };
  let zoom = 'none';
  if (focused) {
    const xs = focused.points.map((pt) => pt.x), ys = focused.points.map((pt) => pt.y);
    const bw = Math.max(0.05, Math.max(...xs) - Math.min(...xs));
    const bh = Math.max(0.05, Math.max(...ys) - Math.min(...ys));
    const cx = (Math.max(...xs) + Math.min(...xs)) / 2;
    const cy = (Math.max(...ys) + Math.min(...ys)) / 2;
    // Fill ~92% of the viewport with the panel, capped so pixels stay clean.
    const s = Math.min(3, Math.min(1 / bw, 1 / bh) * 0.92);
    zoom = `scale(${s}) translate(${(0.5 - cx) * 100}%, ${(0.5 - cy) * 100}%)`;
  }

  return (
    <div ref={ref} className={boxClassName ?? READER_PAGE_BOX_CLASS}
      style={{
        aspectRatio: `${page.width || 1000} / ${page.height || 1414}`,
        // Camera glide: longer ease-in-out reads like a smooth pan between
        // panels instead of a snap (matches the veil's morph timing).
        transform: zoom, transition: 'transform 0.85s cubic-bezier(0.65, 0, 0.35, 1)',
      }}>
      {panels.map((p) => {
        const xs = p.points.map((pt) => pt.x), ys = p.points.map((pt) => pt.y);
        const minX = Math.min(...xs), minY = Math.min(...ys);
        const bw = Math.max(...xs) - minX, bh = Math.max(...ys) - minY;
        return (
          <div key={p.id} className="absolute inset-0" style={{ clipPath: toPolygonCss(p.points) }}>
            {p.imageUrl
              ? <img src={p.imageUrl} alt="" draggable={false}
                  style={{
                    position: 'absolute', left: `${minX * 100}%`, top: `${minY * 100}%`,
                    width: `${bw * 100}%`, height: `${bh * 100}%`, objectFit: 'cover',
                    transform: `translate(${(p.offsetX ?? 0) * 100}%, ${(p.offsetY ?? 0) * 100}%) scale(${(p.scale ?? 1) * (p.flipX ? -1 : 1)}, ${(p.scale ?? 1) * (p.flipY ? -1 : 1)})`,
                    transformOrigin: 'center',
                  }} />
              : <div className="absolute inset-0 bg-[repeating-linear-gradient(45deg,#f3f0e8,#f3f0e8_10px,#ece7db_10px,#ece7db_20px)]" />}
          </div>
        );
      })}
      <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        {panels.map((p) => (
          <polygon key={p.id} points={p.points.map((pt) => `${pt.x * 100},${pt.y * 100}`).join(' ')}
            fill="none" stroke="#111" strokeWidth={1.4} vectorEffect="non-scaling-stroke" />
        ))}
      </svg>
      {/* Bubbles — svgs in PAGE PIXEL coordinates (paths never distort), same
          3-pass split as the editor (shapes grouped → fusion patch → text).
          OWNERSHIP RULE: a bubble belongs to ONE panel; in guided view the
          focused panel's bubbles render ABOVE the veil (queued, entrance-
          animated), while other panels' bubbles are INVISIBLE (instantly, no
          fade) — only the panel being read has balloons on screen. In
          whole-page view this IS the only layer, and it's the queue. */}
      {renderBubbleLayer(focused ? bubbles.filter((b) => !inFocus(b)) : bubbles, 'under', !!focused)}
      {/* Guided-view veil: darken the page EXCEPT the focused panel. The hole is
          RESAMPLED to a fixed vertex count so every panel's path has the same
          structure — CSS `transition: d` then MORPHS the hole smoothly from one
          panel to the next (no more abrupt jump). Kept mounted with opacity so
          entering/leaving guided view fades instead of popping. */}
      {(() => {
        const hole = focused ? resamplePolygonLinear(focused.points, 28) : null;
        const holePath = hole ? ` M ${hole.map((pt) => `${(pt.x * 100).toFixed(3)} ${(pt.y * 100).toFixed(3)}`).join(' L ')} Z` : '';
        const veilD = `M0 0 H100 V100 H0 Z${holePath}`;
        const outlineD = hole ? `M ${hole.map((pt) => `${(pt.x * 100).toFixed(3)} ${(pt.y * 100).toFixed(3)}`).join(' L ')} Z` : '';
        const morph = 'd 0.85s cubic-bezier(0.65, 0, 0.35, 1), opacity 0.4s ease';
        if (!panels.length) return null;
        return (
          <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path
              fill="rgba(2,2,4,0.97)" fillRule="evenodd"
              style={{ d: `path("${veilD}")`, transition: morph, opacity: focused ? 1 : 0 } as React.CSSProperties} />
            {outlineD && (
              <path
                fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth={1.6} vectorEffect="non-scaling-stroke"
                style={{ d: `path("${outlineD}")`, transition: morph, opacity: focused ? 1 : 0 } as React.CSSProperties} />
            )}
          </svg>
        );
      })()}
      {/* The focused panel's OWN bubbles live above the veil: they stay bright
          and whole even where they overflow into neighbouring panels. */}
      {focused && renderBubbleLayer(bubbles.filter((b) => inFocus(b)), 'over')}
      {/* Bubbles that just left a focused panel — fade out quickly (CSS
          opacity, group-level) instead of the instant "corte seco" the
          guided view used to apply when moving on. */}
      {fading && fading.bubbles.length > 0 && (
        <div className="pointer-events-none absolute inset-0"
          style={{ transition: `opacity ${READER_FADE_OUT_MS}ms ease-out`, opacity: fading.out ? 0 : 1 }}>
          {renderBubbleLayer(fading.bubbles, 'fade-out')}
        </div>
      )}
    </div>
  );
  },
);
ReaderPage.displayName = 'ReaderPage';
